import './styles.css';
import { LOGO } from './logo.js';

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Package, Users, FileText, Truck, BarChart2, Shield, Settings, Search, X,
  Plus, Edit2, Check, AlertTriangle, Eye, EyeOff, MessageCircle, LogOut, Home,
  DollarSign, ClipboardList, RefreshCw, Lock, Bell, CheckCircle, XCircle,
  User, ChevronRight, AlertCircle, Printer, Building2, Receipt,
  TrendingDown, CreditCard, Calendar, Filter, ChevronLeft, ChevronRight as ChevronR,
  Layers, Trash2, ClipboardPaste, ChevronUp, ChevronDown, ChevronsUpDown, Sun, Moon, Copy,
  Clock, ArrowLeft, MapPin, Download, FileSpreadsheet, GripVertical } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import * as API from "./services/api";
import { Btn, Toast, Spinner } from "./components/common";
// CMS en construccion (otra sesion) - modules/cms/ aun no esta comiteado.
// Deshabilitado temporalmente para no romper el build de produccion;
// descomentar cuando el modulo este listo para subir.
// import CmsModule from "./modules/cms";

/* ── THEME ─────────────────────────────────────────────────────────── */
const CC=["#3B9EE0","#10b981","#8b5cf6","#f97316","#ef4444","#5DADE2"];
const TIP={contentStyle:{background:'var(--card)',border:'1px solid var(--border)',borderRadius:8,color:'var(--text)',fontSize:12}};

const hexToRgb = h => { const b=h.replace('#',''); return[parseInt(b.slice(0,2),16),parseInt(b.slice(2,4),16),parseInt(b.slice(4,6),16)]; };

const applyTheme = (s={}) => {
  const root = document.documentElement;
  root.setAttribute('data-theme', s.theme || 'light');
  if (s.colorAccent && s.colorAccent !== '#3B9EE0') {
    const [r,g,b] = hexToRgb(s.colorAccent);
    root.style.setProperty('--accent',        s.colorAccent);
    root.style.setProperty('--accent-h',      s.colorAccent);
    root.style.setProperty('--accent-dim',    `rgba(${r},${g},${b},0.13)`);
    root.style.setProperty('--accent-border', `rgba(${r},${g},${b},0.28)`);
  } else {
    root.style.removeProperty('--accent');
    root.style.removeProperty('--accent-h');
    root.style.removeProperty('--accent-dim');
    root.style.removeProperty('--accent-border');
  }
  if (s.theme === 'light') {
    root.style.removeProperty('--bg');
    root.style.removeProperty('--card');
  } else {
    if (s.colorPrimary   && s.colorPrimary   !== '#020d1b') root.style.setProperty('--bg',   s.colorPrimary);
    else root.style.removeProperty('--bg');
    if (s.colorSecondary && s.colorSecondary !== '#0d1f35') root.style.setProperty('--card', s.colorSecondary);
    else root.style.removeProperty('--card');
  }
};

/* ── UTILS ──────────────────────────────────────────────────────────── */
const fmtUsd=v=>`$${(+v||0).toFixed(2)}`;
const fmtNio=v=>`C$${(+v||0).toFixed(2)}`;
const fmtLbs=v=>`${(+v||0).toFixed(2)} lb`;
const today=()=>new Date().toISOString().slice(0,10);
// Muestra cualquier fecha ("YYYY-MM-DD" o "YYYY-MM-DD HH:MM") en formato dd/mm/aaaa (hh:mm si trae hora).
const fmtDate=iso=>{
  if(!iso)return"";
  const[datePart,timePart]=String(iso).split(" ");
  const m=/^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart||"");
  if(!m)return iso;
  const[,y,mo,d]=m;
  return timePart?`${d}/${mo}/${y} ${timePart}`:`${d}/${mo}/${y}`;
};
/* ── FILTRO GLOBAL DE PERÍODO (Dashboard + Reportes) ──────────────────
   Helpers puros, sin dependencias de red ni de React — se prueban por
   separado con datos fijos antes de conectarlos a la UI. */
const MESES_CORTOS=['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

// presetRange: {desde,hasta} en YYYY-MM-DD para cada preset del filtro.
// desde=null && hasta=null → "todo el historial" (sin límite inferior/superior).
// "rango" (personalizado) no calcula nada aquí — el propio componente maneja sus fechas.
const presetRange=preset=>{
  const iso=d=>d.toISOString().slice(0,10);
  const now=new Date();
  switch(preset){
    case "hoy": return{desde:iso(now),hasta:iso(now)};
    case "ayer":{const y=new Date(now);y.setDate(y.getDate()-1);return{desde:iso(y),hasta:iso(y)};}
    case "semana":{const dow=(now.getDay()+6)%7;const mon=new Date(now);mon.setDate(now.getDate()-dow);return{desde:iso(mon),hasta:iso(now)};}
    case "mes":{const d=new Date(now.getFullYear(),now.getMonth(),1);return{desde:iso(d),hasta:iso(now)};}
    case "mesAnterior":{const d=new Date(now.getFullYear(),now.getMonth()-1,1);const f=new Date(now.getFullYear(),now.getMonth(),0);return{desde:iso(d),hasta:iso(f)};}
    case "anio":{const d=new Date(now.getFullYear(),0,1);return{desde:iso(d),hasta:iso(now)};}
    case "historial": return{desde:null,hasta:null};
    default: return null;
  }
};

// Granularidad automática del eje X/filas según el span de días del rango efectivo.
const pickGranularity=(desde,hasta)=>{
  if(!desde||!hasta)return"mes";
  const days=Math.max(0,(new Date(hasta)-new Date(desde))/86400000);
  if(days<=31)return"dia";
  if(days<=182)return"semana";
  if(days<=730)return"mes";
  return"anio";
};

// Clave de agrupación (ordenable como string) por granularidad.
const groupKey=(fechaStr,gran)=>{
  const d=(fechaStr||"").toString().slice(0,10);
  if(!d)return"";
  if(gran==="dia")return d;
  if(gran==="semana"){
    const dt=new Date(d+"T00:00:00");
    const dow=(dt.getDay()+6)%7; // lunes=0
    dt.setDate(dt.getDate()-dow);
    return dt.toISOString().slice(0,10);
  }
  if(gran==="mes")return d.slice(0,7);
  return d.slice(0,4);
};

// Etiqueta legible de cada punto/fila a partir de la clave anterior.
const groupLabel=(key,gran)=>{
  if(!key)return"";
  if(gran==="dia"){const[,m,dd]=key.split("-");return`${dd}/${m}`;}
  if(gran==="semana"){const[,m,dd]=key.split("-");return`Sem ${dd}/${m}`;}
  if(gran==="mes"){const[y,m]=key.split("-");return`${MESES_CORTOS[+m]} ${y}`;}
  return key;
};

// Agregación central 100% cliente: recibe los datos ya cargados por loadAll()
// y un rango {desde,hasta} (YYYY-MM-DD o null=sin límite) y calcula todo lo
// que necesitan el filtro nuevo de Dashboard y Reportes. No toca el backend.
function computeReportData(data,desde,hasta){
  const{clients=[],packages=[],invoices=[],deliveries=[],ordenes=[],gastos=[]}=data||{};
  const d10=s=>(s||"").toString().slice(0,10);
  const inRange=s=>{const v=d10(s);if(!v)return false;if(desde&&v<desde)return false;if(hasta&&v>hasta)return false;return true;};
  const clientName=id=>{const c=clients.find(x=>x.id===id);return c?`${c.name} ${c.lastName}`:"";};

  const paquetesReg=packages.filter(p=>inRange(p.rxDate));
  const facturasEnRango=invoices.filter(i=>inRange(i.fecha));
  const facturasPagadas=facturasEnRango.filter(i=>i.status==="Pagada");
  const facturasAnuladas=facturasEnRango.filter(i=>i.status==="Anulada");
  const gastosEnRango=gastos.filter(g=>g.estado==="Activo"&&inRange(g.fecha));
  const ordenesEnRango=ordenes.filter(o=>inRange(o.fecha));
  const ordenesPendEnRango=ordenesEnRango.filter(o=>o.status==="Pendiente");
  const ordenesConvEnRango=ordenesEnRango.filter(o=>o.status==="Facturada");
  const ordenesCancEnRango=ordenesEnRango.filter(o=>o.status==="Cancelada");
  const clientesNuevos=clients.filter(c=>inRange(c.createdAt)).length;

  const totalRev=facturasPagadas.reduce((s,i)=>s+(+i.totalUsd||0),0);
  const totalGastos=gastosEnRango.reduce((s,g)=>s+(+g.montoUsd||0),0);
  const utilidad=totalRev-totalGastos;
  const ticketProm=facturasPagadas.length?totalRev/facturasPagadas.length:0;

  const paquetesEntregados=paquetesReg.filter(p=>p.status==="Entregado").length;
  const pkgsPorFacturarIds=new Set(ordenesPendEnRango.flatMap(o=>o.pkgIds||[]));
  const paquetesPorFacturarList=packages.filter(p=>pkgsPorFacturarIds.has(p.id));
  const paquetesPorFacturar=paquetesPorFacturarList.length;

  const librasGestAereo=paquetesReg.filter(p=>p.type==="Aéreo").reduce((s,p)=>s+(+p.weight||0),0);
  const librasGestMar=paquetesReg.filter(p=>p.type==="Marítimo").reduce((s,p)=>s+(+p.weight||0),0);
  const tipoDist=[{n:"Aéreo",v:paquetesReg.filter(p=>p.type==="Aéreo").length},{n:"Marítimo",v:paquetesReg.filter(p=>p.type==="Marítimo").length}];

  // Libras facturadas: total = peso facturado real de cada recibo pagado (cifra
  // financiera). Desglose Aéreo/Marítimo = peso REAL de los paquetes de esos
  // recibos (el redondeo de "mínimo por factura" es agregado y no se puede
  // repartir con precisión entre tipos de envío — decisión confirmada con el usuario).
  const librasFacturadasTotal=facturasPagadas.reduce((s,i)=>s+(+i.billedWeight||0),0);
  const facturasPagadasIds=new Set(facturasPagadas.map(i=>i.id));
  const pkgsDeFacturasPagadas=packages.filter(p=>p.invId&&facturasPagadasIds.has(p.invId));
  const librasFactAereo=pkgsDeFacturasPagadas.filter(p=>p.type==="Aéreo").reduce((s,p)=>s+(+p.weight||0),0);
  const librasFactMar=pkgsDeFacturasPagadas.filter(p=>p.type==="Marítimo").reduce((s,p)=>s+(+p.weight||0),0);

  const comparativoTipo=["Aéreo","Marítimo"].map(tipo=>{
    let cantPaquetes=0,libras=0,ingresos=0;
    facturasPagadas.forEach(inv=>(inv.details||[]).forEach(d=>{
      if(d.type===tipo){cantPaquetes++;libras+=(+d.weight||0);ingresos+=(+d.subtotal||0);}
    }));
    return{tipo,paquetes:cantPaquetes,libras:+libras.toFixed(2),ingresos:+ingresos.toFixed(2)};
  });

  const gastosCat=Object.values(gastosEnRango.reduce((acc,g)=>{
    acc[g.categoria]||(acc[g.categoria]={cat:g.categoria,total:0,cnt:0});
    acc[g.categoria].total+=(+g.montoUsd||0);acc[g.categoria].cnt++;
    return acc;
  },{})).sort((a,b)=>b.total-a.total);

  const allDates=[...paquetesReg.map(p=>p.rxDate),...facturasPagadas.map(i=>i.fecha)].filter(Boolean).sort();
  const effDesde=desde||allDates[0]||today();
  const effHasta=hasta||today();
  const granularity=pickGranularity(effDesde,effHasta);

  const buckets=new Map();
  const bucket=k=>{if(!buckets.has(k))buckets.set(k,{});return buckets.get(k);};
  facturasPagadas.forEach(i=>{
    const k=groupKey(i.fecha,granularity);const b=bucket(k);b.ing=(b.ing||0)+(+i.totalUsd||0);b.facs=(b.facs||0)+1;
    (i.details||[]).forEach(d=>{
      if(d.type==="Aéreo")b.facAereo=(b.facAereo||0)+(+d.weight||0); else b.facMaritimo=(b.facMaritimo||0)+(+d.weight||0);
    });
  });
  paquetesReg.forEach(p=>{
    const k=groupKey(p.rxDate,granularity);const b=bucket(k);
    b.pk=(b.pk||0)+1;
    if(p.type==="Aéreo")b.aereo=(b.aereo||0)+(+p.weight||0); else b.maritimo=(b.maritimo||0)+(+p.weight||0);
  });
  gastosEnRango.forEach(g=>{const k=groupKey(g.fecha,granularity);const b=bucket(k);b.gas=(b.gas||0)+(+g.montoUsd||0);});

  const allKeys=[...buckets.keys()].sort();
  const chartIngresos=allKeys.map(k=>({m:groupLabel(k,granularity),v:+((buckets.get(k).ing)||0).toFixed(2),f:buckets.get(k).facs||0}));
  const chartPaquetes=allKeys.map(k=>({m:groupLabel(k,granularity),v:buckets.get(k).pk||0}));
  const chartLibrasFacturadas=allKeys.map(k=>({m:groupLabel(k,granularity),aereo:+((buckets.get(k).facAereo)||0).toFixed(2),maritimo:+((buckets.get(k).facMaritimo)||0).toFixed(2)}));
  const chartLibras=allKeys.map(k=>({m:groupLabel(k,granularity),aereo:+((buckets.get(k).aereo)||0).toFixed(2),maritimo:+((buckets.get(k).maritimo)||0).toFixed(2)}));
  const resumenPeriodo=allKeys.map(k=>{
    const b=buckets.get(k);const ing=b.ing||0,gas=b.gas||0;
    return{periodo:groupLabel(k,granularity),paquetes:b.pk||0,libras:+((b.aereo||0)+(b.maritimo||0)).toFixed(2),ingresos:+ing.toFixed(2),gastos:+gas.toFixed(2),utilidad:+(ing-gas).toFixed(2)};
  });

  const rankingClientes=clients.map(c=>{
    const invsCli=facturasPagadas.filter(i=>i.clientId===c.id);
    const invsCliIds=new Set(invsCli.map(i=>i.id));
    const pkgsCli=pkgsDeFacturasPagadas.filter(p=>invsCliIds.has(p.invId));
    const totalUsd=invsCli.reduce((s,i)=>s+(+i.totalUsd||0),0);
    const libras=pkgsCli.reduce((s,p)=>s+(+p.weight||0),0);
    return{id:c.id,name:c.name,lastName:c.lastName,label:c.label,pkgs:pkgsCli.length,libras:+libras.toFixed(2),invs:invsCli.length,totalUsd,ticketProm:invsCli.length?totalUsd/invsCli.length:0};
  }).filter(c=>c.invs>0).sort((a,b)=>b.totalUsd-a.totalUsd);

  const detalleRecibos=facturasEnRango.map(i=>{
    const tipos=new Set((i.details||[]).map(d=>d.type));
    return{fecha:i.fecha,number:i.number,cliente:clientName(i.clientId),tipo:tipos.size>1?"Mixto":([...tipos][0]||"—"),
      cantPaquetes:(i.details||[]).length,libras:+((i.details||[]).reduce((s,d)=>s+(+d.weight||0),0)).toFixed(2),totalUsd:i.totalUsd,status:i.status};
  }).sort((a,b)=>(b.fecha||"").localeCompare(a.fecha||""));

  const detallePaquetes=paquetesReg.map(p=>{
    const inv=p.invId?invoices.find(i=>i.id===p.invId):null;
    const ent=p.invId?deliveries.find(d=>d.invId===p.invId):null;
    return{tracking:p.tracking||"",code:p.code,cliente:clientName(p.clientId),tipo:p.type,peso:p.weight,estado:p.status,
      fechaRegistro:p.rxDate,fechaFacturacion:inv?inv.fecha:"",fechaEntrega:ent?ent.delivDate:""};
  }).sort((a,b)=>(b.fechaRegistro||"").localeCompare(a.fechaRegistro||""));

  return{
    granularity,
    totalRev,totalGastos,utilidad,ticketProm,
    cntRecibos:facturasPagadas.length,cntAnulados:facturasAnuladas.length,cntGastos:gastosEnRango.length,
    paquetesRegistrados:paquetesReg.length,paquetesEntregados,paquetesPorFacturar,
    opPend:ordenesPendEnRango.length,opConv:ordenesConvEnRango.length,opCanc:ordenesCancEnRango.length,
    tasaConv:ordenesEnRango.length?Math.round(ordenesConvEnRango.length/ordenesEnRango.length*100):0,
    clientesNuevos,tipoDist,
    librasGestionadas:{aereo:+librasGestAereo.toFixed(2),maritimo:+librasGestMar.toFixed(2),total:+(librasGestAereo+librasGestMar).toFixed(2)},
    librasFacturadas:{aereo:+librasFactAereo.toFixed(2),maritimo:+librasFactMar.toFixed(2),total:+librasFacturadasTotal.toFixed(2)},
    comparativoTipo,gastosCat,
    chartIngresos,chartPaquetes,chartLibras,chartLibrasFacturadas,
    resumenPeriodo,rankingClientes,detalleRecibos,detallePaquetes,
    paquetesRegList:paquetesReg,facturasPagadasList:facturasPagadas,paquetesPorFacturarList,
    gastosEnRangoList:gastosEnRango,
  };
}

// Tolera 403 puntuales (usuario sin permiso para ese módulo) devolviendo un valor
// por defecto en vez de tumbar toda la carga inicial — otros errores sí se propagan.
const safeFetch=(promise,fallback)=>promise.catch(e=>{
  if(e?.status===403)return fallback;
  throw e;
});
// ¿el usuario tiene el permiso "modulo:accion"? (viene precalculado en el JWT al hacer login)
const can=(user,slug)=>(user?.permisos||[]).includes(slug);
// Slug "modulo:ver" que gatea la visibilidad de cada entrada del menú (dashboard siempre visible).
const NAV_PERM={
  clients:"clientes:ver", packages:"paquetes:ver",
  billing:"facturas:ver", ordenes:"ordenes:ver", gastos:"gastos:ver",
  periodos:"periodos:ver", delivery:"entregas:ver", reports:"reportes:ver",
  audit:"auditoria:ver", settings:"settings:ver",
  cms:"cms_paginas:ver",
};
const sortedActiveClientOptions=clients=>clients.filter(c=>c.status==="Activo").slice()
  .sort((a,b)=>`${a.name} ${a.lastName}`.localeCompare(`${b.name} ${b.lastName}`,'es'))
  .map(c=>({v:c.id,l:`${c.name} ${c.lastName} · ${c.code}`}));
// minLbsPerPackage (opcional, default null=desactivado): cuando se pasa un
// valor, redondea CADA paquete individualmente a ese minimo ANTES de sumar
// -sin el factor de escala proporcional que usan los otros modos- y toma
// precedencia sobre useMin. Es una regla de negocio independiente de
// "peso minimo por factura" (minLbs/useMin), sin logica compartida entre
// ambas; los call-sites existentes que no pasan este parametro se comportan
// exactamente igual que antes.
const calcInv=(details,minLbs,discType,discVal,exRate,useMin=true,minLbsPerPackage=null)=>{
  const totalW=details.reduce((s,d)=>s+parseFloat(d.weight||0),0);
  let billedW,sub;
  if(minLbsPerPackage){
    const pkgW=d=>Math.max(parseFloat(d.weight||0),parseFloat(minLbsPerPackage||1));
    billedW=details.reduce((s,d)=>s+pkgW(d),0);
    sub=details.reduce((s,d)=>s+pkgW(d)*parseFloat(d.rate||0),0);
  }else{
    billedW=useMin?Math.max(totalW,parseFloat(minLbs||1)):totalW;
    const minAdj=billedW>totalW&&totalW>0?billedW/totalW:1;
    const rawSub=details.reduce((s,d)=>s+parseFloat(d.weight||0)*parseFloat(d.rate||0),0);
    sub=rawSub*minAdj;
  }
  let discAmt=0;
  if(discType==="porcentaje"&&discVal>0)discAmt=sub*(discVal/100);
  else if(discType==="monto"&&discVal>0)discAmt=Math.min(parseFloat(discVal)||0,sub);
  const totalUsd=Math.max(0,sub-discAmt);
  return{totalW,billedW,sub:+sub.toFixed(2),discAmt:+discAmt.toFixed(2),totalUsd:+totalUsd.toFixed(2),totalNio:+(totalUsd*exRate).toFixed(2)};
};

// Vista de facturación por línea — solo presentación, no altera calcInv ni
// el peso real almacenado en cada paquete (ese sigue intacto para reportes,
// estadísticas, costos y auditoría). Recibe un objeto factura/orden (o el
// equivalente en vivo mientras se arma: {details, billedWeight, subtotalUsd,
// minLbsPerPackageUsed}) y devuelve cada línea con el PESO FACTURADO que el
// cliente debe ver, más una línea de ajuste cuando corresponde.
//  - Modo "mínimo por paquete" (minLbsPerPackageUsed conocido): cada línea
//    ya trae su propio peso redondeado — la suma cuadra exacta, sin ajuste.
//  - Modo "mínimo por factura" (o documentos previos a este campo): cada
//    línea conserva su peso real reconocible; si el mínimo agregado subió
//    el total, se agrega una única línea "Ajuste por peso mínimo aplicado"
//    que reconcilia el total exacto — nunca se muestran subtotales que no
//    coincidan con el total.
function billedLineItems(inv){
  const details=inv.details||[];
  if(inv.minLbsPerPackageUsed){
    const m=parseFloat(inv.minLbsPerPackageUsed)||1;
    const lines=details.map(d=>{
      const w=Math.max(parseFloat(d.weight||0),m);
      return{...d,billedWeight:w,billedSubtotal:+(w*parseFloat(d.rate||0)).toFixed(2)};
    });
    return{lines,adjustment:null};
  }
  const lines=details.map(d=>({...d,billedWeight:parseFloat(d.weight||0),billedSubtotal:+(parseFloat(d.weight||0)*parseFloat(d.rate||0)).toFixed(2)}));
  const totalW=lines.reduce((s,d)=>s+d.billedWeight,0);
  const billedW=parseFloat(inv.billedWeight??totalW);
  const deltaW=+(billedW-totalW).toFixed(2);
  if(deltaW>0.004){
    const rawSub=lines.reduce((s,d)=>s+d.billedSubtotal,0);
    const trueSub=parseFloat(inv.subtotalUsd??rawSub);
    const deltaAmt=+(trueSub-rawSub).toFixed(2);
    return{lines,adjustment:{label:"Ajuste por peso mínimo aplicado",weight:deltaW,amount:deltaAmt}};
  }
  return{lines,adjustment:null};
}

/* ── INVOICE HTML (print / PDF) ─────────────────────────────────────── */
function buildInvoiceHTML(inv,cli,settings){
  const N="#1B3A5C",LB="#EBF4FF",GR="#f8fafc";
  const f2=n=>parseFloat(n||0).toFixed(2);
  const{lines,adjustment}=billedLineItems(inv);
  const rows=lines.map((d,i)=>`<tr style="background:${i%2===0?LB:GR};border-bottom:1px solid #dde5f0;">
    <td style="padding:9px 12px;font-family:monospace;font-size:13px;font-weight:700;color:${N};">${d.code}</td>
    <td style="padding:9px 12px;font-family:monospace;font-size:11px;color:#666;">${d.tracking||"—"}</td>
    <td style="padding:9px 12px;font-size:12px;color:#333;">${d.type}</td>
    <td style="padding:9px 12px;text-align:right;font-size:13px;font-weight:700;">${f2(d.billedWeight)}</td>
    <td style="padding:9px 12px;text-align:right;font-size:12px;">$${f2(d.rate)}</td>
    <td style="padding:9px 12px;text-align:right;font-size:14px;font-weight:700;color:${N};">$${f2(d.billedSubtotal)}</td>
  </tr>`).join("")+(adjustment?`<tr style="background:#fffbeb;border-bottom:1px solid #dde5f0;">
    <td colspan="3" style="padding:9px 12px;font-size:12px;color:#92400e;font-style:italic;">${adjustment.label}</td>
    <td style="padding:9px 12px;text-align:right;font-size:13px;font-weight:700;color:#92400e;">+${f2(adjustment.weight)}</td>
    <td></td>
    <td style="padding:9px 12px;text-align:right;font-size:14px;font-weight:700;color:#92400e;">+$${f2(adjustment.amount)}</td>
  </tr>`:"");
  const discLine=inv.discAmt>0?`<tr><td colspan="5" style="text-align:right;padding:6px 12px;font-size:13px;color:#059669;">Descuento (${inv.discType==="porcentaje"?inv.discVal+"%":"$"+f2(inv.discVal)})</td><td style="text-align:right;padding:6px 12px;font-size:13px;color:#059669;font-weight:700;">-$${f2(inv.discAmt)}</td></tr>`:"";
  return`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${inv.number} - ${settings.companyName}</title><style>*{box-sizing:border-box;margin:0;padding:0;}body{background:#fff;font-family:Arial,Helvetica,sans-serif;}@media print{@page{margin:10mm;size:A4;}}</style></head><body><div style="max-width:750px;margin:0 auto;padding:32px 36px;background:#fff;color:#1a1a1a;">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:18px;border-bottom:3px solid ${N};">
    <div><img src="${settings.logo||LOGO}" alt="${settings.companyName}" style="height:65px;display:block;margin-bottom:8px;"/><div style="font-size:11px;color:#666;line-height:1.7;">${settings.ruc?"RUC: "+settings.ruc+"<br/>":""}${settings.address||""}<br/>📱 ${settings.whatsapp||""} ${settings.email?"· ✉ "+settings.email:""}</div></div>
    <div style="text-align:right;"><div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:4px;">RECIBO</div><div style="font-size:26px;font-weight:800;color:${N};font-family:monospace;">${inv.number}</div><div style="font-size:12px;color:#666;margin-top:4px;">Fecha: ${fmtDate(inv.fecha)}</div><div style="margin-top:8px;display:inline-block;background:${inv.status==="Pagada"?"#d1fae5":"#fee2e2"};color:${inv.status==="Pagada"?"#065f46":"#991b1b"};padding:4px 14px;border-radius:20px;font-size:12px;font-weight:700;">${inv.status}</div></div>
  </div>
  <div style="background:${LB};border:1px solid #bcd4ed;border-radius:8px;padding:14px 18px;margin-bottom:20px;">
    <div style="font-size:10px;color:#666;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:4px;">EMITIDO A</div>
    <div style="font-size:18px;font-weight:700;color:${N};">${cli?cli.name+" "+cli.lastName:"—"}</div>
    <div style="font-size:12px;color:#555;margin-top:3px;">📱 ${cli?cli.whatsapp:""} ${cli&&cli.email?"· ✉ "+cli.email:""} ${cli&&cli.address?"· "+cli.address:""}</div>
  </div>
  <table style="width:100%;border-collapse:collapse;margin-bottom:16px;">
    <thead><tr style="background:${N};color:white;"><th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;">Código</th><th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:700;">Tracking</th><th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:700;">Tipo</th><th style="padding:10px 12px;text-align:right;font-size:11px;font-weight:700;">Peso facturado (lb)</th><th style="padding:10px 12px;text-align:right;font-size:11px;font-weight:700;">Tarifa</th><th style="padding:10px 12px;text-align:right;font-size:11px;font-weight:700;">Subtotal</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot>${discLine}<tr style="background:${GR};border-top:1px solid #cbd5e1;"><td colspan="5" style="text-align:right;padding:10px 12px;font-size:12px;color:#64748b;font-weight:600;text-transform:uppercase;">Subtotal USD</td><td style="text-align:right;padding:10px 12px;font-size:13px;color:#334155;">$${f2(inv.subtotalUsd)}</td></tr>
    <tr style="background:${N};"><td colspan="5" style="text-align:right;padding:14px 12px;font-size:13px;color:rgba(255,255,255,0.85);font-weight:700;text-transform:uppercase;">TOTAL USD</td><td style="text-align:right;padding:14px 12px;font-size:22px;color:#fff;font-weight:800;">$${f2(inv.totalUsd)}</td></tr>
    <tr style="background:${LB};"><td colspan="5" style="text-align:right;padding:8px 12px;font-size:12px;color:#475569;">Total córdobas (T.C. C$${f2(inv.exRate)} × $)</td><td style="text-align:right;padding:8px 12px;font-size:14px;color:${N};font-weight:700;">C$${f2(inv.totalNio)}</td></tr></tfoot>
  </table>
  <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px 16px;margin-bottom:20px;display:flex;gap:28px;flex-wrap:wrap;">
    <div><div style="font-size:10px;color:#888;font-weight:700;text-transform:uppercase;margin-bottom:3px;">Método de pago</div><div style="font-size:14px;font-weight:700;color:${N};">${inv.payMethod}</div></div>
    ${inv.reference?`<div><div style="font-size:10px;color:#888;font-weight:700;text-transform:uppercase;margin-bottom:3px;">Referencia</div><div style="font-size:13px;font-family:monospace;font-weight:700;">${inv.reference}</div></div>`:""}
    <div><div style="font-size:10px;color:#888;font-weight:700;text-transform:uppercase;margin-bottom:3px;">Emitida por</div><div style="font-size:13px;">${inv.createdBy||"—"}</div></div>
  </div>
  <div style="text-align:center;font-size:11px;color:#aaa;border-top:1px solid #e5e7eb;padding-top:14px;"><strong style="color:#666;">${settings.companyName}</strong> — Tu agencia de confianza · ${settings.address||""} · ${settings.whatsapp||""}</div>
</div></body></html>`;
}

const loadScript=src=>new Promise((resolve,reject)=>{if(document.querySelector(`script[src="${src}"]`)){resolve();return;}const s=document.createElement("script");s.src=src;s.onload=resolve;s.onerror=reject;document.head.appendChild(s);});

/* ── EXPORTACIÓN (Fase 5) ──────────────────────────────────────────────
   columns: [{key,label,value?:(row)=>string}] — value() permite formatear
   una celda (moneda, fecha, etc.) sin depender de los render() JSX que ya
   usan las tablas en pantalla. rows vacío igual genera el archivo con los
   encabezados + un mensaje, nunca un archivo en blanco o un error. ────── */
const cellOf=(col,row)=>{const v=typeof col.value==="function"?col.value(row):row[col.key];return v==null?"":v;};
const NO_ROWS_MSG="No existen registros para los filtros seleccionados";

const exportToExcel=async(rows,columns,filename)=>{
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js");
  const XLSX=window.XLSX;
  const header=columns.map(c=>c.label);
  const body=rows.length?rows.map(r=>columns.map(c=>cellOf(c,r))):[[NO_ROWS_MSG]];
  const ws=XLSX.utils.aoa_to_sheet([header,...body]);
  ws["!cols"]=columns.map(()=>({wch:18}));
  const wb=XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb,ws,"Datos");
  XLSX.writeFile(wb,`${filename}.xlsx`);
};

const exportToPdf=async(rows,columns,filename,title)=>{
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");
  const{jsPDF}=window.jspdf;
  const doc=new jsPDF({orientation:columns.length>5?"landscape":"portrait"});
  doc.setFontSize(13);doc.setTextColor(20);
  doc.text(title||filename,14,16);
  doc.setFontSize(9);doc.setTextColor(130);
  doc.text(`Generado: ${new Date().toLocaleString("es-NI")}`,14,22);
  if(rows.length){
    doc.autoTable({
      startY:28,
      head:[columns.map(c=>c.label)],
      body:rows.map(r=>columns.map(c=>String(cellOf(c,r)))),
      styles:{fontSize:8,cellPadding:3},
      headStyles:{fillColor:[59,158,224]},
    });
  }else{
    doc.setFontSize(11);doc.setTextColor(150);
    doc.text(NO_ROWS_MSG,14,34);
  }
  doc.save(`${filename}.pdf`);
};

const EXPORT_MENU_ITEM={display:"flex",alignItems:"center",gap:8,width:"100%",padding:"8px 12px",background:"none",border:"none",cursor:"pointer",fontSize:12,color:"var(--text)",textAlign:"left"};

function ExportButton({rows,columns,filename,title,addToast,size=13}){
  const[open,setOpen]=useState(false);
  const[busy,setBusy]=useState(false);
  const ref=useRef(null);
  useEffect(()=>{
    if(!open)return;
    const onDoc=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    document.addEventListener("mousedown",onDoc);
    return()=>document.removeEventListener("mousedown",onDoc);
  },[open]);
  const run=async fmt=>{
    setOpen(false);setBusy(true);
    try{
      if(fmt==="excel")await exportToExcel(rows,columns,filename);
      else await exportToPdf(rows,columns,filename,title);
    }catch(e){addToast?.("No se pudo generar el archivo: "+e.message,"error");}
    finally{setBusy(false);}
  };
  return<div ref={ref} style={{position:"relative",display:"inline-block"}} onClick={e=>e.stopPropagation()}>
    <button onClick={()=>setOpen(o=>!o)} disabled={busy} title="Exportar datos" style={{background:"none",border:"none",cursor:busy?"default":"pointer",color:"var(--muted)",padding:4,borderRadius:6,display:"flex"}}>
      {busy?<RefreshCw size={size} className="spin"/>:<Download size={size}/>}
    </button>
    {open&&<div style={{position:"absolute",right:0,top:"100%",marginTop:4,zIndex:80,background:"var(--card)",border:"1px solid var(--border)",borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,0.3)",minWidth:128,overflow:"hidden"}}>
      <button style={EXPORT_MENU_ITEM} onClick={()=>run("excel")}><FileSpreadsheet size={13} color="var(--success)"/>Excel</button>
      <button style={EXPORT_MENU_ITEM} onClick={()=>run("pdf")}><FileText size={13} color="var(--danger)"/>PDF</button>
    </div>}
  </div>;
}

// Export de una sola tarjeta KPI: una fila con indicador/valor/detalle
const KPI_EXPORT_COLS=[{key:"indicador",label:"Indicador"},{key:"valor",label:"Valor"},{key:"detalle",label:"Detalle"}];
const kpiExportRows=(indicador,valor,detalle)=>[{indicador,valor,detalle:detalle||""}];

const printInvoice=(inv,cli,settings)=>{const w=window.open("","_blank","width=900,height=1050");w.document.write(buildInvoiceHTML(inv,cli,settings));w.document.close();w.focus();setTimeout(()=>w.print(),700);};

const generatePDFAndWhatsApp=async(inv,cli,settings,addToast,setLoading)=>{
  setLoading(true);addToast("Generando PDF…","info");
  try{
    await Promise.all([loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"),loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js")]);
    const html=buildInvoiceHTML(inv,cli,settings);
    const wrap=document.createElement("div");
    wrap.style.cssText="position:fixed;left:-9999px;top:0;width:794px;background:#fff;z-index:-100;";
    wrap.innerHTML=html.replace(/<!DOCTYPE[^>]*>|<html[^>]*>|<\/html>|<head>[\s\S]*<\/head>|<body>|<\/body>/gi,"");
    document.body.appendChild(wrap);
    await new Promise(r=>setTimeout(r,600));
    const canvas=await window.html2canvas(wrap,{scale:2,useCORS:true,backgroundColor:"#ffffff",logging:false});
    document.body.removeChild(wrap);
    const{jsPDF}=window.jspdf;
    const pdf=new jsPDF({orientation:"portrait",unit:"mm",format:"a4"});
    const pw=pdf.internal.pageSize.getWidth();
    const ph=pdf.internal.pageSize.getHeight();
    const ih=(canvas.height*pw)/canvas.width;
    if(ih<=ph){pdf.addImage(canvas.toDataURL("image/jpeg",0.93),"JPEG",0,0,pw,ih);}
    else{const ratio=canvas.width/pw;const sliceH=ph*ratio;let off=0;while(off<canvas.height){if(off>0)pdf.addPage();const sl=document.createElement("canvas");sl.width=canvas.width;sl.height=Math.min(sliceH,canvas.height-off);sl.getContext("2d").drawImage(canvas,0,-off);pdf.addImage(sl.toDataURL("image/jpeg",0.93),"JPEG",0,0,pw,(sl.height*pw)/sl.width);off+=sliceH;}}
    const fname=`${inv.number}_${cli?(cli.lastName||"").replace(/\s/g,"_"):"cliente"}.pdf`;
    pdf.save(fname);
    const phone="505"+(cli?cli.whatsapp:"").replace(/\D/g,"").slice(-8);
    const msg=[`*${cli?cli.name+" "+cli.lastName:"Cliente"}*`,``,`Adjuntamos su comprobante de pago:`,``,`*${inv.number}*`,`*Total USD:* $${parseFloat(inv.totalUsd).toFixed(2)}`,`*Total NIO:* C$${parseFloat(inv.totalNio).toFixed(2)}`,`Paquetes: ${(inv.pkgIds||[]).length}`,`Pago: ${inv.payMethod}${inv.reference?" - Ref: "+inv.reference:""}`,`Fecha: ${fmtDate(inv.fecha)}`,``,`Gracias por preferirnos.`,`*${settings.companyName}*`].join("\n");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`,"_blank");
    addToast(`PDF ${fname} descargado. WhatsApp abierto.`,"success");
  }catch(err){addToast("Error al generar PDF: "+(err.message||"desconocido"),"error");}
  finally{setLoading(false);}
};

/* ── ORDEN DE PAGO — PDF + WhatsApp ────────────────────────────────── */
const N2="#1e3a5f";const LB2="#eef3fa";const GR2="#f8fafc";
function buildOrdenHTML(ord,cli,settings){
  const f2=v=>parseFloat(v||0).toFixed(2);
  const{lines,adjustment}=billedLineItems(ord);
  const rows=lines.map(d=>`<tr style="border-bottom:1px solid #e2e8f0;">
    <td style="padding:9px 12px;font-size:13px;font-family:monospace;font-weight:700;color:${N2};">${d.code}</td>
    <td style="padding:9px 12px;font-size:12px;color:#555;">${d.tracking||"—"}</td>
    <td style="padding:9px 12px;font-size:12px;color:#333;">${d.type}</td>
    <td style="padding:9px 12px;text-align:right;font-size:13px;font-weight:700;">${f2(d.billedWeight)}</td>
    <td style="padding:9px 12px;text-align:right;font-size:12px;">$${f2(d.rate)}</td>
    <td style="padding:9px 12px;text-align:right;font-size:14px;font-weight:700;color:${N2};">$${f2(d.billedSubtotal)}</td>
  </tr>`).join("")+(adjustment?`<tr style="background:#fffbeb;border-bottom:1px solid #e2e8f0;">
    <td colspan="3" style="padding:9px 12px;font-size:12px;color:#92400e;font-style:italic;">${adjustment.label}</td>
    <td style="padding:9px 12px;text-align:right;font-size:13px;font-weight:700;color:#92400e;">+${f2(adjustment.weight)}</td>
    <td></td>
    <td style="padding:9px 12px;text-align:right;font-size:14px;font-weight:700;color:#92400e;">+$${f2(adjustment.amount)}</td>
  </tr>`:"");
  const discLine=ord.discAmt>0?`<tr><td colspan="5" style="text-align:right;padding:6px 12px;font-size:13px;color:#059669;">Descuento (${ord.discType==="porcentaje"?ord.discVal+"%":"$"+f2(ord.discVal)})</td><td style="text-align:right;padding:6px 12px;font-size:13px;color:#059669;font-weight:700;">-$${f2(ord.discAmt)}</td></tr>`:"";
  const stateColor=ord.status==="Pendiente"?"#d97706":ord.status==="Facturada"?"#065f46":"#991b1b";
  const stateBg=ord.status==="Pendiente"?"#fef3c7":ord.status==="Facturada"?"#d1fae5":"#fee2e2";
  return`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${ord.number} - ${settings.companyName}</title><style>*{box-sizing:border-box;margin:0;padding:0;}body{background:#fff;font-family:Arial,Helvetica,sans-serif;}@media print{@page{margin:10mm;size:A4;}}</style></head><body><div style="max-width:750px;margin:0 auto;padding:32px 36px;background:#fff;color:#1a1a1a;">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:18px;border-bottom:3px solid ${N2};">
    <div><img src="${settings.logo||LOGO}" alt="${settings.companyName}" style="height:65px;display:block;margin-bottom:8px;"/><div style="font-size:11px;color:#666;line-height:1.7;">${settings.ruc?"RUC: "+settings.ruc+"<br/>":""}${settings.address||""}<br/>📱 ${settings.whatsapp||""} ${settings.email?"· ✉ "+settings.email:""}</div></div>
    <div style="text-align:right;"><div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:4px;">ORDEN DE PAGO</div><div style="font-size:26px;font-weight:800;color:${N2};font-family:monospace;">${ord.number}</div><div style="font-size:12px;color:#666;margin-top:4px;">Fecha: ${fmtDate(ord.fecha)}</div><div style="margin-top:8px;display:inline-block;background:${stateBg};color:${stateColor};padding:4px 14px;border-radius:20px;font-size:12px;font-weight:700;">${ord.status}</div></div>
  </div>
  <div style="background:${LB2};border:1px solid #bcd4ed;border-radius:8px;padding:14px 18px;margin-bottom:20px;">
    <div style="font-size:10px;color:#666;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:4px;">COBRAR A</div>
    <div style="font-size:18px;font-weight:700;color:${N2};">${cli?cli.name+" "+cli.lastName:"—"}</div>
    <div style="font-size:12px;color:#555;margin-top:3px;">📱 ${cli?cli.whatsapp:""} ${cli&&cli.email?"· ✉ "+cli.email:""} ${cli&&cli.address?"· "+cli.address:""}</div>
  </div>
  <table style="width:100%;border-collapse:collapse;margin-bottom:16px;">
    <thead><tr style="background:${N2};color:white;"><th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;">Código</th><th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:700;">Tracking</th><th style="padding:10px 12px;text-align:left;font-size:11px;font-weight:700;">Tipo</th><th style="padding:10px 12px;text-align:right;font-size:11px;font-weight:700;">Peso facturado (lb)</th><th style="padding:10px 12px;text-align:right;font-size:11px;font-weight:700;">Tarifa</th><th style="padding:10px 12px;text-align:right;font-size:11px;font-weight:700;">Subtotal</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot>${discLine}<tr style="background:${GR2};border-top:1px solid #cbd5e1;"><td colspan="5" style="text-align:right;padding:10px 12px;font-size:12px;color:#64748b;font-weight:600;text-transform:uppercase;">Subtotal USD</td><td style="text-align:right;padding:10px 12px;font-size:13px;color:#334155;">$${f2(ord.subtotalUsd)}</td></tr>
    <tr style="background:${N2};"><td colspan="5" style="text-align:right;padding:14px 12px;font-size:13px;color:rgba(255,255,255,0.85);font-weight:700;text-transform:uppercase;">TOTAL A PAGAR (USD)</td><td style="text-align:right;padding:14px 12px;font-size:22px;color:#fff;font-weight:800;">$${f2(ord.totalUsd)}</td></tr>
    <tr style="background:${LB2};"><td colspan="5" style="text-align:right;padding:8px 12px;font-size:12px;color:#475569;">Total córdobas (T.C. C$${f2(ord.exRate)} × $)</td><td style="text-align:right;padding:8px 12px;font-size:14px;color:${N2};font-weight:700;">C$${f2(ord.totalNio)}</td></tr></tfoot>
  </table>
  ${ord.notes?`<div style="background:#fffbeb;border:1px solid #fcd34d;border-radius:8px;padding:12px 16px;margin-bottom:16px;font-size:13px;color:#78350f;"><strong>Nota:</strong> ${ord.notes}</div>`:""}
  ${ord.facturaNumber?`<div style="background:#d1fae5;border:1px solid #6ee7b7;border-radius:8px;padding:12px 16px;margin-bottom:16px;font-size:13px;color:#065f46;"><strong>✅ Convertida a Factura:</strong> ${ord.facturaNumber}</div>`:""}
  ${(settings.bancos||[]).length>0?`<div style="display:flex;justify-content:flex-end;margin-bottom:16px;">
    <div style="background:${LB2};border:1px solid #bcd4ed;border-radius:8px;padding:12px 16px;min-width:280px;max-width:380px;">
      <div style="font-size:10px;color:#666;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:8px;">INFORMACION PARA PAGO</div>
      <div style="display:flex;flex-direction:column;gap:6px;">
        ${(settings.bancos||[]).map(b=>`<div style="background:#fff;border:1px solid #dde8f5;border-radius:5px;padding:6px 12px;font-size:12px;color:#334;display:flex;justify-content:space-between;align-items:center;">
          <span><span style="font-weight:700;color:${N2};">${b.banco}</span> Cuenta ${b.moneda}:</span><span style="font-family:monospace;font-weight:600;color:${N2};">${b.cuenta}</span>
        </div>`).join("")}
      </div>
    </div>
  </div>`:""}
  <div style="text-align:center;font-size:11px;color:#aaa;border-top:1px solid #e5e7eb;padding-top:14px;"><strong style="color:#666;">${settings.companyName}</strong> — Tu agencia de confianza · ${settings.address||""} · ${settings.whatsapp||""}</div>
</div></body></html>`;
}

const generateOrdenPDFAndWhatsApp=async(ord,cli,settings,addToast,setLoading)=>{
  setLoading(true);addToast("Generando PDF…","info");
  try{
    await Promise.all([loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"),loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js")]);
    const html=buildOrdenHTML(ord,cli,settings);
    const wrap=document.createElement("div");
    wrap.style.cssText="position:fixed;left:-9999px;top:0;width:794px;background:#fff;z-index:-100;";
    wrap.innerHTML=html.replace(/<!DOCTYPE[^>]*>|<html[^>]*>|<\/html>|<head>[\s\S]*<\/head>|<body>|<\/body>/gi,"");
    document.body.appendChild(wrap);
    await new Promise(r=>setTimeout(r,600));
    const canvas=await window.html2canvas(wrap,{scale:2,useCORS:true,backgroundColor:"#ffffff",logging:false});
    document.body.removeChild(wrap);
    const{jsPDF}=window.jspdf;
    const pdf=new jsPDF({orientation:"portrait",unit:"mm",format:"a4"});
    const pw=pdf.internal.pageSize.getWidth();
    const ph=pdf.internal.pageSize.getHeight();
    const ih=(canvas.height*pw)/canvas.width;
    if(ih<=ph){pdf.addImage(canvas.toDataURL("image/jpeg",0.93),"JPEG",0,0,pw,ih);}
    else{const ratio=canvas.width/pw;const sliceH=ph*ratio;let off=0;while(off<canvas.height){if(off>0)pdf.addPage();const sl=document.createElement("canvas");sl.width=canvas.width;sl.height=Math.min(sliceH,canvas.height-off);sl.getContext("2d").drawImage(canvas,0,-off);pdf.addImage(sl.toDataURL("image/jpeg",0.93),"JPEG",0,0,pw,(sl.height*pw)/sl.width);off+=sliceH;}}
    const fname=`${ord.number}_${cli?(cli.lastName||"").replace(/\s/g,"_"):"cliente"}.pdf`;
    pdf.save(fname);
    const phone="505"+(cli?cli.whatsapp:"").replace(/\D/g,"").slice(-8);
    const bancosMsg=(settings.bancos||[]).length
      ?[``,`*Información para pago:*`,...settings.bancos.map(b=>`${b.banco} (${b.moneda}): ${b.cuenta}`)]
      :[];
    const msg=[`*${cli?cli.name+" "+cli.lastName:"Cliente"}*`,``,`Le enviamos su Orden de Pago:`,``,`*${ord.number}*`,`*Total USD:* $${parseFloat(ord.totalUsd).toFixed(2)}`,`*Total NIO:* C$${parseFloat(ord.totalNio).toFixed(2)}`,`Paquetes: ${(ord.pkgIds||[]).length}`,`Fecha: ${fmtDate(ord.fecha)}`,...bancosMsg,``,`Gracias por preferirnos.`,`*${settings.companyName}*`].join("\n");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`,"_blank");
    addToast(`PDF ${fname} descargado. WhatsApp abierto — adjunte el archivo.`,"success");
  }catch(err){addToast("Error al generar PDF: "+(err.message||"desconocido"),"error");}
  finally{setLoading(false);}
};

/* ── UI PRIMITIVES ──────────────────────────────────────────────────── */
function Badge({v}){const norm=s=>(s||'').normalize('NFD').replace(/[̀-ͯ]/g,'').replace(/s+/g,'-').replace(/é/g,'e').replace(/í/g,'i').replace(/ó/g,'o').replace(/á/g,'a').replace(/ú/g,'u');return<span className={`badge badge--${norm(v)}`}>{v}</span>;}
function FInput({label,value,onChange,placeholder,type="text",error,required,disabled,style={},onKeyDown,suffix,min,max}){return<div className="field">{label&&<label className="ctrl-label">{label}{required&&<span className="req"> *</span>}</label>}<div style={{position:"relative"}}><input type={type} value={value} onChange={e=>onChange(e.target.value)} onKeyDown={onKeyDown} placeholder={placeholder} disabled={disabled} min={min} max={max} className={`ctrl-input${error?' has-error':''}`} style={{...style,paddingRight:suffix?36:undefined}}/>{suffix&&<div style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",display:"flex",alignItems:"center"}}>{suffix}</div>}</div>{error&&<span className="ctrl-error">{error}</span>}</div>;}

/* ── PAGINATION ─────────────────────────────────────────────────────── */
function usePagination(items,def=10){
  const[page,setPage]=useState(1);
  const[perPage,setPerPage]=useState(def);
  const totalPages=Math.max(1,Math.ceil(items.length/perPage));
  const paged=items.slice((page-1)*perPage,page*perPage);
  const goPage=p=>setPage(Math.max(1,Math.min(p,totalPages)));
  const changePerPage=n=>{setPerPage(n);setPage(1);};
  return{paged,page,goPage,perPage,changePerPage,total:items.length,totalPages};
}
function Pagination({page,totalPages,goPage,perPage,changePerPage,total}){
  if(total===0)return null;
  const start=(page-1)*perPage+1,end=Math.min(page*perPage,total);
  const btnSty={display:"flex",alignItems:"center",justifyContent:"center",width:28,height:28,borderRadius:6,border:"1px solid var(--border)",background:"transparent",cursor:"pointer",color:"var(--text)",fontSize:13};
  return<div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0 0",gap:8,flexWrap:"wrap"}}>
    <span style={{fontSize:12,color:"var(--muted)"}}>Mostrando {start}–{end} de {total}</span>
    <div style={{display:"flex",alignItems:"center",gap:6}}>
      <select value={perPage} onChange={e=>changePerPage(+e.target.value)} className="ctrl-input" style={{width:"auto",fontSize:12,padding:"4px 8px"}}>
        {[10,25,50,100].map(n=><option key={n} value={n}>{n} / pág</option>)}
      </select>
      <button style={{...btnSty,opacity:page<=1?0.35:1}} disabled={page<=1} onClick={()=>goPage(page-1)}><ChevronLeft size={14}/></button>
      <span style={{fontSize:12,color:"var(--muted)",minWidth:48,textAlign:"center"}}>{page} / {totalPages}</span>
      <button style={{...btnSty,opacity:page>=totalPages?0.35:1}} disabled={page>=totalPages} onClick={()=>goPage(page+1)}><ChevronR size={14}/></button>
    </div>
  </div>;
}
function FilterChips({opts,val,set}){return<div style={{display:"flex",gap:5,flexWrap:"wrap"}}>{["",  ...opts].map(o=>{const v=typeof o==="string"?o:o.v;const l=typeof o==="string"?o:o.l;return<button key={v} onClick={()=>set(val===v&&v!==""?"":v)} style={{padding:"4px 12px",borderRadius:20,border:`1px solid ${val===v&&v!==""?'var(--accent)':'var(--border)'}`,background:val===v&&v!==""?'var(--accent-dim)':"transparent",color:val===v&&v!==""?'var(--accent)':'var(--muted)',fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>{l||"Todos"}</button>;})}</div>;}
function FSelect({label,value,onChange,options,error,required,disabled}){return<div className="field">{label&&<label className="ctrl-label">{label}{required&&<span className="req"> *</span>}</label>}<select value={value} onChange={e=>onChange(e.target.value)} disabled={disabled} className={`ctrl-input${error?' has-error':''}`} style={{appearance:"none",cursor:"pointer"}}><option value="">— Seleccionar —</option>{options.map(o=><option key={typeof o==="object"?o.v:o} value={typeof o==="object"?o.v:o}>{typeof o==="object"?o.l:o}</option>)}</select>{error&&<span className="ctrl-error">{error}</span>}</div>;}
function Modal({title,onClose,children,width=520}){return<div className="modal-overlay"><div className="modal-box" style={{maxWidth:width}}><div className="modal-header"><h2 className="modal-title">{title}</h2><button onClick={onClose} className="btn--icon"><X size={18}/></button></div>{children}</div></div>;}
function StatCard({icon:Icon,title,value,sub,color='var(--accent)',exportRows,exportColumns,exportFilename,addToast,breakdown}){return<div className="stat-card"><div className="stat-card__hd"><span className="stat-card__lbl">{title}</span><div style={{display:"flex",alignItems:"center",gap:2}}>{exportRows&&<ExportButton rows={exportRows} columns={exportColumns} filename={exportFilename||title} title={title} addToast={addToast} size={12}/>}<div className="stat-card__ico" style={{background:color+'20'}}><Icon size={17} color={color}/></div></div></div><div className="stat-card__val">{value}</div>{sub&&<div className="stat-card__sub">{sub}</div>}{breakdown&&<div style={{display:"flex",gap:12,marginTop:6,flexWrap:"wrap"}}>{breakdown.map((b,i)=><span key={i} style={{fontSize:11,color:"var(--muted)"}}>{b.label}: <b style={{color:"var(--text)"}}>{b.value}</b></span>)}</div>}</div>;}
function PH({title,sub,action}){return<div className="page-hd"><div><h1 className="page-hd__ttl">{title}</h1>{sub&&<p className="page-hd__sub">{sub}</p>}</div>{action}</div>;}

/* ── FILTRO GLOBAL DE PERÍODO — Dashboard + Reportes ──────────────────── */
const PERIOD_PRESETS=[
  {v:"hoy",l:"Hoy"},{v:"ayer",l:"Ayer"},{v:"semana",l:"Esta semana"},
  {v:"mes",l:"Este mes"},{v:"mesAnterior",l:"Mes anterior"},{v:"anio",l:"Este año"},
  {v:"historial",l:"Todo el historial"},{v:"rango",l:"Rango personalizado"},
];
const PERIOD_PRESET_LABEL={hoy:"Hoy",ayer:"Ayer",semana:"Esta semana",mes:"Este mes",mesAnterior:"Mes anterior",anio:"Este año"};

function usePeriodFilter(){
  const[preset,setPreset]=useState("historial");
  const[desde,setDesde]=useState(()=>{const d=new Date();d.setDate(1);return d.toISOString().slice(0,10);});
  const[hasta,setHasta]=useState(today);
  const[selPId,setSelPId]=useState("");
  return{preset,setPreset,desde,setDesde,hasta,setHasta,selPId,setSelPId};
}

// Resuelve el filtro (preset|rango|período de negocio) a {desde,hasta,label}
// listo para pasar a computeReportData. label="" cuando no hay filtro activo
// (Todo el historial) — mismo criterio que ya usaba el banner de Reportes.
const resolvePeriodFilter=(filter,periodos)=>{
  const{preset,desde,hasta,selPId}=filter;
  if(preset==="periodo"){
    const p=(periodos||[]).find(x=>String(x.id)===String(selPId));
    if(!p)return{desde:null,hasta:null,label:""};
    return{desde:p.fechaInicio,hasta:p.fechaFin,label:`Período: ${p.nombre} (${fmtDate(p.fechaInicio)} → ${fmtDate(p.fechaFin)})`};
  }
  if(preset==="rango"){
    return{desde:desde||null,hasta:hasta||null,label:desde&&hasta?`Rango: ${fmtDate(desde)} → ${fmtDate(hasta)}`:""};
  }
  const r=presetRange(preset)||{desde:null,hasta:null};
  return{desde:r.desde,hasta:r.hasta,label:PERIOD_PRESET_LABEL[preset]||""};
};

function PeriodFilterBar({filter,periodos,includePeriodos,label}){
  const{preset,setPreset,desde,setDesde,hasta,setHasta,selPId,setSelPId}=filter;
  const selSty={appearance:"none",cursor:"pointer",background:"var(--input)",border:"1px solid var(--border)",borderRadius:7,padding:"6px 10px",color:"var(--text)",fontSize:12,fontFamily:"var(--font)",outline:"none"};
  return<div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
    <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",justifyContent:"flex-end"}}>
      <Filter size={13} color="var(--muted)"/>
      <select value={preset} onChange={e=>setPreset(e.target.value)} style={selSty}>
        {PERIOD_PRESETS.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
        {includePeriodos&&<option value="periodo">Período (definido en Períodos)</option>}
      </select>
      {preset==="periodo"&&includePeriodos&&<select value={selPId} onChange={e=>setSelPId(e.target.value)} style={selSty}>
        <option value="">— Seleccionar período —</option>
        {(periodos||[]).map(p=><option key={p.id} value={p.id}>{p.nombre} ({p.estado})</option>)}
      </select>}
      {preset==="rango"&&<>
        <input type="date" value={desde} onChange={e=>setDesde(e.target.value)} style={selSty}/>
        <span style={{color:"var(--muted)",fontSize:12}}>→</span>
        <input type="date" value={hasta} onChange={e=>setHasta(e.target.value)} style={selSty}/>
      </>}
    </div>
    {label&&<div style={{fontSize:11,color:"var(--accent)",display:"flex",alignItems:"center",gap:5}}>
      <b>Filtro:</b> {label}
    </div>}
  </div>;
}
function SearchBar({value,onChange,placeholder="Buscar..."}){return<div className="search-bar"><Search size={14} className="search-bar__icon"/><input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} className="ctrl-input"/></div>;}
function Table({cols,rows,empty="Sin registros",sort,onSort}){return<div className="table-wrap"><table className="data-table"><thead><tr>{cols.map(c=><th key={c.k}>{c.sortKey?<button onClick={()=>onSort(c.sortKey)} style={{background:"none",border:"none",cursor:"pointer",display:"inline-flex",alignItems:"center",gap:4,color:"inherit",font:"inherit",padding:0}}>{c.l}{sort?.key===c.sortKey?(sort.dir==="asc"?<ChevronUp size={12}/>:<ChevronDown size={12}/>):<ChevronsUpDown size={12} style={{opacity:0.35}}/>}</button>:c.l}</th>)}</tr></thead><tbody>{!rows.length?<tr><td colSpan={cols.length} className="empty-cell">{empty}</td></tr>:rows.map((r,i)=><tr key={i}>{cols.map(c=><td key={c.k}>{c.render?c.render(r,i):r[c.k]??"-"}</td>)}</tr>)}</tbody></table></div>;}
/* ── LOGIN ──────────────────────────────────────────────────────────── */
function LoginScreen({onLogin,theme,onToggleTheme}){
  const [email,setEmail]=useState("");const[pwd,setPwd]=useState("");const[err,setErr]=useState("");const[loading,setLoading]=useState(false);const[showPwd,setShowPwd]=useState(false);
  const logoSrc=useMemo(()=>{try{return JSON.parse(localStorage.getItem("mb_logo")||"{}")?.logo||LOGO;}catch{return LOGO;}},[]);
  const go=async()=>{
    if(!email||!pwd){setErr("Completa todos los campos");return;}
    setLoading(true);setErr("");
    try{
      const res=await API.auth.login({email,password:pwd});
      localStorage.setItem("mb_token",res.token);
      onLogin(res.user);
    }catch(e){setErr(e.message||"Error al iniciar sesión");}
    finally{setLoading(false);}
  };
  return<div className="login-wrap">
    <button onClick={onToggleTheme} className="login-theme-btn" title="Cambiar a modo claro/oscuro" style={{position:"fixed",top:18,right:18}}>
      {theme==="light"?<Moon size={17}/>:<Sun size={17}/>}
    </button>
    <img src={logoSrc} alt="Momotombo Box" className="login-logo"/>
    <div className="card" style={{width:"100%",maxWidth:400}}>
      <p className="c-muted fs-13 text-center mb-20">Sistema de gestión de paquetería</p>
      <div className="flex-col gap-14">
        <FInput label="Correo electrónico" value={email} onChange={v=>{setEmail(v);setErr("");}} placeholder="usuario@empresa.com" type="email" onKeyDown={e=>e.key==="Enter"&&go()}/>
        <FInput label="Contraseña" value={pwd} onChange={v=>{setPwd(v);setErr("");}} placeholder="••••••••" type={showPwd?"text":"password"} onKeyDown={e=>e.key==="Enter"&&go()} suffix={<button type="button" onClick={()=>setShowPwd(p=>!p)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--muted)",padding:2,display:"flex",alignItems:"center"}}>{showPwd?<EyeOff size={15}/>:<Eye size={15}/>}</button>}/>
        {err&&<div className="alert alert--danger"><AlertCircle size={13}/>{err}</div>}
        <Btn onClick={go} size="lg" className="btn--full mt-4" disabled={loading}>
          {loading?<><RefreshCw size={15} className="spin"/>Ingresando…</>:<><Lock size={14}/>Ingresar al sistema</>}
        </Btn>
      </div>
    </div>
  </div>;
}

/* ── DASHBOARD ──────────────────────────────────────────────────────── */
const INV_EXPORT_COLS=clients=>[
  {key:"number",label:"Número"},
  {key:"cliente",label:"Cliente",value:r=>{const c=clients.find(x=>x.id===r.clientId);return c?`${c.name} ${c.lastName}`:"";}},
  {key:"fecha",label:"Fecha",value:r=>fmtDate(r.fecha)},
  {key:"totalUsd",label:"Total USD",value:r=>fmtUsd(r.totalUsd)},
  {key:"status",label:"Estado"},
];
const PKG_EXPORT_COLS=clients=>[
  {key:"code",label:"Código"},
  {key:"cliente",label:"Cliente",value:r=>{const c=clients.find(x=>x.id===r.clientId);return c?`${c.name} ${c.lastName}`:"";}},
  {key:"type",label:"Tipo"},
  {key:"weight",label:"Peso (lb)"},
  {key:"status",label:"Estado"},
  {key:"rxDate",label:"Recepción",value:r=>fmtDate(r.rxDate)},
];
const CLI_EXPORT_COLS=[
  {key:"code",label:"Código"},
  {key:"nombre",label:"Nombre",value:r=>`${r.name} ${r.lastName}`},
  {key:"whatsapp",label:"WhatsApp"},
  {key:"label",label:"Etiqueta"},
  {key:"status",label:"Estado"},
];
const ORD_EXPORT_COLS=clients=>[
  {key:"number",label:"Número"},
  {key:"cliente",label:"Cliente",value:r=>{const c=clients.find(x=>x.id===r.clientId);return c?`${c.name} ${c.lastName}`:"";}},
  {key:"fecha",label:"Fecha",value:r=>fmtDate(r.fecha)},
  {key:"totalUsd",label:"Total USD",value:r=>fmtUsd(r.totalUsd)},
  {key:"status",label:"Estado"},
];
const GASTO_EXPORT_COLS=[
  {key:"fecha",label:"Fecha",value:r=>fmtDate(r.fecha)},
  {key:"categoria",label:"Categoría"},
  {key:"proveedor",label:"Proveedor"},
  {key:"descripcion",label:"Descripción"},
  {key:"montoUsd",label:"Monto USD",value:r=>fmtUsd(r.montoUsd)},
  {key:"estado",label:"Estado"},
];

// Catálogo de tarjetas KPI personalizables (Fase 6) — cada una declara el
// permiso que la habilita (reutilizando can(user,slug), igual que NAV_PERM)
// y su propio render con la data ya calculada por DashboardModule. Se
// renderizan todas juntas en un único grid-4, así que reordenarlas por
// arrastre nunca rompe un layout de 2 columnas.
const DASHBOARD_STAT_DEFS=[
  {id:"ingresos_totales",permiso:"facturas:ver",label:"Ingresos totales",
   render:ctx=><StatCard key="ingresos_totales" icon={DollarSign} title="Ingresos totales" value={fmtUsd(ctx.agg.totalRev)} sub={`${ctx.agg.cntRecibos} facturas pagadas`} color={'var(--success)'} addToast={ctx.addToast} exportRows={ctx.agg.facturasPagadasList} exportColumns={INV_EXPORT_COLS(ctx.clients)} exportFilename="ingresos_totales"/>},
  {id:"paquetes_pendientes",permiso:"paquetes:ver",label:"Paquetes pendientes",
   render:ctx=><StatCard key="paquetes_pendientes" icon={Package} title="Paquetes pendientes" value={ctx.pkgsPend.length} sub={`${ctx.packages.length} total`} color={'var(--accent)'} addToast={ctx.addToast} exportRows={ctx.pkgsPend} exportColumns={PKG_EXPORT_COLS(ctx.clients)} exportFilename="paquetes_pendientes"/>},
  {id:"clientes_activos",permiso:"clientes:ver",label:"Clientes activos",
   render:ctx=><StatCard key="clientes_activos" icon={Users} title="Clientes activos" value={ctx.clisAct.length} sub={`${ctx.clients.length} total`} color={'var(--violet)'} addToast={ctx.addToast} exportRows={ctx.clisAct} exportColumns={CLI_EXPORT_COLS} exportFilename="clientes_activos"/>},
  {id:"entregados",permiso:"paquetes:ver",label:"Entregados",
   render:ctx=><StatCard key="entregados" icon={CheckCircle} title="Entregados" value={ctx.pkgsEnt.length} sub="Confirmados físicamente" color={'var(--success)'} addToast={ctx.addToast} exportRows={ctx.pkgsEnt} exportColumns={PKG_EXPORT_COLS(ctx.clients)} exportFilename="paquetes_entregados"/>},
  {id:"paquetes_por_facturar",permiso:"ordenes:ver",label:"Paquetes por facturar",
   render:ctx=><StatCard key="paquetes_por_facturar" icon={ClipboardList} title="Paquetes por facturar" value={ctx.agg.paquetesPorFacturar} sub="En órdenes de pago" color={'var(--warn)'} addToast={ctx.addToast} exportRows={ctx.agg.paquetesPorFacturarList} exportColumns={PKG_EXPORT_COLS(ctx.clients)} exportFilename="paquetes_por_facturar_op"/>},
  {id:"libras_gestionadas",permiso:"paquetes:ver",label:"Libras gestionadas",
   render:ctx=><StatCard key="libras_gestionadas" icon={Layers} title="Libras gestionadas" value={fmtLbs(ctx.agg.librasGestionadas.total)} color={'var(--info)'} addToast={ctx.addToast} exportRows={ctx.agg.paquetesRegList} exportColumns={PKG_EXPORT_COLS(ctx.clients)} exportFilename="libras_gestionadas" breakdown={[{label:"Aéreo",value:fmtLbs(ctx.agg.librasGestionadas.aereo)},{label:"Marítimo",value:fmtLbs(ctx.agg.librasGestionadas.maritimo)}]}/>},
  {id:"recibos_emitidos",permiso:"facturas:ver",label:"Recibos emitidos",
   render:ctx=><StatCard key="recibos_emitidos" icon={FileText} title="Recibos emitidos" value={ctx.agg.cntRecibos} sub={ctx.agg.cntAnulados?`${ctx.agg.cntAnulados} anulados`:"Sin anulados"} color={'var(--success)'} addToast={ctx.addToast} exportRows={ctx.agg.facturasPagadasList} exportColumns={INV_EXPORT_COLS(ctx.clients)} exportFilename="recibos_emitidos"/>},
  {id:"paquetes_registrados",permiso:"paquetes:ver",label:"Paquetes registrados",
   render:ctx=><StatCard key="paquetes_registrados" icon={Package} title="Paquetes registrados" value={ctx.agg.paquetesRegistrados} sub={`${ctx.agg.paquetesEntregados} entregados`} color={'var(--accent)'} addToast={ctx.addToast} exportRows={ctx.agg.paquetesRegList} exportColumns={PKG_EXPORT_COLS(ctx.clients)} exportFilename="paquetes_registrados"/>},
  {id:"op_pendientes",permiso:"ordenes:ver",label:"OP Pendientes",
   render:ctx=><StatCard key="op_pendientes" icon={Receipt} title="OP Pendientes" value={ctx.opPendList.length} sub={`${ctx.opConvList.length} convertidas este mes`} color={'var(--warn)'} addToast={ctx.addToast} exportRows={ctx.opPendList} exportColumns={ORD_EXPORT_COLS(ctx.clients)} exportFilename="op_pendientes"/>},
  {id:"op_convertidas",permiso:"ordenes:ver",label:"OP Convertidas",
   render:ctx=><StatCard key="op_convertidas" icon={CheckCircle} title="OP Convertidas" value={ctx.opConvList.length} sub={`${ctx.ordenes.length} órdenes total`} color={'var(--info)'} addToast={ctx.addToast} exportRows={ctx.opConvList} exportColumns={ORD_EXPORT_COLS(ctx.clients)} exportFilename="op_convertidas"/>},
  {id:"gastos_mes",permiso:"gastos:ver",label:"Gastos totales",
   render:ctx=><StatCard key="gastos_mes" icon={TrendingDown} title="Gastos totales" value={fmtUsd(ctx.agg.totalGastos)} sub={`${ctx.agg.cntGastos} registros`} color={'var(--danger)'} addToast={ctx.addToast} exportRows={ctx.agg.gastosEnRangoList} exportColumns={GASTO_EXPORT_COLS} exportFilename="gastos_totales"/>},
  {id:"utilidad_bruta",permiso:"facturas:ver",label:"Utilidad bruta",
   render:ctx=><StatCard key="utilidad_bruta" icon={DollarSign} title="Utilidad bruta" value={fmtUsd(Math.max(0,ctx.agg.totalRev-ctx.agg.totalGastos))} sub="Ingresos del período − Gastos del período" color={ctx.agg.totalRev>=ctx.agg.totalGastos?'var(--success)':'var(--danger)'} addToast={ctx.addToast} exportRows={kpiExportRows("Utilidad bruta",fmtUsd(Math.max(0,ctx.agg.totalRev-ctx.agg.totalGastos)),"Ingresos del período − Gastos del período")} exportColumns={KPI_EXPORT_COLS} exportFilename="utilidad_bruta"/>},
];

// Catálogo de gráficos/listas personalizables — mismo mecanismo de permiso
// y personalización que las StatCard, pero en un grid-2 aparte porque son
// bloques más grandes. Antes de la Fase 6 estos 6 bloques estaban "quemados"
// en el Dashboard: se mostraban (vacíos) incluso a usuarios sin ningún
// permiso relacionado, revelando que el módulo existe. Ahora respetan el
// mismo filtro can(user,permiso) que ya aplican las StatCard.
const DASHBOARD_PANEL_DEFS=[
  {id:"panel_ingresos",permiso:"facturas:ver",label:"Ingresos (gráfico)",
   render:ctx=><div key="panel_ingresos" className="card">
     <div className="row row--between mb-14"><h3 className="c-text fw-700 fs-13">Ingresos (USD)</h3><ExportButton rows={ctx.agg.chartIngresos} columns={[{key:"m",label:"Período"},{key:"v",label:"Ingresos USD",value:r=>fmtUsd(r.v)}]} filename="ingresos" title="Ingresos" addToast={ctx.addToast}/></div>
     {ctx.agg.chartIngresos.length>0?<ResponsiveContainer width="100%" height={190}><AreaChart data={ctx.agg.chartIngresos}><defs><linearGradient id="ga" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={'var(--accent)'} stopOpacity={0.25}/><stop offset="100%" stopColor={'var(--accent)'} stopOpacity={0}/></linearGradient></defs><CartesianGrid strokeDasharray="3 3" stroke={'var(--border)'}/><XAxis dataKey="m" tick={{fill:'var(--muted)',fontSize:11}}/><YAxis tick={{fill:'var(--muted)',fontSize:11}} tickFormatter={v=>`$${v}`}/><Tooltip {...TIP} formatter={v=>[fmtUsd(v),"USD"]}/><Area type="monotone" dataKey="v" stroke={'var(--accent)'} fill="url(#ga)" strokeWidth={2}/></AreaChart></ResponsiveContainer>:<p className="c-muted fs-13 text-center" style={{padding:"30px 0"}}>Sin ingresos en el período</p>}
   </div>},
  {id:"panel_distribucion_tipo",permiso:"paquetes:ver",label:"Distribución por tipo (gráfico)",
   render:ctx=><div key="panel_distribucion_tipo" className="card">
     <div className="row row--between mb-14"><h3 className="c-text fw-700 fs-13">Distribución por tipo</h3><ExportButton rows={ctx.agg.tipoDist} columns={[{key:"n",label:"Tipo"},{key:"v",label:"Paquetes"}]} filename="distribucion_por_tipo" title="Distribución por tipo" addToast={ctx.addToast}/></div>
     {ctx.agg.tipoDist.some(t=>t.v>0)?<>
       <ResponsiveContainer width="100%" height={150}><PieChart><Pie data={ctx.agg.tipoDist} cx="50%" cy="50%" innerRadius={42} outerRadius={68} paddingAngle={4} dataKey="v">{ctx.agg.tipoDist.map((e,i)=><Cell key={i} fill={CC[i]}/>)}</Pie><Tooltip {...TIP} formatter={(v,n,p)=>[v+" paquetes",p.payload.n]}/></PieChart></ResponsiveContainer>
       <div style={{display:"flex",gap:16,justifyContent:"center",marginTop:8}}>{ctx.agg.tipoDist.map((p,i)=><span key={i} style={{fontSize:12,color:'var(--muted)',display:"flex",alignItems:"center",gap:5}}><span style={{width:8,height:8,borderRadius:"50%",background:CC[i],display:"inline-block"}}/><b style={{color:'var(--text)'}}>{p.v}</b> {p.n}</span>)}</div>
     </>:<p className="c-muted fs-13 text-center" style={{padding:"30px 0"}}>Sin paquetes registrados en el período</p>}
   </div>},
  {id:"panel_evolucion_paquetes",permiso:"paquetes:ver",label:"Evolución de paquetes registrados",
   render:ctx=><div key="panel_evolucion_paquetes" className="card">
     <div className="row row--between mb-14"><h3 className="c-text fw-700 fs-13">Evolución de paquetes registrados</h3><ExportButton rows={ctx.agg.chartPaquetes} columns={[{key:"m",label:"Período"},{key:"v",label:"Paquetes"}]} filename="evolucion_paquetes" title="Evolución de paquetes registrados" addToast={ctx.addToast}/></div>
     {ctx.agg.chartPaquetes.length>0?<ResponsiveContainer width="100%" height={180}><BarChart data={ctx.agg.chartPaquetes}><CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/><XAxis dataKey="m" tick={{fill:"var(--muted)",fontSize:11}}/><YAxis tick={{fill:"var(--muted)",fontSize:11}} allowDecimals={false}/><Tooltip {...TIP}/><Bar dataKey="v" name="Paquetes" fill="var(--accent)" radius={[4,4,0,0]}/></BarChart></ResponsiveContainer>:<p className="c-muted fs-13 text-center" style={{padding:"30px 0"}}>Sin paquetes registrados en el período</p>}
   </div>},
  {id:"panel_evolucion_libras",permiso:"paquetes:ver",label:"Evolución de libras gestionadas",
   render:ctx=><div key="panel_evolucion_libras" className="card">
     <div className="row row--between mb-14"><h3 className="c-text fw-700 fs-13">Evolución de libras gestionadas</h3><ExportButton rows={ctx.agg.chartLibras} columns={[{key:"m",label:"Período"},{key:"aereo",label:"Aéreo (lb)"},{key:"maritimo",label:"Marítimo (lb)"}]} filename="evolucion_libras" title="Evolución de libras gestionadas" addToast={ctx.addToast}/></div>
     {ctx.agg.chartLibras.length>0?<ResponsiveContainer width="100%" height={180}><LineChart data={ctx.agg.chartLibras}><CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/><XAxis dataKey="m" tick={{fill:"var(--muted)",fontSize:11}}/><YAxis tick={{fill:"var(--muted)",fontSize:11}}/><Tooltip {...TIP}/><Line type="monotone" dataKey="aereo" name="Aéreo" stroke={CC[0]} strokeWidth={2.5} dot={{r:3}}/><Line type="monotone" dataKey="maritimo" name="Marítimo" stroke={CC[1]} strokeWidth={2.5} dot={{r:3}}/></LineChart></ResponsiveContainer>:<p className="c-muted fs-13 text-center" style={{padding:"30px 0"}}>Sin paquetes registrados en el período</p>}
   </div>},
  {id:"panel_ultimas_facturas",permiso:"facturas:ver",label:"Últimas facturas",
   render:ctx=><div key="panel_ultimas_facturas" className="card">
     <div className="row row--between mb-14"><h3 className="c-text fw-700 fs-13">Últimas facturas</h3><ExportButton rows={ctx.facturasRecientes} columns={INV_EXPORT_COLS(ctx.clients)} filename="ultimas_facturas" title="Últimas facturas" addToast={ctx.addToast}/></div>
     {ctx.invoices.length===0?<p className="c-muted fs-13">Sin facturas</p>:ctx.facturasRecientes.map(inv=>{const cli=ctx.clients.find(c=>c.id===inv.clientId);return<div key={inv.id} style={{display:'flex',gap:12,alignItems:'center',justifyContent:"space-between",padding:"9px 0",borderBottom:`1px solid ${'var(--border)'}`}}><div><code style={{fontSize:12,fontWeight:700,color:'var(--accent)'}}>{inv.number}</code><div style={{fontSize:11,color:'var(--muted)'}}>{cli?`${cli.name} ${cli.lastName}`:""}</div></div><div style={{textAlign:"right"}}><div style={{fontSize:13,fontWeight:700,color:'var(--accent)'}}>{fmtUsd(inv.totalUsd)}</div><Badge v={inv.status}/></div></div>;})}
   </div>},
  {id:"panel_paquetes_sin_facturar",permiso:"paquetes:ver",label:"Paquetes sin facturar",
   render:ctx=><div key="panel_paquetes_sin_facturar" className="card">
     <div className="row row--between mb-14"><h3 className="c-text fw-700 fs-13">Paquetes sin facturar</h3><ExportButton rows={ctx.pkgsSinFacturar} columns={PKG_EXPORT_COLS(ctx.clients)} filename="paquetes_sin_facturar" title="Paquetes sin facturar" addToast={ctx.addToast}/></div>
     {ctx.pkgsPend.length===0?<p className="c-muted fs-13">Sin pendientes</p>:ctx.pkgsSinFacturar.map(pkg=>{const cli=ctx.clients.find(c=>c.id===pkg.clientId);return<div key={pkg.id} style={{display:'flex',gap:12,alignItems:'center',justifyContent:"space-between",padding:"9px 0",borderBottom:`1px solid ${'var(--border)'}`}}><div><code style={{fontSize:12,fontWeight:700,color:'var(--accent)'}}>{pkg.code}</code><div style={{fontSize:11,color:'var(--muted)'}}>{cli?`${cli.name} ${cli.lastName}`:""} · {fmtLbs(pkg.weight)}</div></div><Badge v={pkg.status}/></div>;})}
   </div>},
];

function ManageDashboardCardsModal({groups,current,onClose,onSave}){
  const initOrder=g=>{
    const ids=g.items.map(c=>c.id);
    if(!current)return ids;
    const known=current.filter(id=>ids.includes(id));
    const missing=ids.filter(id=>!known.includes(id));
    return[...known,...missing];
  };
  const[state,setState]=useState(()=>Object.fromEntries(groups.map(g=>[g.key,{
    order:initOrder(g),
    checked:new Set(current?current.filter(id=>g.items.some(c=>c.id===id)):g.items.map(c=>c.id)),
  }])));
  const[saving,setSaving]=useState(false);
  const dragRef=useRef(null);
  const labelOf=(g,id)=>g.items.find(c=>c.id===id)?.label||id;
  const toggle=(gk,id)=>setState(s=>{const grp={...s[gk]};const n=new Set(grp.checked);n.has(id)?n.delete(id):n.add(id);grp.checked=n;return{...s,[gk]:grp};});
  const onDrop=(gk,id)=>e=>{
    e.preventDefault();
    const from=dragRef.current;
    if(!from||from.gk!==gk||from.id===id)return;
    setState(s=>{
      const grp={...s[gk]};const arr=[...grp.order];
      const fi=arr.indexOf(from.id),ti=arr.indexOf(id);
      if(fi<0||ti<0)return s;
      arr.splice(fi,1);arr.splice(ti,0,from.id);
      grp.order=arr;return{...s,[gk]:grp};
    });
  };
  const save=async()=>{
    setSaving(true);
    try{await onSave(groups.flatMap(g=>state[g.key].order.filter(id=>state[g.key].checked.has(id))));}
    finally{setSaving(false);}
  };
  const reset=async()=>{setSaving(true);try{await onSave(null);}finally{setSaving(false);}};
  return<Modal title="Gestionar Dashboard" onClose={onClose} width={520}>
    <p className="c-muted fs-12" style={{marginBottom:14}}>Elige qué mostrar y arrastra para reordenar.</p>
    <div style={{display:"flex",flexDirection:"column",gap:18,maxHeight:440,overflowY:"auto"}}>
      {groups.map(g=><div key={g.key}>
        <div className="fs-11 fw-700" style={{color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>{g.title}</div>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {state[g.key].order.map(id=><div key={id} draggable onDragStart={()=>{dragRef.current={gk:g.key,id};}} onDragOver={e=>e.preventDefault()} onDrop={onDrop(g.key,id)}
            style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",border:"1px solid var(--border)",borderRadius:8,background:"var(--card2)",cursor:"grab"}}>
            <GripVertical size={14} color="var(--muted)"/>
            <label style={{display:"flex",alignItems:"center",gap:8,flex:1,cursor:"pointer",fontSize:13}}>
              <input type="checkbox" checked={state[g.key].checked.has(id)} onChange={()=>toggle(g.key,id)}/>
              {labelOf(g,id)}
            </label>
          </div>)}
          {state[g.key].order.length===0&&<p className="c-muted fs-13">Nada disponible en esta sección.</p>}
        </div>
      </div>)}
    </div>
    <div className="row row--between mt-18">
      <Btn variant="ghost" onClick={reset} disabled={saving}>Restablecer por defecto</Btn>
      <div style={{display:"flex",gap:10}}>
        <Btn variant="secondary" onClick={onClose}>Cancelar</Btn>
        <Btn onClick={save} disabled={saving}>{saving?<><RefreshCw size={13} className="spin"/>Guardando…</>:<><Check size={14}/>Guardar</>}</Btn>
      </div>
    </div>
  </Modal>;
}

function DashboardModule({data,currentUser,addToast}){
  const{clients,packages,invoices,ordenes=[],gastos=[],periodos=[]}=data;
  const filter=usePeriodFilter();
  const{desde,hasta,label}=useMemo(()=>resolvePeriodFilter(filter,periodos),[filter.preset,filter.desde,filter.hasta,filter.selPId,periodos]);
  const agg=useMemo(()=>computeReportData(data,desde,hasta),[data,desde,hasta]);

  // Métricas "de estado" (no dependen del período — reflejan el presente):
  // pendientes/activos/entregados/OP son conteos del sistema tal como está
  // hoy, no de lo ocurrido en el rango seleccionado.
  const pkgsPend=packages.filter(p=>p.status==="Registrado");
  const pkgsEnt=packages.filter(p=>p.status==="Entregado");
  const clisAct=clients.filter(c=>c.status==="Activo");
  const opPendList=ordenes.filter(o=>o.status==="Pendiente");
  const opConvList=ordenes.filter(o=>o.status==="Facturada");
  const facturasRecientes=invoices.slice(0,5);
  const pkgsSinFacturar=pkgsPend.slice(0,5);

  const[cardPrefs,setCardPrefs]=useState(null);
  const[showManage,setShowManage]=useState(false);
  useEffect(()=>{API.dashboardPrefs.get().then(r=>setCardPrefs(r.tarjetas)).catch(()=>{});},[]);

  const allowedStats=DASHBOARD_STAT_DEFS.filter(c=>can(currentUser,c.permiso));
  const allowedPanels=DASHBOARD_PANEL_DEFS.filter(c=>can(currentUser,c.permiso));
  const orderOf=(defs,ids)=>{
    if(!ids)return defs;
    const byId=Object.fromEntries(defs.map(c=>[c.id,c]));
    return ids.map(id=>byId[id]).filter(Boolean);
  };
  const visibleCards=useMemo(()=>orderOf(allowedStats,cardPrefs),[cardPrefs,allowedStats]);
  const visiblePanels=useMemo(()=>orderOf(allowedPanels,cardPrefs),[cardPrefs,allowedPanels]);
  const cardCtx={agg,clients,packages,invoices,gastos,pkgsPend,pkgsEnt,clisAct,opPendList,opConvList,ordenes,facturasRecientes,pkgsSinFacturar,addToast};

  const persistPrefs=async ids=>{
    await API.dashboardPrefs.update(ids);
    setCardPrefs(ids);
  };
  const saveCardPrefs=async ids=>{
    try{
      await persistPrefs(ids);
      addToast("Preferencias del Dashboard guardadas","success");
      setShowManage(false);
    }catch(e){addToast(e.message,"error");}
  };

  // Reordenar en vivo directo sobre el Dashboard (independiente del modal de
  // ocultar/mostrar). Se implementa con eventos de mouse normales (no el
  // drag-and-drop nativo de HTML5): el DnD nativo depende de una "sesión de
  // arrastre" del navegador ligada al nodo del DOM que se está arrastrando,
  // y si ese nodo cambia de posición en el DOM a mitad del gesto (que es
  // justamente lo que hace falta para mostrar el reacomodo en tiempo real),
  // el navegador puede perder esa sesión de forma silenciosa — el primer
  // arrastre "funciona de casualidad" y los siguientes dejan de responder.
  // Con mousedown/mousemove/mouseup no existe esa sesión nativa que romper.
  //
  // El arreglo "committed" (draftStat/Panel) nunca se muta mientras el mouse
  // solo pasa por encima — la vista en vivo se calcula siempre desde el
  // mismo arreglo base + la posición actual del mouse (hoverId), así que el
  // mismo hover repetido da siempre el mismo resultado. El arreglo base solo
  // se actualiza una vez, al soltar el mouse.
  const[reordering,setReordering]=useState(false);
  const[draftStat,setDraftStat]=useState(null);
  const[draftPanel,setDraftPanel]=useState(null);
  const[dragging,setDragging]=useState(null); // {group,id}|null
  const[hoverId,setHoverId]=useState(null);
  const hoverRef=useRef(null);

  const previewOrder=(base,draggedId,overId)=>{
    if(!draggedId||!overId||draggedId===overId)return base;
    const without=base.filter(id=>id!==draggedId);
    const idx=without.indexOf(overId);
    if(idx<0)return base;
    const result=[...without];
    result.splice(idx,0,draggedId);
    return result;
  };

  const startReorder=()=>{
    setDraftStat(visibleCards.map(c=>c.id));
    setDraftPanel(visiblePanels.map(c=>c.id));
    setReordering(true);
  };
  const cancelReorder=()=>{setReordering(false);setDraftStat(null);setDraftPanel(null);setDragging(null);setHoverId(null);};
  const confirmReorder=async()=>{
    try{
      await persistPrefs([...draftStat,...draftPanel]);
      addToast("Orden del Dashboard guardado","success");
    }catch(e){addToast(e.message,"error");}
    finally{setReordering(false);setDraftStat(null);setDraftPanel(null);setDragging(null);setHoverId(null);}
  };

  useEffect(()=>{
    if(!dragging)return;
    const onMove=e=>{
      const el=document.elementFromPoint(e.clientX,e.clientY);
      const cardEl=el?.closest?.("[data-dash-card]");
      if(!cardEl)return;
      const id=cardEl.getAttribute("data-dash-card");
      const grp=cardEl.getAttribute("data-dash-group");
      if(grp!==dragging.group||id===dragging.id)return;
      if(hoverRef.current!==id){hoverRef.current=id;setHoverId(id);}
    };
    const onUp=()=>{
      const hid=hoverRef.current;
      if(hid){
        const setOrder=dragging.group==="stat"?setDraftStat:setDraftPanel;
        setOrder(base=>previewOrder(base,dragging.id,hid));
      }
      hoverRef.current=null;
      setDragging(null);setHoverId(null);
    };
    window.addEventListener("mousemove",onMove);
    window.addEventListener("mouseup",onUp);
    return()=>{window.removeEventListener("mousemove",onMove);window.removeEventListener("mouseup",onUp);};
  },[dragging]);

  const dragProps=(group,id)=>reordering?{
    "data-dash-card":id,
    "data-dash-group":group,
    onMouseDown:e=>{
      if(e.button!==0)return;
      e.preventDefault();
      hoverRef.current=null;
      setDragging({group,id});setHoverId(null);
    },
    style:{
      outline:dragging?.id===id?"2px solid var(--accent)":"2px dashed var(--accent)",
      outlineOffset:2,borderRadius:12,cursor:dragging?.id===id?"grabbing":"grab",
      opacity:dragging?.id===id?0.45:1,transition:"opacity 0.12s",userSelect:"none",
    },
  }:{};

  const statDefsById=Object.fromEntries(allowedStats.map(c=>[c.id,c]));
  const panelDefsById=Object.fromEntries(allowedPanels.map(c=>[c.id,c]));
  const previewStat=dragging?.group==="stat"?previewOrder(draftStat,dragging.id,hoverId):draftStat;
  const previewPanel=dragging?.group==="panel"?previewOrder(draftPanel,dragging.id,hoverId):draftPanel;
  const statsToRender=reordering?(previewStat||[]).map(id=>statDefsById[id]).filter(Boolean):visibleCards;
  const panelsToRender=reordering?(previewPanel||[]).map(id=>panelDefsById[id]).filter(Boolean):visiblePanels;

  return<div style={{display:"flex",flexDirection:"column",gap:20}}>
    <PH title={`Buen día, ${currentUser.name.split(" ")[0]}`} sub={new Date().toLocaleDateString("es-NI",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
      action={reordering
        ?<div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <Btn variant="secondary" onClick={cancelReorder}><X size={13}/>Cancelar</Btn>
          <Btn onClick={confirmReorder}><Check size={14}/>Confirmar orden</Btn>
        </div>
        :<div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <Btn variant="secondary" onClick={startReorder}><GripVertical size={13}/>Reordenar</Btn>
          <Btn variant="secondary" onClick={()=>setShowManage(true)}><Settings size={13}/>Gestionar Dashboard</Btn>
        </div>}/>
    <PeriodFilterBar filter={filter} periodos={periodos} includePeriodos={false} label={label}/>
    {reordering&&<div style={{background:"var(--accent-dim)",border:"1px solid var(--accent-border)",borderRadius:8,padding:"8px 14px",fontSize:12,color:"var(--accent)",display:"flex",alignItems:"center",gap:8}}>
      <GripVertical size={13}/>Arrastra las tarjetas para reordenarlas. Los cambios se guardan al confirmar.
    </div>}
    {statsToRender.length>0&&<div className="grid-4">
      {statsToRender.map(c=><div key={c.id} {...dragProps("stat",c.id)}>{c.render(cardCtx)}</div>)}
    </div>}
    {panelsToRender.length>0&&<div className="grid-2">
      {panelsToRender.map(c=><div key={c.id} {...dragProps("panel",c.id)}>{c.render(cardCtx)}</div>)}
    </div>}
    {showManage&&<ManageDashboardCardsModal
      groups={[{key:"stat",title:"Tarjetas",items:allowedStats},{key:"panel",title:"Gráficos y listas",items:allowedPanels}]}
      current={cardPrefs} onClose={()=>setShowManage(false)} onSave={saveCardPrefs}/>}
  </div>;
}

/* ── CLIENTS ────────────────────────────────────────────────────────── */
const CLIENT_LABELS=["Normal","VIP","Frecuente","Crédito","Moroso","Revisar Cuenta"];
function ClientsModule({data,refresh,addToast,currentUser}){
  const[q,setQ]=useState("");const[fSt,setFSt]=useState("");const[fLbl,setFLbl]=useState("");const[modal,setModal]=useState(null);const[form,setForm]=useState({});const[errs,setErrs]=useState({});const[saving,setSaving]=useState(false);
  const[sort,setSort]=useState({key:null,dir:"asc"});
  const onSort=key=>setSort(p=>p.key===key?{key,dir:p.dir==="asc"?"desc":"asc"}:{key,dir:"asc"});
  const list=data.clients.filter(c=>`${c.name} ${c.lastName} ${c.whatsapp} ${c.code}`.toLowerCase().includes(q.toLowerCase())&&(!fSt||c.status===fSt)&&(!fLbl||c.label===fLbl));
  const sortedList=useMemo(()=>{
    if(!sort.key)return list;
    const dir=sort.dir==="asc"?1:-1;
    return[...list].sort((a,b)=>{
      const av=a[sort.key],bv=b[sort.key];
      if(typeof av==="string")return av.localeCompare(bv,'es')*dir;
      return((+av||0)-(+bv||0))*dir;
    });
  },[list,sort]);
  const pg=usePagination(sortedList);
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));
  const validate=()=>{const e={};if(!form.name?.trim())e.name="Requerido";if(!form.lastName?.trim())e.lastName="Requerido";if(!form.whatsapp?.trim())e.whatsapp="Requerido";if(!(parseFloat(form.rateAir)>0))e.rateAir="Inválido";if(!(parseFloat(form.rateSea)>0))e.rateSea="Inválido";setErrs(e);return!Object.keys(e).length;};
  const save=async()=>{
    if(!validate())return;setSaving(true);
    try{
      if(modal==="new"){const r=await API.clientes.create({...form,rateAir:+form.rateAir,rateSea:+form.rateSea});addToast(`Cliente ${r.code} creado`,"success");}
      else{await API.clientes.update(form.id,{...form,rateAir:+form.rateAir,rateSea:+form.rateSea});addToast("Cliente actualizado","success");}
      await refresh();setModal(null);
    }catch(e){addToast(e.message,"error");}
    finally{setSaving(false);}
  };
  const toggle=async(c)=>{try{await API.clientes.toggleStatus(c.id);await refresh();addToast(`Cliente ${c.status==="Activo"?"desactivado":"activado"}`,"info");}catch(e){addToast(e.message,"error");}};
  const cols=[
    {k:"code",l:"Código",sortKey:"code",render:r=><code className="c-accent fw-700 fs-12">{r.code}</code>},
    {k:"name",l:"Cliente",sortKey:"name",render:r=><div><div style={{fontWeight:600}}>{r.name} {r.lastName}</div><div style={{fontSize:11,color:'var(--muted)'}}>📱 {r.whatsapp}</div></div>},
    {k:"label",l:"Etiqueta",sortKey:"label",render:r=><Badge v={r.label}/>},
    {k:"rateAir",l:"Tarifa Aérea",sortKey:"rateAir",render:r=><span className="c-accent fw-700">{fmtUsd(r.rateAir)}/lb</span>},
    {k:"rateSea",l:"Tarifa Marítima",sortKey:"rateSea",render:r=><span className="c-info fw-700">{fmtUsd(r.rateSea)}/lb</span>},
    {k:"status",l:"Estado",sortKey:"status",render:r=><Badge v={r.status}/>},
    {k:"a",l:"",render:r=><div style={{display:"flex",gap:4}}>
      {can(currentUser,"clientes:editar")&&<Btn variant="ghost" onClick={()=>{setForm({...r,rateAir:String(r.rateAir),rateSea:String(r.rateSea)});setErrs({});setModal("edit");}}><Edit2 size={13}/></Btn>}
      {can(currentUser,"clientes:estado")&&<Btn variant="ghost" onClick={()=>toggle(r)} style={{color:r.status==="Activo"?'var(--warn)':'var(--success)'}}>{r.status==="Activo"?<XCircle size={13}/>:<CheckCircle size={13}/>}</Btn>}
    </div>},
  ];
  return<div>
    <PH title="Clientes" sub={`${data.clients.length} clientes registrados`} action={can(currentUser,"clientes:crear")&&<Btn onClick={()=>{setForm({name:"",lastName:"",whatsapp:"",email:"",id_num:"",address:"",rateAir:String(data.settings.rateAir),rateSea:String(data.settings.rateSea),label:"Normal",status:"Activo",obs:""});setErrs({});setModal("new");}}><Plus size={14}/>Nuevo Cliente</Btn>}/>
    <div className="card">
      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
        <SearchBar value={q} onChange={v=>{setQ(v);pg.goPage(1);}} placeholder="Nombre, teléfono, código..."/>
        <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
          <FilterChips opts={["Activo","Inactivo"]} val={fSt} set={v=>{setFSt(v);pg.goPage(1);}}/>
          <FilterChips opts={CLIENT_LABELS} val={fLbl} set={v=>{setFLbl(v);pg.goPage(1);}}/>
        </div>
      </div>
      <Table cols={cols} rows={pg.paged} sort={sort} onSort={onSort} empty="No hay clientes registrados"/>
      <Pagination {...pg}/>
    </div>
    {modal&&<Modal title={modal==="new"?"Nuevo Cliente":"Editar Cliente"} onClose={()=>setModal(null)} width={600}>
      <div className="grid-2">
        <FInput label="Nombre" value={form.name||""} onChange={v=>f("name",v)} required error={errs.name}/>
        <FInput label="Apellidos" value={form.lastName||""} onChange={v=>f("lastName",v)} required error={errs.lastName}/>
        <FInput label="WhatsApp" value={form.whatsapp||""} onChange={v=>f("whatsapp",v)} placeholder="88881234" required error={errs.whatsapp}/>
        <FInput label="Correo electrónico" value={form.email||""} onChange={v=>f("email",v)} type="email"/>
        <FInput label="No. Identificación" value={form.id_num||""} onChange={v=>f("id_num",v)}/>
        <FSelect label="Etiqueta" value={form.label||"Normal"} onChange={v=>f("label",v)} options={CLIENT_LABELS}/>
        <FInput label="Tarifa aérea ($/lb)" value={form.rateAir||""} onChange={v=>f("rateAir",v)} type="number" required error={errs.rateAir}/>
        <FInput label="Tarifa marítima ($/lb)" value={form.rateSea||""} onChange={v=>f("rateSea",v)} type="number" required error={errs.rateSea}/>
        <div className="col-full"><FInput label="Dirección" value={form.address||""} onChange={v=>f("address",v)}/></div>
        <div className="col-full"><FInput label="Observaciones" value={form.obs||""} onChange={v=>f("obs",v)}/></div>
        {modal==="edit"&&<FSelect label="Estado" value={form.status||"Activo"} onChange={v=>f("status",v)} options={["Activo","Inactivo"]}/>}
      </div>
      <div className="row row--end mt-20 gap-10"><Btn variant="secondary" onClick={()=>setModal(null)}>Cancelar</Btn><Btn onClick={save} disabled={saving}>{saving?<><RefreshCw size={13} className="spin"/>Guardando…</>:<><Check size={14}/>Guardar</>}</Btn></div>
    </Modal>}
  </div>;
}

/* ── PACKAGES ───────────────────────────────────────────────────────── */
const PKG_EVENTO_UI={
  Registro:{icon:Package,color:'var(--info)',label:"Registro del paquete"},
  AgregadoOP:{icon:ClipboardList,color:'var(--accent)',label:"Agregado a Orden de Pago"},
  RemovidoOP:{icon:XCircle,color:'var(--warn)',label:"Removido de Orden de Pago"},
  CanceladoOP:{icon:XCircle,color:'var(--danger)',label:"Orden de Pago cancelada"},
  Facturacion:{icon:FileText,color:'var(--success)',label:"Facturado"},
  Entrega:{icon:Truck,color:'var(--success)',label:"Entrega física"},
  Anulacion:{icon:AlertTriangle,color:'var(--danger)',label:"Anulación"},
};

function PackageTracking({pkgId,onBack,addToast,data}){
  const[loading,setLoading]=useState(true);
  const[hist,setHist]=useState(null);
  useEffect(()=>{
    let alive=true;
    setLoading(true);
    API.paquetes.getHistorial(pkgId).then(r=>{if(alive){setHist(r);setLoading(false);}})
      .catch(e=>{if(alive){addToast("Error cargando historial: "+e.message,"error");setLoading(false);}});
    return()=>{alive=false;};
  },[pkgId,addToast]);
  const cliente=hist?data.clients.find(c=>c.id===hist.paquete.clientId):null;

  return<div>
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
      <Btn variant="secondary" onClick={onBack}><ArrowLeft size={14}/>Volver</Btn>
      <div>
        <div style={{fontSize:16,fontWeight:700}}>Tracking del paquete {hist?.paquete?.code||""}</div>
        <div className="fs-12 c-muted">Historial cronológico de movimientos</div>
      </div>
    </div>
    {loading?<Spinner/>:!hist?<div className="card"><p className="c-muted">No se pudo cargar el historial</p></div>:<>
      <div className="card" style={{marginBottom:14}}>
        <div style={{display:"flex",flexWrap:"wrap",gap:18,alignItems:"center"}}>
          <div><div className="fs-11 c-muted">Código</div><code className="c-accent fw-700">{hist.paquete.code}</code></div>
          <div><div className="fs-11 c-muted">Cliente</div>{cliente?<div><span className="fs-13 fw-600">{cliente.name} {cliente.lastName}</span> <span className="fs-11 c-muted">({cliente.code})</span></div>:<span className="fs-13 c-muted">—</span>}</div>
          <div><div className="fs-11 c-muted">Tracking</div><span className="fs-13">{hist.paquete.tracking||"—"}</span></div>
          <div><div className="fs-11 c-muted">Estado actual</div><Badge v={hist.paquete.status}/></div>
          <div><div className="fs-11 c-muted">Peso</div><span className="fs-13 fw-600">{fmtLbs(hist.paquete.weight)}</span></div>
        </div>
      </div>
      <div className="card">
        {hist.eventos.length<=1&&<div style={{background:'var(--card2)',border:'1px solid var(--border)',borderRadius:8,padding:"9px 12px",marginBottom:14,fontSize:12,color:'var(--muted)',display:"flex",gap:8,alignItems:"center"}}>
          <AlertCircle size={13}/>Este paquete solo tiene el evento de registro histórico — los movimientos posteriores no se capturaron antes de esta funcionalidad.
        </div>}
        <div style={{display:"flex",flexDirection:"column"}}>
          {hist.eventos.map((ev,i)=>{
            const ui=PKG_EVENTO_UI[ev.evento]||{icon:MapPin,color:'var(--muted)',label:ev.evento};
            const Icon=ui.icon;
            const last=i===hist.eventos.length-1;
            return<div key={i} style={{display:"flex",gap:12}}>
              <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
                <div style={{width:30,height:30,borderRadius:"50%",background:ui.color+"22",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Icon size={15} color={ui.color}/></div>
                {!last&&<div style={{width:2,flex:1,background:'var(--border)',minHeight:24}}/>}
              </div>
              <div style={{paddingBottom:last?0:18,flex:1}}>
                <div style={{fontSize:13,fontWeight:700}}>{ui.label}</div>
                {ev.descripcion&&<div className="fs-12 c-sub" style={{marginTop:2}}>{ev.descripcion}</div>}
                <div className="fs-11 c-muted" style={{marginTop:4,display:"flex",gap:10,flexWrap:"wrap"}}>
                  <span>{fmtDate(ev.fecha)}</span>
                  <span>· {ev.usuarioNombre||"Sistema"}</span>
                  {ev.ipOrigen&&<span>· IP {ev.ipOrigen}</span>}
                </div>
              </div>
            </div>;
          })}
        </div>
      </div>
    </>}
  </div>;
}

function PackagesModule({data,refresh,addToast,openBilling,currentUser,initialSelectedId,onConsumeInitialSelectedId}){
  const[q,setQ]=useState("");const[fs,setFs]=useState("");const[ft,setFt]=useState("");const[modal,setModal]=useState(null);const[form,setForm]=useState({});const[errs,setErrs]=useState({});const[ww,setWw]=useState(false);const[saving,setSaving]=useState(false);
  const[view,setView]=useState("list");const[trackId,setTrackId]=useState(null);
  useEffect(()=>{
    if(initialSelectedId){setTrackId(initialSelectedId);setView("tracking");onConsumeInitialSelectedId?.();}
  },[initialSelectedId,onConsumeInitialSelectedId]);
  const list=data.packages.filter(p=>{const cli=data.clients.find(c=>c.id===p.clientId);return`${p.code} ${p.tracking||""} ${cli?cli.name+" "+cli.lastName:""} ${p.desc}`.toLowerCase().includes(q.toLowerCase())&&(!fs||p.status===fs)&&(!ft||p.type===ft);});
  const pg=usePagination(list);
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));
  const validate=()=>{const e={};if(!form.clientId)e.clientId="Requerido";if(!(parseFloat(form.weight)>0))e.weight="Debe ser > 0";if(!form.rxDate)e.rxDate="Requerido";setErrs(e);return!Object.keys(e).length;};
  const doSave=async()=>{
    if(!validate())return;setSaving(true);
    try{
      if(modal==="new"){const r=await API.paquetes.create({...form,clientId:parseInt(form.clientId),weight:parseFloat(form.weight)});addToast(`Paquete ${r.code} registrado`,"success");}
      else{await API.paquetes.update(form.id,{...form,weight:parseFloat(form.weight)});addToast("Paquete actualizado","success");}
      await refresh();setModal(null);setWw(false);
    }catch(e){addToast(e.message,"error");}
    finally{setSaving(false);}
  };
  const cols=[
    {k:"code",l:"Código",render:r=><code className="c-accent fw-700 fs-12">{r.code}</code>},
    {k:"c",l:"Cliente",render:r=>{const c=data.clients.find(x=>x.id===r.clientId);return c?<div><div style={{fontWeight:600,fontSize:13}}>{c.name} {c.lastName}</div><Badge v={c.label}/></div>:"-";}},
    {k:"tracking",l:"Tracking",render:r=><code className="fs-11 c-sub">{r.tracking||"—"}</code>},
    {k:"type",l:"Tipo",render:r=><span className="fs-12 fw-700" style={{color:r.type==="Aéreo"?"var(--info)":"var(--warn)"}}>{r.type==="Aéreo"?"✈ Aéreo":"🚢 Marítimo"}</span>},
    {k:"weight",l:"Peso",render:r=><b style={{fontSize:13}}>{fmtLbs(r.weight)}</b>},
    {k:"status",l:"Estado",render:r=><Badge v={r.status}/>},
    {k:"rxDate",l:"Recepción",render:r=><span className="fs-12 c-muted">{fmtDate(r.rxDate)}</span>},
    {k:"a",l:"",render:r=><div style={{display:"flex",gap:4}}>
      {r.status==="Registrado"&&can(currentUser,"paquetes:editar")&&<Btn variant="ghost" onClick={()=>{setForm({...r,weight:String(r.weight),clientId:String(r.clientId)});setErrs({});setWw(false);setModal("edit");}}><Edit2 size={13}/></Btn>}
      {r.status==="Registrado"&&<Btn variant="ghost" style={{color:'var(--accent)'}} onClick={()=>openBilling(r.clientId)}><FileText size={13}/></Btn>}
      <Btn variant="ghost" title="Tracking del paquete" onClick={()=>{setTrackId(r.id);setView("tracking");}}><Clock size={13}/></Btn>
    </div>},
  ];
  if(view==="tracking")return<PackageTracking pkgId={trackId} addToast={addToast} onBack={()=>setView("list")} data={data}/>;
  if(view==="bulk")return<BulkPackagesModule data={data} refresh={refresh} addToast={addToast} currentUser={currentUser} onBack={()=>setView("list")}/>;
  return<div>
    <PH title="Paquetería" sub={`${data.packages.length} paquetes · ${list.length} mostrando`} action={<div style={{display:"flex",gap:8}}>
      {can(currentUser,"paquetes:crear")&&<Btn variant="secondary" onClick={()=>setView("bulk")}><Layers size={14}/>Registro Masivo</Btn>}
      {can(currentUser,"paquetes:crear")&&<Btn onClick={()=>{setForm({clientId:"",tracking:"",type:"Aéreo",weight:"",desc:"",rxDate:today(),obs:""});setErrs({});setWw(false);setModal("new");}}><Plus size={14}/>Nuevo Paquete</Btn>}
    </div>}/>
    <div className="card">
      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
        <SearchBar value={q} onChange={v=>{setQ(v);pg.goPage(1);}} placeholder="Código, tracking, cliente..."/>
        <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
          <FilterChips opts={["Registrado","Facturado","Entregado"]} val={fs} set={v=>{setFs(v);pg.goPage(1);}}/>
          <FilterChips opts={["Aéreo","Marítimo"]} val={ft} set={v=>{setFt(v);pg.goPage(1);}}/>
        </div>
      </div>
      <Table cols={cols} rows={pg.paged} empty="No hay paquetes"/>
      <Pagination {...pg}/>
    </div>
    {modal&&<Modal title={modal==="new"?"Registrar Paquete":"Editar Paquete"} onClose={()=>setModal(null)}>
      <div className="flex-col gap-14">
        <FSelect label="Cliente" value={form.clientId||""} onChange={v=>f("clientId",v)} options={sortedActiveClientOptions(data.clients)} required error={errs.clientId}/>
        <div className="grid-2">
          <FInput label="Tracking (opcional)" value={form.tracking||""} onChange={v=>f("tracking",v)} placeholder="Número de rastreo"/>
          <FSelect label="Tipo de envío" value={form.type||"Aéreo"} onChange={v=>f("type",v)} options={["Aéreo","Marítimo"]}/>
          <div><FInput label="Peso (lb)" value={form.weight||""} onChange={v=>{f("weight",v);setWw(parseFloat(v||0)>100);}} type="number" required error={errs.weight} placeholder="0.00"/>
            {ww&&<div style={{background:'var(--warn-dim)',border:`1px solid ${'var(--warn)'}44`,borderRadius:6,padding:"7px 10px",marginTop:6,fontSize:11,color:'var(--warn)',display:"flex",gap:5,alignItems:"center"}}><AlertTriangle size={12}/>Peso inusual: {form.weight} lb. ¿Confirma?</div>}
          </div>
          <FInput label="Fecha recepción Nicaragua" value={form.rxDate||""} onChange={v=>f("rxDate",v)} type="date" required error={errs.rxDate}/>
        </div>
        <FInput label="Descripción del contenido (opcional)" value={form.desc||""} onChange={v=>f("desc",v)} error={errs.desc} placeholder="Ropa, electrónica…"/>
        <FInput label="Observaciones" value={form.obs||""} onChange={v=>f("obs",v)} placeholder="Notas adicionales"/>
      </div>
      <div className="row row--end mt-18 gap-10"><Btn variant="secondary" onClick={()=>setModal(null)}>Cancelar</Btn><Btn onClick={doSave} disabled={saving}>{saving?<><RefreshCw size={13} className="spin"/>Guardando…</>:<><Check size={14}/>Guardar Paquete</>}</Btn></div>
    </Modal>}
  </div>;
}

/* ── BULK PACKAGES ──────────────────────────────────────────────────── */
const BULK_COMMON_KEY = "mb_bulk_common";
const BULK_ROWS_INIT = 8;

function BulkPackagesModule({ data, refresh, addToast, currentUser, onBack }) {
  const rowIdRef = useRef(0);
  const emptyRow = () => ({ id: `r${rowIdRef.current++}`, tracking: "", weight: "" });
  const cellRefs = useRef({});
  const pendingFocus = useRef(null);

  const [common, setCommon] = useState(() => {
    let saved = {};
    try { saved = JSON.parse(sessionStorage.getItem(BULK_COMMON_KEY) || "{}"); } catch {}
    return {
      clientId: saved.clientId || "",
      type: saved.type || "Aéreo",
      rxDate: today(),
      desc: saved.desc || "",
      obs: "",
    };
  });
  const [commonErrs, setCommonErrs] = useState({});
  const [rows, setRows] = useState(() => Array.from({ length: BULK_ROWS_INIT }, emptyRow));
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setCommon(p => ({ ...p, [k]: v }));

  // Recuerda cliente/tipo/descripción únicamente durante la sesión (no persiste en BD)
  useEffect(() => {
    sessionStorage.setItem(BULK_COMMON_KEY, JSON.stringify({
      clientId: common.clientId, type: common.type, desc: common.desc,
    }));
  }, [common.clientId, common.type, common.desc]);

  useEffect(() => {
    if (!pendingFocus.current) return;
    const { idx, field } = pendingFocus.current;
    pendingFocus.current = null;
    cellRefs.current[`${idx}-${field}`]?.focus();
  }, [rows.length]);

  const validation = useMemo(() => {
    const counts = {};
    rows.forEach(r => { const t = (r.tracking || "").trim().toLowerCase(); if (t) counts[t] = (counts[t] || 0) + 1; });
    const errors = {}; const active = [];
    rows.forEach(r => {
      const tracking = (r.tracking || "").trim();
      const weightRaw = (r.weight ?? "").toString().trim();
      if (!tracking && !weightRaw) return; // fila vacía: se ignora, no es error
      const e = {};
      if (!tracking) e.tracking = "Requerido";
      else if (counts[tracking.toLowerCase()] > 1) e.tracking = "Duplicado";
      const w = parseFloat(weightRaw);
      if (!weightRaw) e.weight = "Requerido";
      else if (isNaN(w) || w <= 0) e.weight = "Debe ser > 0";
      if (Object.keys(e).length) errors[r.id] = e;
      active.push({ ...r, tracking, weight: w });
    });
    const validRows = active.filter(r => !errors[r.id]);
    return { errors, active, validRows, hasErrors: Object.keys(errors).length > 0 };
  }, [rows]);

  const updateCell = (id, field, value) => setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));

  const addRow = () => setRows(prev => [...prev, emptyRow()]);

  const removeRow = id => setRows(prev =>
    prev.length <= 1
      ? prev.map(r => r.id === id ? { ...r, tracking: "", weight: "" } : r)
      : prev.filter(r => r.id !== id)
  );

  const clearTable = () => { setRows(Array.from({ length: BULK_ROWS_INIT }, emptyRow)); };

  const handleKeyDown = (e, idx, field) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (idx === rows.length - 1) {
      pendingFocus.current = { idx: idx + 1, field };
      addRow();
    } else {
      cellRefs.current[`${idx + 1}-${field}`]?.focus();
    }
  };

  // Pegado masivo desde Excel: filas separadas por salto de línea, columnas por tabulador
  const handlePaste = (e, rowId, field) => {
    const text = e.clipboardData.getData("text");
    if (!text) return;
    let lines = text.replace(/\r/g, "").split("\n").filter((l, i, arr) => !(i === arr.length - 1 && l === ""));
    if (!lines.length) return;
    if (lines.length === 1 && !lines[0].includes("\t")) return; // valor simple: dejar comportamiento nativo del input
    e.preventDefault();
    if (field === "tracking") {
      const firstCells = lines[0].split("\t");
      if (firstCells.length > 1 && isNaN(parseFloat(firstCells[1]))) lines = lines.slice(1); // descarta encabezado "Tracking / Peso"
    }
    if (!lines.length) return;
    setRows(prev => {
      const startIdx = prev.findIndex(r => r.id === rowId);
      if (startIdx === -1) return prev;
      const next = [...prev];
      lines.forEach((line, i) => {
        const cells = line.split("\t").map(c => c.trim());
        const rIdx = startIdx + i;
        while (rIdx >= next.length) next.push(emptyRow());
        const row = { ...next[rIdx] };
        if (field === "tracking") {
          if (cells[0] !== undefined) row.tracking = cells[0];
          if (cells[1] !== undefined) row.weight = cells[1];
        } else if (cells[0] !== undefined) {
          row.weight = cells[0];
        }
        next[rIdx] = row;
      });
      return next;
    });
  };

  const validateCommon = () => {
    const e = {};
    if (!common.clientId) e.clientId = "Requerido";
    if (!common.rxDate) e.rxDate = "Requerido";
    return e;
  };

  const doSave = async () => {
    const ce = validateCommon();
    setCommonErrs(ce);
    if (Object.keys(ce).length) { addToast("Complete los datos comunes requeridos", "error"); return; }
    if (validation.hasErrors) { addToast("Corrija los errores marcados en la tabla antes de guardar", "error"); return; }
    if (!validation.validRows.length) { addToast("Agregue al menos un paquete", "error"); return; }
    setSaving(true);
    try {
      const res = await API.paquetes.createMasivo({
        clientId: parseInt(common.clientId), type: common.type, rxDate: common.rxDate,
        desc: common.desc, obs: common.obs,
        items: validation.validRows.map(r => ({ tracking: r.tracking, weight: r.weight })),
      });
      addToast(`Se registraron correctamente ${res.count} paquetes.`, "success");
      await refresh();
      clearTable();
    } catch (e) { addToast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const cli = common.clientId ? data.clients.find(c => c.id === parseInt(common.clientId)) : null;
  const errCount = Object.keys(validation.errors).length;

  return <div>
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
      <Btn variant="secondary" onClick={onBack}><ArrowLeft size={14}/>Volver</Btn>
      <div>
        <div style={{fontSize:16,fontWeight:700}}>Registro Masivo de Paquetes</div>
        <div className="fs-12 c-muted">Registra varios paquetes del mismo cliente en una sola operación</div>
      </div>
    </div>

    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 12 }}>A. Datos comunes</h3>
      <div className="grid-2">
        <FSelect label="Cliente" value={common.clientId} onChange={v => f("clientId", v)}
          options={sortedActiveClientOptions(data.clients)}
          required error={commonErrs.clientId}/>
        <FSelect label="Tipo de envío" value={common.type} onChange={v => f("type", v)} options={["Aéreo", "Marítimo"]}/>
        <FInput label="Fecha de recepción" value={common.rxDate} onChange={v => f("rxDate", v)} type="date" required error={commonErrs.rxDate}/>
        <FInput label="Descripción del contenido (opcional)" value={common.desc} onChange={v => f("desc", v)} error={commonErrs.desc} placeholder="Ropa, electrónica…"/>
        <div className="col-full"><FInput label="Observaciones (opcional)" value={common.obs} onChange={v => f("obs", v)} placeholder="Notas adicionales"/></div>
      </div>
      {cli && <div style={{ background: "var(--accent-dim)", border: "1px solid var(--accent-border)", borderRadius: 8, padding: "10px 13px", marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
        <User size={16} color="var(--accent)"/>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{cli.name} {cli.lastName}</div>
          <div style={{ fontSize: 11, color: "var(--muted)" }}>✈ {fmtUsd(cli.rateAir)}/lb · 🚢 {fmtUsd(cli.rateSea)}/lb</div>
        </div>
        <Badge v={cli.label}/>
      </div>}
    </div>

    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 6 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>B. Lista de paquetes</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="secondary" onClick={addRow}><Plus size={13}/>Agregar fila</Btn>
          <Btn variant="secondary" onClick={clearTable}><Trash2 size={13}/>Limpiar tabla</Btn>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>
        <ClipboardPaste size={13}/>Pega directamente desde Excel (Tracking + Peso) — se generan las filas automáticamente
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead><tr><th style={{ width: 36 }}>#</th><th>Tracking</th><th style={{ width: 140 }}>Peso (lb)</th><th style={{ width: 40 }}/></tr></thead>
          <tbody>
            {rows.map((r, idx) => {
              const err = validation.errors[r.id] || {};
              return <tr key={r.id}>
                <td style={{ fontSize: 11, color: "var(--muted)" }}>{idx + 1}</td>
                <td>
                  <input
                    ref={el => (cellRefs.current[`${idx}-tracking`] = el)}
                    value={r.tracking}
                    onChange={e => updateCell(r.id, "tracking", e.target.value)}
                    onPaste={e => handlePaste(e, r.id, "tracking")}
                    onKeyDown={e => handleKeyDown(e, idx, "tracking")}
                    placeholder="Tracking"
                    className={`ctrl-input${err.tracking ? " has-error" : ""}`}
                    style={{ fontFamily: "monospace", fontSize: 12, padding: "6px 10px" }}/>
                  {err.tracking && <span className="ctrl-error">{err.tracking}</span>}
                </td>
                <td>
                  <input
                    ref={el => (cellRefs.current[`${idx}-weight`] = el)}
                    value={r.weight}
                    onChange={e => updateCell(r.id, "weight", e.target.value)}
                    onPaste={e => handlePaste(e, r.id, "weight")}
                    onKeyDown={e => handleKeyDown(e, idx, "weight")}
                    inputMode="decimal" placeholder="0.00"
                    className={`ctrl-input${err.weight ? " has-error" : ""}`}
                    style={{ fontSize: 12, padding: "6px 10px", textAlign: "right" }}/>
                  {err.weight && <span className="ctrl-error">{err.weight}</span>}
                </td>
                <td>
                  <button onClick={() => removeRow(r.id)} title="Eliminar fila"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--danger)", display: "flex" }}>
                    <X size={14}/>
                  </button>
                </td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>

      {errCount > 0
        ? <div className="alert alert--danger" style={{ marginTop: 14 }}><AlertTriangle size={13}/>{errCount} fila(s) con error — corrígelas antes de guardar</div>
        : validation.validRows.length > 0 && <div className="alert alert--success" style={{ marginTop: 14 }}>
            <CheckCircle size={13}/>Se registrarán: <b>{validation.validRows.length}</b> paquete(s) nuevo(s)
          </div>}

      <div className="row row--end mt-18 gap-10">
        {can(currentUser,"paquetes:crear")&&<Btn onClick={doSave} disabled={saving}>
          {saving ? <><RefreshCw size={13} className="spin"/>Guardando…</> : <><Check size={14}/>Guardar</>}
        </Btn>}
      </div>
    </div>
  </div>;
}

/* ── BILLING ────────────────────────────────────────────────────────── */
const PAY_METHODS=["Efectivo","Transferencia","POS","Tarjeta","Mixto","Otro"];
const needRef=m=>["Transferencia","POS","Tarjeta","Mixto","Otro"].includes(m);

function BillingModule({data,refresh,addToast,initClientId,currentUser}){
  const[view,setView]=useState("list");const[selInv,setSelInv]=useState(null);
  const[cancelM,setCancelM]=useState(null);const[cancelReason,setCancelReason]=useState("");const[adminPwd,setAdminPwd]=useState("");const[adminErr,setAdminErr]=useState("");
  const[q,setQ]=useState("");const[fSt,setFSt]=useState("");const[fPay,setFPay]=useState("");const[newInv,setNewInv]=useState(null);const[genPDF,setGenPDF]=useState(false);const[saving,setSaving]=useState(false);

  const openNew=useCallback((cid="")=>{setNewInv({clientId:cid?String(cid):"",sel:[],details:[],discType:"",discPct:"",discAmt2:"",exRate:String(data.settings.exchangeRate),payMethod:"Efectivo",reference:"",fecha:today(),weightMode:"factura"});setView("new");},[data.settings.exchangeRate]);

  const avail=cid=>data.packages.filter(p=>p.clientId===parseInt(cid)&&p.status==="Registrado");
  const togglePkg=pkg=>{const cli=data.clients.find(c=>c.id===parseInt(newInv.clientId));const rate=pkg.type==="Aéreo"?cli.rateAir:cli.rateSea;const has=newInv.sel.includes(pkg.id);const sel=has?newInv.sel.filter(x=>x!==pkg.id):[...newInv.sel,pkg.id];const det=has?newInv.details.filter(d=>d.pkgId!==pkg.id):[...newInv.details,{pkgId:pkg.id,code:pkg.code,tracking:pkg.tracking,type:pkg.type,weight:pkg.weight,rate,subtotal:pkg.weight*rate}];setNewInv(p=>({...p,sel,details:det}));};
  const updRate=(pid,rate)=>setNewInv(p=>({...p,details:p.details.map(d=>d.pkgId===pid?{...d,rate:+rate||0,subtotal:d.weight*(+rate||0)}:d)}));

  const totals=useMemo(()=>{if(!newInv?.details.length)return null;let dt=newInv.discType,dv=0;if(dt==="porcentaje")dv=parseFloat(newInv.discPct)||0;else if(dt==="monto")dv=parseFloat(newInv.discAmt2)||0;else if(dt==="ambos"){const bp=newInv.details.reduce((s,d)=>s+d.weight*d.rate,0)*(parseFloat(newInv.discPct)||0)/100;const ba=parseFloat(newInv.discAmt2)||0;dt=bp>=ba?"porcentaje":"monto";dv=bp>=ba?parseFloat(newInv.discPct)||0:ba;}return calcInv(newInv.details,data.settings.minLbs,dt,dv,parseFloat(newInv.exRate)||data.settings.exchangeRate,newInv.weightMode==="factura",newInv.weightMode==="paquete"?data.settings.minLbsPerPackage:null);},[newInv,data.settings]);


  const createInv=async()=>{
    if(!newInv.clientId){addToast("Seleccione un cliente","error");return;}
    if(!newInv.sel.length){addToast("Agregue al menos un paquete","error");return;}
    if(needRef(newInv.payMethod)&&!newInv.reference.trim()){addToast("Referencia de pago requerida","error");return;}
    if(!newInv.fecha||isNaN(Date.parse(newInv.fecha))){addToast("Fecha del recibo inválida","error");return;}
    let dt=newInv.discType,dv=0;if(dt==="porcentaje")dv=parseFloat(newInv.discPct)||0;else if(dt==="monto")dv=parseFloat(newInv.discAmt2)||0;else if(dt==="ambos"){const bp=totals?.sub*(parseFloat(newInv.discPct)||0)/100;const ba=parseFloat(newInv.discAmt2)||0;dt=bp>=ba?"porcentaje":"monto";dv=bp>=ba?parseFloat(newInv.discPct)||0:ba;}
    setSaving(true);
    try{
      await API.facturas.create({clientId:parseInt(newInv.clientId),details:newInv.details,totalWeight:totals.totalW,billedWeight:totals.billedW,subtotalUsd:totals.sub,discType:dt||null,discVal:dv,discAmt:totals.discAmt,totalUsd:totals.totalUsd,exRate:parseFloat(newInv.exRate),totalNio:totals.totalNio,payMethod:newInv.payMethod,reference:newInv.reference,fecha:newInv.fecha,minLbsPerPackageUsed:newInv.weightMode==="paquete"?data.settings.minLbsPerPackage:null});
      addToast("Recibo emitido con éxito","success");
      await refresh();setView("list");setNewInv(null);
    }catch(e){addToast(e.message,"error");}
    finally{setSaving(false);}
  };

  const doCancel=async()=>{
    if(!cancelReason.trim()){setAdminErr("Motivo obligatorio");return;}
    if(!adminPwd){setAdminErr("Contraseña requerida");return;}
    setSaving(true);
    try{
      await API.facturas.anular(cancelM.id,cancelReason,adminPwd);
      addToast(`Recibo ${cancelM.number} anulado`,"info");
      await refresh();setCancelM(null);setCancelReason("");setAdminPwd("");setAdminErr("");
    }catch(e){setAdminErr(e.message);}
    finally{setSaving(false);}
  };

  const filtered=data.invoices.filter(i=>{const cli=data.clients.find(c=>c.id===i.clientId);return`${i.number} ${cli?cli.name+" "+cli.lastName:""}`.toLowerCase().includes(q.toLowerCase())&&(!fSt||i.status===fSt)&&(!fPay||i.payMethod===fPay);});
  const pgB=usePagination(filtered);
  const handlePrint=(inv,cli)=>printInvoice(inv,cli,data.settings);
  const handleWA=async(inv,cli)=>generatePDFAndWhatsApp(inv,cli,data.settings,addToast,setGenPDF);

  if(view==="new"&&newInv){
    const cli=newInv.clientId?data.clients.find(c=>c.id===parseInt(newInv.clientId)):null;
    const pkgs=newInv.clientId?avail(newInv.clientId):[];
    return<div>
      <div style={{display:'flex',gap:12,alignItems:'center',marginBottom:22}}><Btn variant="ghost" onClick={()=>{setView("list");setNewInv(null);}}><ChevronRight size={16} style={{transform:"rotate(180deg)"}}/> Volver</Btn><h1 style={{fontSize:20,fontWeight:700,color:'var(--text)'}}>Nuevo Recibo</h1></div>
      <div className="grid-3-2">
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div className="card"><h3 style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:12}}>1. Cliente</h3>
            <FSelect value={newInv.clientId} onChange={v=>setNewInv(p=>({...p,clientId:v,sel:[],details:[]}))} options={sortedActiveClientOptions(data.clients)}/>
            {cli&&<div style={{background:'var(--accent-dim)',border:`1px solid ${'var(--accent-border)'}`,borderRadius:8,padding:"10px 13px",marginTop:10,display:"flex",gap:10,alignItems:"center"}}><User size={16} color={'var(--accent)'}/><div style={{flex:1}}><div style={{fontSize:13,fontWeight:700,color:'var(--text)'}}>{cli.name} {cli.lastName}</div><div style={{fontSize:11,color:'var(--muted)'}}>✈ {fmtUsd(cli.rateAir)}/lb · 🚢 {fmtUsd(cli.rateSea)}/lb</div></div><Badge v={cli.label}/></div>}
            <div style={{marginTop:14}}>
              <p className="ctrl-label" style={{marginBottom:8}}>Tipo de cálculo de peso</p>
              <div style={{display:"flex",gap:8}}>
                {[{v:"factura",l:"Peso mínimo por factura"},{v:"real",l:"Peso real"},{v:"paquete",l:"Peso mínimo por paquete"}].map(o=><button key={o.v} onClick={()=>setNewInv(p=>({...p,weightMode:o.v}))} style={{flex:1,padding:"9px 6px",borderRadius:8,border:`2px solid ${newInv.weightMode===o.v?'var(--accent)':'var(--border)'}`,background:newInv.weightMode===o.v?'var(--accent-dim)':"transparent",color:newInv.weightMode===o.v?'var(--accent)':'var(--muted)',fontWeight:700,fontSize:12,cursor:"pointer",transition:"all 0.15s"}}>{o.l}</button>)}
              </div>
            </div>
          </div>
          {newInv.clientId&&<div className="card"><h3 style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:12}}>2. Paquetes ({pkgs.length} disponibles)</h3>
            {!pkgs.length?<p style={{color:'var(--muted)',fontSize:13}}>Sin paquetes "Registrado" para este cliente</p>:pkgs.map(pkg=>{const sel=newInv.sel.includes(pkg.id);const det=newInv.details.find(d=>d.pkgId===pkg.id);
              return<div key={pkg.id} style={{border:`1px solid ${sel?'var(--accent)':'var(--border)'}`,borderRadius:8,padding:"11px 13px",marginBottom:8,background:sel?'var(--accent-dim)':"transparent",cursor:"pointer",transition:"all 0.12s"}} onClick={()=>togglePkg(pkg)}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    <div style={{width:17,height:17,borderRadius:4,border:`2px solid ${sel?'var(--accent)':'var(--border)'}`,background:sel?'var(--accent)':"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{sel&&<Check size={11} color="#fff"/>}</div>
                    <div><code style={{fontSize:12,fontWeight:700,color:'var(--text)'}}>{pkg.code}</code><div style={{fontSize:11,color:'var(--muted)'}}>{pkg.tracking||"Sin tracking"} · {pkg.type}</div></div>
                  </div>
                  <div style={{textAlign:"right"}}><b style={{fontSize:13}}>{fmtLbs(pkg.weight)}</b><div style={{fontSize:11,color:'var(--muted)'}}>{pkg.desc}</div></div>
                </div>
                {sel&&<div style={{marginTop:9,paddingTop:9,borderTop:`1px solid ${'var(--border)'}`}} onClick={e=>e.stopPropagation()}>
                  <FInput label="Tarifa ($/lb)" value={String(det?.rate||"")} onChange={v=>updRate(pkg.id,v)} type="number" style={{width:130}}/>
                </div>}
              </div>;})}
          </div>}
          <div className="card"><h3 style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:12}}>3. Descuento (opcional)</h3>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <FSelect label="Tipo" value={newInv.discType} onChange={v=>setNewInv(p=>({...p,discType:v}))} options={[{v:"porcentaje",l:"Porcentaje (%)"},{v:"monto",l:"Monto fijo ($)"},{v:"ambos",l:"Comparar ambos"}]}/>
              {(newInv.discType==="porcentaje"||newInv.discType==="ambos")&&<FInput label="Porcentaje (%)" value={newInv.discPct} onChange={v=>setNewInv(p=>({...p,discPct:v}))} type="number" placeholder="5"/>}
              {(newInv.discType==="monto"||newInv.discType==="ambos")&&<FInput label="Monto ($)" value={newInv.discAmt2} onChange={v=>setNewInv(p=>({...p,discAmt2:v}))} type="number" placeholder="5.00"/>}
            </div>
          </div>
          <div className="card"><h3 style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:12}}>4. Forma de pago</h3>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <FInput label="Fecha del recibo" value={newInv.fecha} onChange={v=>setNewInv(p=>({...p,fecha:v}))} type="date" required/>
              <FInput label="Tipo de cambio (C$/$)" value={newInv.exRate} onChange={v=>setNewInv(p=>({...p,exRate:v}))} type="number"/>
              <FSelect label="Método de pago" value={newInv.payMethod} onChange={v=>setNewInv(p=>({...p,payMethod:v,reference:""}))} options={PAY_METHODS}/>
              {needRef(newInv.payMethod)&&<div style={{gridColumn:"1/-1"}}><FInput label="Referencia de pago" value={newInv.reference} onChange={v=>setNewInv(p=>({...p,reference:v}))} placeholder="No. transferencia / voucher…" required/></div>}
            </div>
          </div>
        </div>
        <div><div className="sticky" style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:'20px'}}>
          <div style={{marginBottom:14,paddingBottom:12,borderBottom:`1px solid ${'var(--border)'}`,display:"flex",alignItems:"center",gap:10}}><img src={data?.settings?.logo||LOGO} alt="" style={{height:30}}/><span style={{fontSize:12,color:'var(--muted)',fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>Vista previa</span></div>
          {!totals?<p style={{color:'var(--muted)',fontSize:13,padding:"20px 0",textAlign:"center"}}>Seleccione paquetes</p>:(()=>{const{lines:pvLines,adjustment:pvAdj}=billedLineItems({details:newInv.details,billedWeight:totals.billedW,subtotalUsd:totals.sub,minLbsPerPackageUsed:newInv.weightMode==="paquete"?data.settings.minLbsPerPackage:null});return<>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>{pvLines.map(d=><div key={d.pkgId} style={{display:"flex",justifyContent:"space-between",fontSize:13}}><div><code style={{color:'var(--accent)',fontSize:12,fontWeight:700}}>{d.code}</code><div style={{color:'var(--muted)',fontSize:11}}>{fmtLbs(d.billedWeight)} × {fmtUsd(d.rate)}/lb</div></div><b style={{color:'var(--text)'}}>{fmtUsd(d.billedSubtotal)}</b></div>)}
            {pvAdj&&<div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:'var(--warn)',fontStyle:"italic"}}><span>{pvAdj.label}</span><b>+{fmtUsd(pvAdj.amount)}</b></div>}</div>
            <div style={{borderTop:`1px solid ${'var(--border)'}`,marginTop:10,paddingTop:10,display:"flex",flexDirection:"column",gap:6}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:'var(--muted)'}}><span>Subtotal</span><span>{fmtUsd(totals.sub)}</span></div>
              {totals.discAmt>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:'var(--success)'}}><span>Descuento</span><span>-{fmtUsd(totals.discAmt)}</span></div>}
              <div style={{display:"flex",justifyContent:"space-between",fontSize:19,fontWeight:700,color:'var(--accent)',borderTop:`1px solid ${'var(--border)'}`,paddingTop:8,marginTop:4}}><span>Total USD</span><span>{fmtUsd(totals.totalUsd)}</span></div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:'var(--muted)'}}><span>Total NIO</span><span>{fmtNio(totals.totalNio)}</span></div>
            </div>
            <Btn onClick={createInv} size="lg" style={{width:"100%",justifyContent:"center",marginTop:16,background:'var(--success)'}} disabled={saving}>{saving?<><RefreshCw size={15} className="spin"/>Emitiendo…</>:<><FileText size={15}/>Emitir Recibo</>}</Btn>
          </>;})()}
        </div></div>
      </div>
    </div>;
  }

  const cols=[
    {k:"number",l:"Recibo",render:r=><code style={{color:'var(--accent)',fontWeight:700,fontSize:13}}>{r.number}</code>},
    {k:"c",l:"Cliente",render:r=>{const c=data.clients.find(x=>x.id===r.clientId);return c?`${c.name} ${c.lastName}`:"-";}},
    {k:"p",l:"Paq.",render:r=><span style={{color:'var(--muted)'}}>{r.pkgIds.length}</span>},
    {k:"totalUsd",l:"Total USD",render:r=><b style={{color:'var(--accent)'}}>{fmtUsd(r.totalUsd)}</b>},
    {k:"totalNio",l:"Total NIO",render:r=><span style={{fontSize:12,color:'var(--muted)'}}>{fmtNio(r.totalNio)}</span>},
    {k:"pay",l:"Pago",render:r=><span style={{fontSize:12}}>{r.payMethod}</span>},
    {k:"status",l:"Estado",render:r=><Badge v={r.status}/>},
    {k:"d",l:"Fecha",render:r=><span style={{fontSize:12,color:'var(--muted)'}}>{fmtDate(r.fecha)}</span>},
    {k:"a",l:"",render:r=>{const cli=data.clients.find(x=>x.id===r.clientId);return<div style={{display:"flex",gap:4}}>
      <Btn variant="ghost" onClick={()=>setSelInv(r)}><Eye size={13}/></Btn>
      <Btn variant="ghost" onClick={()=>handlePrint(r,cli)} title="Imprimir"><Printer size={13}/></Btn>
      <Btn variant="ghost" onClick={()=>handleWA(r,cli)} disabled={genPDF} style={{color:"#25D366"}} title="WhatsApp + PDF">{genPDF?<RefreshCw size={13} className="spin"/>:<MessageCircle size={13}/>}</Btn>
      {r.status==="Pagada"&&<Btn variant="ghost" onClick={()=>{setCancelM(r);setCancelReason("");setAdminPwd("");setAdminErr("");}} style={{color:'var(--danger)'}}><XCircle size={13}/></Btn>}
    </div>;}},
  ];
  return<div>
    <PH title="Facturación" sub={`${data.invoices.length} facturas · ${data.invoices.filter(i=>i.status==="Pagada").length} pagadas`} action={can(currentUser,"facturas:crear")&&<Btn onClick={()=>openNew()}><Plus size={14}/>Nueva Factura</Btn>}/>
    <div className="card">
      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
        <SearchBar value={q} onChange={v=>{setQ(v);pgB.goPage(1);}} placeholder="Número de factura o cliente..."/>
        <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
          <FilterChips opts={["Pagada","Anulada"]} val={fSt} set={v=>{setFSt(v);pgB.goPage(1);}}/>
          <FilterChips opts={PAY_METHODS} val={fPay} set={v=>{setFPay(v);pgB.goPage(1);}}/>
        </div>
      </div>
      <Table cols={cols} rows={pgB.paged} empty="Sin facturas"/>
      <Pagination {...pgB}/>
    </div>
    {selInv&&(()=>{const cli=data.clients.find(c=>c.id===selInv.clientId);const{lines:billedLines,adjustment:billedAdj}=billedLineItems(selInv);return<Modal title="" onClose={()=>setSelInv(null)} width={660}>
      <div style={{marginBottom:16,paddingBottom:14,borderBottom:`2px solid ${'var(--border)'}`,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}><img src={data?.settings?.logo||LOGO} alt="" style={{height:48}}/><div style={{textAlign:"right"}}><code style={{fontSize:22,fontWeight:800,color:'var(--accent)'}}>{selInv.number}</code><div style={{fontSize:12,color:'var(--muted)',marginTop:3}}>Fecha: {fmtDate(selInv.fecha)}</div><div style={{marginTop:6}}><Badge v={selInv.status}/></div></div></div>
      {cli&&<div style={{background:'var(--accent-dim)',border:`1px solid ${'var(--accent-border)'}`,borderRadius:8,padding:"10px 13px",marginBottom:14}}><div style={{fontSize:11,color:'var(--muted)',marginBottom:3}}>EMITIDO A</div><div style={{fontSize:15,fontWeight:700,color:'var(--text)'}}>{cli.name} {cli.lastName}</div><div style={{fontSize:12,color:'var(--sub)',marginTop:2}}>📱 {cli.whatsapp}</div></div>}
      <div className="table-wrap">
      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:14,minWidth:560}}><thead><tr style={{borderBottom:`2px solid ${'var(--border)'}`}}>{["Código","Tracking","Tipo","Peso facturado (lb)","Tarifa","Subtotal"].map(h=><th key={h} style={{textAlign:h.includes("Sub")||h.includes("Tar")||h.includes("Peso")?"right":"left",padding:"7px 10px",fontSize:11,color:'var(--muted)',fontWeight:700,textTransform:"uppercase"}}>{h}</th>)}</tr></thead>
      <tbody>{billedLines.map(d=><tr key={d.pkgId} style={{borderBottom:`1px solid ${'var(--border)'}`}}><td style={{padding:"8px 10px",fontSize:12,fontFamily:"monospace",color:'var(--accent)',fontWeight:700}}>{d.code}</td><td style={{padding:"8px 10px",fontSize:11,color:'var(--muted)'}}>{d.tracking||"—"}</td><td style={{padding:"8px 10px",fontSize:12}}>{d.type}</td><td style={{padding:"8px 10px",textAlign:"right",fontSize:13,fontWeight:700}}>{d.billedWeight.toFixed(2)}</td><td style={{padding:"8px 10px",textAlign:"right",fontSize:12}}>{fmtUsd(d.rate)}</td><td style={{padding:"8px 10px",textAlign:"right",fontSize:13,fontWeight:700,color:'var(--accent)'}}>{fmtUsd(d.billedSubtotal)}</td></tr>)}
      {billedAdj&&<tr style={{background:'var(--warn-dim)',borderBottom:`1px solid ${'var(--border)'}`}}><td colSpan={3} style={{padding:"8px 10px",fontSize:12,color:'var(--warn)',fontStyle:"italic"}}>{billedAdj.label}</td><td style={{padding:"8px 10px",textAlign:"right",fontSize:13,fontWeight:700,color:'var(--warn)'}}>+{billedAdj.weight.toFixed(2)}</td><td/><td style={{padding:"8px 10px",textAlign:"right",fontSize:13,fontWeight:700,color:'var(--warn)'}}>+{fmtUsd(billedAdj.amount)}</td></tr>}
      </tbody></table>
      </div>
      <div style={{background:'var(--card2)',borderRadius:8,padding:"12px 14px",marginBottom:14}}>
        {selInv.discAmt>0&&<div style={{display:"flex",justifyContent:"space-between",color:'var(--success)',fontSize:13,marginBottom:6}}><span>Descuento ({selInv.discType==="porcentaje"?`${selInv.discVal}%`:fmtUsd(selInv.discVal)})</span><span>-{fmtUsd(selInv.discAmt)}</span></div>}
        <div style={{display:"flex",justifyContent:"space-between",fontSize:20,fontWeight:700,color:'var(--accent)'}}><span>Total USD</span><span>{fmtUsd(selInv.totalUsd)}</span></div>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:'var(--muted)',marginTop:4}}><span>Total NIO (×{selInv.exRate})</span><span>{fmtNio(selInv.totalNio)}</span></div>
        {selInv.reference&&<div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:'var(--muted)',marginTop:4}}><span>Ref. {selInv.payMethod}</span><b>{selInv.reference}</b></div>}
      </div>
      {selInv.status==="Anulada"&&<div style={{background:'var(--dan-dim)',borderRadius:8,padding:"10px 13px",marginBottom:10,fontSize:12,color:'var(--danger)'}}><b>Anulada por {selInv.cancelBy}:</b> {selInv.cancelReason}</div>}
      <div style={{display:'flex',gap:12,alignItems:'center',justifyContent:"flex-end",marginTop:16,gap:8}}>
        <Btn variant="secondary" onClick={()=>handlePrint(selInv,cli)}><Printer size={13}/>Imprimir</Btn>
        <Btn variant="secondary" disabled={genPDF} onClick={()=>handleWA(selInv,cli)} style={{color:"#25D366",borderColor:"#25D36633"}}>{genPDF?<><RefreshCw size={13} className="spin"/>Generando PDF…</>:<><MessageCircle size={13}/>WhatsApp + PDF</>}</Btn>
      </div>
    </Modal>;})()}
    {cancelM&&<Modal title={`Anular ${cancelM.number}`} onClose={()=>setCancelM(null)} width={480}>
      <div style={{background:'var(--dan-dim)',border:`1px solid ${'var(--danger)'}44`,borderRadius:8,padding:"10px 13px",marginBottom:14,fontSize:12,color:'var(--danger)',display:"flex",gap:7,alignItems:"center"}}><AlertTriangle size={15}/>Los paquetes volverán a estado Registrado. Acción irreversible.</div>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <FInput label="Motivo de anulación" value={cancelReason} onChange={setCancelReason} placeholder="Explique el motivo" required/>
        <FInput label="Contraseña del Administrador" value={adminPwd} onChange={v=>{setAdminPwd(v);setAdminErr("");}} type="password" placeholder="••••••••" required/>
        {adminErr&&<span style={{color:'var(--danger)',fontSize:12,display:"flex",gap:5,alignItems:"center"}}><AlertCircle size={12}/>{adminErr}</span>}
      </div>
      <div style={{display:'flex',gap:12,alignItems:'center',justifyContent:"flex-end",marginTop:18,gap:10}}><Btn variant="secondary" onClick={()=>setCancelM(null)}>Cancelar</Btn><Btn variant="danger" onClick={doCancel} disabled={saving}>{saving?<><RefreshCw size={13} className="spin"/>Procesando…</>:<><XCircle size={13}/>Confirmar Anulación</>}</Btn></div>
    </Modal>}
  </div>;
}

/* ── DELIVERY ───────────────────────────────────────────────────────── */
function DeliveryModule({data,refresh,addToast,currentUser}){
  const[modal,setModal]=useState(null);const[form,setForm]=useState({date:today(),obs:""});const[saving,setSaving]=useState(false);
  const[qPend,setQPend]=useState("");const[qHist,setQHist]=useState("");
  const pending=data.invoices.filter(i=>i.status==="Pagada"&&i.pkgIds.some(id=>data.packages.find(p=>p.id===id)?.status==="Facturado"));
  const pendFiltered=pending.filter(r=>{const c=data.clients.find(x=>x.id===r.clientId);return `${r.number} ${c?.name||""} ${c?.lastName||""}`.toLowerCase().includes(qPend.toLowerCase());});
  const pgPend=usePagination(pendFiltered);
  const histFiltered=data.deliveries.filter(d=>{const c=data.clients.find(x=>x.id===d.clientId);return `${d.invNumber} ${c?.name||""} ${c?.lastName||""} ${d.deliveredBy||""}`.toLowerCase().includes(qHist.toLowerCase());});
  const pgHist=usePagination(histFiltered);
  const doDeliver=async()=>{
    if(!form.date){addToast("Fecha obligatoria","error");return;}
    setSaving(true);
    try{await API.entregas.create({invId:modal.id,delivDate:form.date,obs:form.obs});addToast(`Entrega confirmada — ${modal.number}`,"success");await refresh();setModal(null);}
    catch(e){addToast(e.message,"error");}
    finally{setSaving(false);}
  };
  const pc=[{k:"n",l:"Recibo",render:r=><code style={{color:'var(--accent)',fontWeight:700}}>{r.number}</code>},{k:"c",l:"Cliente",render:r=>{const c=data.clients.find(x=>x.id===r.clientId);return c?<div><div style={{fontWeight:600}}>{c.name} {c.lastName}</div><div style={{fontSize:11,color:'var(--info)'}}>📱 {c.whatsapp}</div></div>:"-";}},{k:"p",l:"Paquetes",render:r=><span style={{color:'var(--muted)'}}>{r.pkgIds.length} paquete(s)</span>},{k:"t",l:"Total",render:r=><b style={{color:'var(--accent)'}}>{fmtUsd(r.totalUsd)}</b>},{k:"m",l:"Pago",render:r=><span style={{fontSize:12}}>{r.payMethod}</span>},{k:"a",l:"",render:r=>can(currentUser,"entregas:confirmar")&&<Btn onClick={()=>{setModal(r);setForm({date:today(),obs:""}); }} style={{background:'var(--suc-dim)',color:'var(--success)',border:`1px solid ${'var(--success)'}44`}}><Truck size={14}/>Confirmar Entrega</Btn>}];
  const hc=[{k:"in",l:"Recibo",render:r=><code style={{color:'var(--accent)'}}>{r.invNumber}</code>},{k:"c",l:"Cliente",render:r=>{const c=data.clients.find(x=>x.id===r.clientId);return c?`${c.name} ${c.lastName}`:"-";}},{k:"b",l:"Entregó",render:r=><span style={{fontSize:12}}>{r.deliveredBy}</span>},{k:"d",l:"Fecha",render:r=><b style={{color:'var(--success)'}}>{fmtDate(r.delivDate)}</b>},{k:"o",l:"Obs.",render:r=><span style={{fontSize:12,color:'var(--muted)'}}>{r.obs||"—"}</span>}];
  return<div>
    <PH title="Entrega Física" sub={`${pending.length} pendientes · ${data.deliveries.length} confirmadas`}/>
    <div style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:'20px',marginBottom:16}}>
      <h3 style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:10}}>Pendientes de entrega</h3>
      <div style={{marginBottom:10}}><SearchBar value={qPend} onChange={v=>{setQPend(v);pgPend.goPage(1);}} placeholder="Recibo, cliente…"/></div>
      <Table cols={pc} rows={pgPend.paged} empty="Sin pendientes"/>
      <Pagination {...pgPend}/>
    </div>
    <div className="card">
      <h3 style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:10}}>Historial de entregas</h3>
      <div style={{marginBottom:10}}><SearchBar value={qHist} onChange={v=>{setQHist(v);pgHist.goPage(1);}} placeholder="Recibo, cliente, entregó…"/></div>
      <Table cols={hc} rows={pgHist.paged} empty="Sin historial"/>
      <Pagination {...pgHist}/>
    </div>
    {modal&&(()=>{const cli=data.clients.find(c=>c.id===modal.clientId);return<Modal title={`Confirmar entrega — ${modal.number}`} onClose={()=>setModal(null)}>
      <div style={{background:'var(--suc-dim)',border:`1px solid ${'var(--success)'}44`,borderRadius:8,padding:"10px 13px",marginBottom:14,fontSize:13,color:'var(--success)',display:"flex",gap:8}}><Truck size={16}/><span>Entrega de <b>{modal.pkgIds.length}</b> paquete(s) a <b>{cli?.name} {cli?.lastName}</b></span></div>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <FInput label="Fecha de entrega física" value={form.date} onChange={v=>setForm(p=>({...p,date:v}))} type="date" required/>
        <FInput label="Observaciones" value={form.obs} onChange={v=>setForm(p=>({...p,obs:v}))} placeholder="Ej: Entregado en oficina"/>
      </div>
      <div style={{display:'flex',gap:12,alignItems:'center',justifyContent:"flex-end",marginTop:18,gap:10}}><Btn variant="secondary" onClick={()=>setModal(null)}>Cancelar</Btn><Btn onClick={doDeliver} style={{background:'var(--success)',color:"#fff"}} disabled={saving}>{saving?<><RefreshCw size={13} className="spin"/>Confirmando…</>:<><CheckCircle size={14}/>Confirmar Entrega</>}</Btn></div>
    </Modal>;})()}
  </div>;
}

/* ── REPORTS ────────────────────────────────────────────────────────── */
// Comparación visual de dos series (Aéreo/Marítimo) para una métrica —
// barras horizontales escaladas al máximo del par, sin depender de un eje
// compartido con otras métricas de escala muy distinta (paquetes vs $ vs lb).
function CompareRow({label,aereo,maritimo,fmt}){
  const f=fmt||(v=>v);
  const max=Math.max(aereo,maritimo,1);
  const Bar=({lbl,val,color})=><div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
    <span style={{width:64,fontSize:11,color:"var(--muted)"}}>{lbl}</span>
    <div style={{flex:1,background:"var(--card2)",borderRadius:4,height:14,overflow:"hidden"}}><div style={{width:`${(val/max)*100}%`,height:"100%",background:color,borderRadius:4}}/></div>
    <span style={{width:76,fontSize:12,fontWeight:700,textAlign:"right"}}>{f(val)}</span>
  </div>;
  return<div style={{marginBottom:14}}>
    <div style={{fontSize:12,fontWeight:700,marginBottom:6,color:"var(--text)"}}>{label}</div>
    <Bar lbl="Aéreo" val={aereo} color={CC[0]}/>
    <Bar lbl="Marítimo" val={maritimo} color={CC[1]}/>
  </div>;
}

function ReportsModule({data,addToast}){
  const periodos=data.periodos||[];
  const filter=usePeriodFilter();
  const{desde,hasta,label}=useMemo(()=>resolvePeriodFilter(filter,periodos),[filter.preset,filter.desde,filter.hasta,filter.selPId,periodos]);
  const agg=useMemo(()=>computeReportData(data,desde,hasta),[data,desde,hasta]);

  const[sortRank,setSortRank]=useState({key:"totalUsd",dir:"desc"});
  const onSortRank=key=>setSortRank(p=>p.key===key?{key,dir:p.dir==="asc"?"desc":"asc"}:{key,dir:"desc"});
  const rankingSorted=useMemo(()=>{
    const dir=sortRank.dir==="asc"?1:-1;
    return[...agg.rankingClientes].sort((a,b)=>{
      const av=sortRank.key==="name"?`${a.name} ${a.lastName}`:a[sortRank.key];
      const bv=sortRank.key==="name"?`${b.name} ${b.lastName}`:b[sortRank.key];
      if(typeof av==="string")return av.localeCompare(bv,'es')*dir;
      return((+av||0)-(+bv||0))*dir;
    });
  },[agg.rankingClientes,sortRank]);

  const pgResumen=usePagination(agg.resumenPeriodo,10);
  const pgRanking=usePagination(rankingSorted,10);
  const pgRecibos=usePagination(agg.detalleRecibos,10);
  const pgPaquetes=usePagination(agg.detallePaquetes,10);

  // Las exportaciones deben reflejar exactamente el filtro activo: el sufijo
  // de nombre de archivo y el título del PDF/Excel incluyen la etiqueta del
  // filtro (o van sin sufijo cuando es "todo el historial").
  const fnSlug=(label||"").toLowerCase().replace(/[^a-z0-9áéíóúñ]+/gi,"_").replace(/^_+|_+$/g,"").slice(0,40);
  const fnamed=base=>fnSlug?`${base}_${fnSlug}`:base;
  const titled=t=>label?`${t} — ${label}`:t;

  const gastosPie=agg.gastosCat.map((g,i)=>({n:g.cat,v:g.total,c:CC[i%CC.length]}));

  return<div style={{display:"flex",flexDirection:"column",gap:16}}>
    <PH title="Reportes" sub={label||"Análisis financiero y operacional — todo el historial"}/>

    <PeriodFilterBar filter={filter} periodos={periodos} includePeriodos={true} label={label}/>

    {/* ── KPIs PRINCIPALES ── */}
    <div className="grid-4">
      <StatCard icon={DollarSign} title="Ingresos" value={fmtUsd(agg.totalRev)} sub={`${agg.cntRecibos} recibos pagados`} color="var(--success)" addToast={addToast} exportFilename={fnamed("ingresos")} exportRows={kpiExportRows("Ingresos",fmtUsd(agg.totalRev),`${agg.cntRecibos} recibos pagados`)} exportColumns={KPI_EXPORT_COLS}/>
      <StatCard icon={TrendingDown} title="Gastos" value={fmtUsd(agg.totalGastos)} sub={`${agg.cntGastos} registros`} color="var(--danger)" addToast={addToast} exportFilename={fnamed("gastos")} exportRows={kpiExportRows("Gastos",fmtUsd(agg.totalGastos),`${agg.cntGastos} registros`)} exportColumns={KPI_EXPORT_COLS}/>
      <StatCard icon={DollarSign} title="Utilidad neta" value={fmtUsd(Math.abs(agg.utilidad))} sub={agg.utilidad>=0?"Ganancia":"Pérdida"} color={agg.utilidad>=0?"var(--success)":"var(--danger)"} addToast={addToast} exportFilename={fnamed("utilidad_neta")} exportRows={kpiExportRows("Utilidad neta",fmtUsd(Math.abs(agg.utilidad)),agg.utilidad>=0?"Ganancia":"Pérdida")} exportColumns={KPI_EXPORT_COLS}/>
      <StatCard icon={Package} title="Paquetes" value={agg.paquetesRegistrados} sub={`${agg.paquetesEntregados} entregados`} color="var(--accent)" addToast={addToast} exportFilename={fnamed("paquetes")} exportRows={agg.paquetesRegList} exportColumns={PKG_EXPORT_COLS(data.clients)}/>
    </div>
    <div className="grid-4">
      <StatCard icon={FileText} title="Recibos emitidos" value={agg.cntRecibos} sub={`${agg.cntAnulados} anulados`} color="var(--violet)" addToast={addToast} exportFilename={fnamed("recibos_emitidos")} exportRows={agg.facturasPagadasList} exportColumns={INV_EXPORT_COLS(data.clients)}/>
      <StatCard icon={Receipt} title="OP Pendientes" value={agg.opPend} sub={`${agg.opCanc} canceladas`} color="var(--warn)" addToast={addToast} exportFilename={fnamed("op_pendientes")} exportRows={kpiExportRows("OP Pendientes",agg.opPend,`${agg.opCanc} canceladas`)} exportColumns={KPI_EXPORT_COLS}/>
      <StatCard icon={CheckCircle} title="OP Convertidas" value={agg.opConv} sub={`Tasa: ${agg.tasaConv}%`} color="var(--info)" addToast={addToast} exportFilename={fnamed("op_convertidas")} exportRows={kpiExportRows("OP Convertidas",agg.opConv,`Tasa: ${agg.tasaConv}%`)} exportColumns={KPI_EXPORT_COLS}/>
      <StatCard icon={Users} title="Clientes" value={agg.clientesNuevos} sub="nuevos en el período" color="var(--info)" addToast={addToast} exportFilename={fnamed("clientes")} exportRows={kpiExportRows("Clientes nuevos",agg.clientesNuevos,"en el período")} exportColumns={KPI_EXPORT_COLS}/>
    </div>
    <div className="grid-4">
      <StatCard icon={Layers} title="Libras facturadas" value={fmtLbs(agg.librasFacturadas.total)} color="var(--accent)" addToast={addToast} exportFilename={fnamed("libras_facturadas")} exportRows={agg.facturasPagadasList} exportColumns={INV_EXPORT_COLS(data.clients)} breakdown={[{label:"Aéreo",value:fmtLbs(agg.librasFacturadas.aereo)},{label:"Marítimo",value:fmtLbs(agg.librasFacturadas.maritimo)}]}/>
      <StatCard icon={DollarSign} title="Ticket promedio" value={fmtUsd(agg.ticketProm)} sub="Promedio por recibo" color="var(--success)" addToast={addToast} exportFilename={fnamed("ticket_promedio")} exportRows={kpiExportRows("Ticket promedio",fmtUsd(agg.ticketProm),"Promedio por recibo")} exportColumns={KPI_EXPORT_COLS}/>
    </div>

    {/* ── GRÁFICOS ── */}
    {agg.chartIngresos.length>0
      ?<div className="grid-2m">
        <div className="card">
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <h3 style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>Ingresos</h3>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <ExportButton rows={agg.chartIngresos} columns={[{key:"m",label:"Período"},{key:"v",label:"Ingresos USD",value:r=>fmtUsd(r.v)},{key:"f",label:"Recibos"}]} filename={fnamed("ingresos")} title={titled("Ingresos")} addToast={addToast}/>
              <img src={data?.settings?.logo||LOGO} alt="" style={{height:20,opacity:0.7}}/>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={agg.chartIngresos}><CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/><XAxis dataKey="m" tick={{fill:"var(--muted)",fontSize:11}}/><YAxis tick={{fill:"var(--muted)",fontSize:11}} tickFormatter={v=>`$${v}`}/><Tooltip {...TIP} formatter={v=>[fmtUsd(v),"USD"]}/><Bar dataKey="v" fill="var(--accent)" radius={[4,4,0,0]}/></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <h3 style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>Paquetes registrados por período</h3>
            <ExportButton rows={agg.chartPaquetes} columns={[{key:"m",label:"Período"},{key:"v",label:"Paquetes"}]} filename={fnamed("paquetes_por_periodo")} title={titled("Paquetes registrados por período")} addToast={addToast}/>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={agg.chartPaquetes}><CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/><XAxis dataKey="m" tick={{fill:"var(--muted)",fontSize:11}}/><YAxis tick={{fill:"var(--muted)",fontSize:11}} allowDecimals={false}/><Tooltip {...TIP}/><Bar dataKey="v" name="Paquetes" fill="var(--info)" radius={[4,4,0,0]}/></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <h3 style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>Evolución de libras facturadas</h3>
            <ExportButton rows={agg.chartLibrasFacturadas} columns={[{key:"m",label:"Período"},{key:"aereo",label:"Aéreo (lb)"},{key:"maritimo",label:"Marítimo (lb)"}]} filename={fnamed("evolucion_libras_facturadas")} title={titled("Evolución de libras facturadas")} addToast={addToast}/>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={agg.chartLibrasFacturadas}><CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/><XAxis dataKey="m" tick={{fill:"var(--muted)",fontSize:11}}/><YAxis tick={{fill:"var(--muted)",fontSize:11}}/><Tooltip {...TIP}/><Line type="monotone" dataKey="aereo" name="Aéreo" stroke={CC[0]} strokeWidth={2.5} dot={{r:3}}/><Line type="monotone" dataKey="maritimo" name="Marítimo" stroke={CC[1]} strokeWidth={2.5} dot={{r:3}}/></LineChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <h3 style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>Comparativo Aéreo vs Marítimo</h3>
            <ExportButton rows={agg.comparativoTipo} columns={[{key:"tipo",label:"Tipo"},{key:"paquetes",label:"Paquetes"},{key:"libras",label:"Libras"},{key:"ingresos",label:"Ingresos USD",value:r=>fmtUsd(r.ingresos)}]} filename={fnamed("comparativo_tipo")} title={titled("Comparativo Aéreo vs Marítimo")} addToast={addToast}/>
          </div>
          <CompareRow label="Paquetes" aereo={agg.comparativoTipo[0].paquetes} maritimo={agg.comparativoTipo[1].paquetes}/>
          <CompareRow label="Libras facturadas" aereo={agg.comparativoTipo[0].libras} maritimo={agg.comparativoTipo[1].libras} fmt={fmtLbs}/>
          <CompareRow label="Ingresos generados" aereo={agg.comparativoTipo[0].ingresos} maritimo={agg.comparativoTipo[1].ingresos} fmt={fmtUsd}/>
        </div>
      </div>
      :<div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:12,padding:"40px",textAlign:"center"}}><p style={{color:"var(--muted)",fontSize:13}}>Sin recibos pagados en el período seleccionado</p></div>
    }

    {/* ── GASTOS Y ÓRDENES ── */}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
      <div className="card">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <h3 style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>Gastos por categoría</h3>
          <ExportButton rows={agg.gastosCat} columns={[{key:"cat",label:"Categoría"},{key:"cnt",label:"Registros"},{key:"total",label:"Total USD",value:r=>fmtUsd(r.total)}]} filename={fnamed("gastos_por_categoria")} title={titled("Gastos por categoría")} addToast={addToast}/>
        </div>
        {agg.gastosCat.length===0
          ?<p style={{color:"var(--muted)",fontSize:13}}>Sin gastos en este período</p>
          :<>
            <ResponsiveContainer width="100%" height={140}><PieChart><Pie data={gastosPie} cx="50%" cy="50%" innerRadius={36} outerRadius={58} paddingAngle={3} dataKey="v">{gastosPie.map((e,i)=><Cell key={i} fill={e.c}/>)}</Pie><Tooltip {...TIP} formatter={(v,n,p)=>[fmtUsd(v),p.payload.n]}/></PieChart></ResponsiveContainer>
            {agg.gastosCat.map(g=><div key={g.cat} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid var(--border)"}}>
              <div><div style={{fontSize:13,fontWeight:600}}>{g.cat}</div><div style={{fontSize:11,color:"var(--muted)"}}>{g.cnt} registro(s)</div></div>
              <b style={{color:"var(--danger)"}}>{fmtUsd(g.total)}</b>
            </div>)}
            <div style={{display:"flex",justifyContent:"space-between",marginTop:12,paddingTop:10,borderTop:"2px solid var(--border)"}}>
              <span style={{fontSize:13,fontWeight:700}}>Total gastos</span>
              <b style={{color:"var(--danger)",fontSize:15}}>{fmtUsd(agg.totalGastos)}</b>
            </div>
          </>
        }
      </div>
      <div className="card">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <h3 style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>Órdenes de Pago — conversión</h3>
          <ExportButton rows={[{l:"Pendientes",v:agg.opPend},{l:"Convertidas a Recibo",v:agg.opConv},{l:"Canceladas",v:agg.opCanc},{l:"Tasa de conversión",v:`${agg.tasaConv}%`}]} columns={[{key:"l",label:"Indicador"},{key:"v",label:"Valor"}]} filename={fnamed("ordenes_conversion")} title={titled("Órdenes de Pago — conversión")} addToast={addToast}/>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {[{l:"Pendientes",v:agg.opPend,c:"var(--warn)"},{l:"Convertidas a Recibo",v:agg.opConv,c:"var(--success)"},{l:"Canceladas",v:agg.opCanc,c:"var(--danger)"}].map(r=><div key={r.l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:"1px solid var(--border)"}}>
            <span style={{fontSize:13,color:"var(--sub)"}}>{r.l}</span><b style={{color:r.c,fontSize:16}}>{r.v}</b>
          </div>)}
          <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
            <span style={{fontSize:13,fontWeight:700}}>Tasa de conversión</span>
            <b style={{color:"var(--accent)",fontSize:16}}>{agg.tasaConv}%</b>
          </div>
        </div>
      </div>
    </div>

    {/* ── RESUMEN POR PERÍODO ── */}
    <div className="card">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <h3 style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>Resumen por período</h3>
        <ExportButton rows={agg.resumenPeriodo} columns={[{key:"periodo",label:"Período"},{key:"paquetes",label:"Paquetes"},{key:"libras",label:"Libras"},{key:"ingresos",label:"Ingresos USD",value:r=>fmtUsd(r.ingresos)},{key:"gastos",label:"Gastos USD",value:r=>fmtUsd(r.gastos)},{key:"utilidad",label:"Utilidad USD",value:r=>fmtUsd(r.utilidad)}]} filename={fnamed("resumen_por_periodo")} title={titled("Resumen por período")} addToast={addToast}/>
      </div>
      <Table
        cols={[
          {k:"periodo",l:"Período"},
          {k:"paquetes",l:"Paquetes"},
          {k:"libras",l:"Libras",render:r=><span>{fmtLbs(r.libras)}</span>},
          {k:"ingresos",l:"Ingresos",render:r=><b style={{color:"var(--success)"}}>{fmtUsd(r.ingresos)}</b>},
          {k:"gastos",l:"Gastos",render:r=><b style={{color:"var(--danger)"}}>{fmtUsd(r.gastos)}</b>},
          {k:"utilidad",l:"Utilidad",render:r=><b style={{color:r.utilidad>=0?"var(--success)":"var(--danger)"}}>{fmtUsd(r.utilidad)}</b>},
        ]}
        rows={pgResumen.paged}
        empty="Sin datos para el período seleccionado"
      />
      <Pagination {...pgResumen}/>
    </div>

    {/* ── RANKING CLIENTES ── */}
    <div className="card">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <h3 style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>Ranking de clientes</h3>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <ExportButton rows={rankingSorted} columns={[{key:"n",label:"Cliente",value:r=>`${r.name} ${r.lastName}`},{key:"pkgs",label:"Paquetes"},{key:"libras",label:"Libras"},{key:"invs",label:"Recibos"},{key:"totalUsd",label:"Total cobrado USD",value:r=>fmtUsd(r.totalUsd)},{key:"ticketProm",label:"Ticket promedio USD",value:r=>fmtUsd(r.ticketProm)}]} filename={fnamed("ranking_clientes")} title={titled("Ranking de clientes")} addToast={addToast}/>
          <img src={data?.settings?.logo||LOGO} alt="" style={{height:20,opacity:0.7}}/>
        </div>
      </div>
      <Table
        cols={[
          {k:"n",l:"Cliente",sortKey:"name",render:r=><div><div style={{fontWeight:600}}>{r.name} {r.lastName}</div>{r.label&&<Badge v={r.label}/>}</div>},
          {k:"pkgs",l:"Paquetes",sortKey:"pkgs",render:r=><b>{r.pkgs}</b>},
          {k:"libras",l:"Libras",sortKey:"libras",render:r=><span>{fmtLbs(r.libras)}</span>},
          {k:"invs",l:"Recibos",sortKey:"invs",render:r=><span>{r.invs}</span>},
          {k:"totalUsd",l:"Total cobrado",sortKey:"totalUsd",render:r=><b style={{color:"var(--accent)",fontSize:14}}>{fmtUsd(r.totalUsd)}</b>},
          {k:"ticketProm",l:"Ticket promedio",sortKey:"ticketProm",render:r=><span>{fmtUsd(r.ticketProm)}</span>},
        ]}
        rows={pgRanking.paged}
        sort={sortRank}
        onSort={onSortRank}
        empty="Sin datos para el período seleccionado"
      />
      <Pagination {...pgRanking}/>
    </div>

    {/* ── DETALLE DE RECIBOS ── */}
    <div className="card">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <h3 style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>Detalle de recibos</h3>
        <ExportButton rows={agg.detalleRecibos} columns={[{key:"fecha",label:"Fecha",value:r=>fmtDate(r.fecha)},{key:"number",label:"Número"},{key:"cliente",label:"Cliente"},{key:"tipo",label:"Tipo de envío"},{key:"cantPaquetes",label:"Cant. paquetes"},{key:"libras",label:"Libras"},{key:"totalUsd",label:"Total facturado USD",value:r=>fmtUsd(r.totalUsd)}]} filename={fnamed("detalle_recibos")} title={titled("Detalle de recibos")} addToast={addToast}/>
      </div>
      <Table
        cols={[
          {k:"fecha",l:"Fecha",render:r=><span className="fs-12 c-muted">{fmtDate(r.fecha)}</span>},
          {k:"number",l:"Número",render:r=><code className="c-accent fw-700 fs-12">{r.number}</code>},
          {k:"cliente",l:"Cliente"},
          {k:"tipo",l:"Tipo de envío"},
          {k:"cantPaquetes",l:"Cant. paquetes"},
          {k:"libras",l:"Libras",render:r=><span>{fmtLbs(r.libras)}</span>},
          {k:"totalUsd",l:"Total facturado",render:r=><b style={{color:"var(--accent)"}}>{fmtUsd(r.totalUsd)}</b>},
        ]}
        rows={pgRecibos.paged}
        empty="Sin recibos para el período seleccionado"
      />
      <Pagination {...pgRecibos}/>
    </div>

    {/* ── DETALLE DE PAQUETES ── */}
    <div className="card">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <h3 style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>Detalle de paquetes</h3>
        <ExportButton rows={agg.detallePaquetes} columns={[{key:"tracking",label:"Tracking"},{key:"cliente",label:"Cliente"},{key:"tipo",label:"Tipo de envío"},{key:"peso",label:"Peso (lb)"},{key:"estado",label:"Estado"},{key:"fechaRegistro",label:"Fecha de registro",value:r=>fmtDate(r.fechaRegistro)},{key:"fechaFacturacion",label:"Fecha de facturación",value:r=>r.fechaFacturacion?fmtDate(r.fechaFacturacion):""},{key:"fechaEntrega",label:"Fecha de entrega",value:r=>r.fechaEntrega?fmtDate(r.fechaEntrega):""}]} filename={fnamed("detalle_paquetes")} title={titled("Detalle de paquetes")} addToast={addToast}/>
      </div>
      <Table
        cols={[
          {k:"tracking",l:"Tracking",render:r=><code className="fs-11 c-sub">{r.tracking||"—"}</code>},
          {k:"cliente",l:"Cliente"},
          {k:"tipo",l:"Tipo de envío"},
          {k:"peso",l:"Peso",render:r=><b>{fmtLbs(r.peso)}</b>},
          {k:"estado",l:"Estado",render:r=><Badge v={r.estado}/>},
          {k:"fechaRegistro",l:"Registro",render:r=><span className="fs-12 c-muted">{fmtDate(r.fechaRegistro)}</span>},
          {k:"fechaFacturacion",l:"Facturación",render:r=><span className="fs-12 c-muted">{r.fechaFacturacion?fmtDate(r.fechaFacturacion):"—"}</span>},
          {k:"fechaEntrega",l:"Entrega",render:r=><span className="fs-12 c-muted">{r.fechaEntrega?fmtDate(r.fechaEntrega):"—"}</span>},
        ]}
        rows={pgPaquetes.paged}
        empty="Sin paquetes para el período seleccionado"
      />
      <Pagination {...pgPaquetes}/>
    </div>
  </div>;
}

/* ── AUDIT ──────────────────────────────────────────────────────────── */
function AuditModule({data}){
  const[q,setQ]=useState("");const[fm,setFm]=useState("");
  const mods=[...new Set(data.audit.map(a=>a.module))];
  const list=data.audit.filter(a=>`${a.user} ${a.entity} ${a.action} ${a.module}`.toLowerCase().includes(q.toLowerCase())&&(!fm||a.module===fm));
  const pgAu=usePagination(list,25);
  const aC={CREAR:'var(--success)',CREAR_MASIVO:'var(--success)',ACTUALIZAR:'var(--info)',ANULAR:'var(--danger)',CONFIRMAR:'var(--accent)',LOGIN:'var(--violet)',"CAMBIO ESTADO":'var(--warn)'};
  const cols=[{k:"at",l:"Fecha/Hora",render:r=><code style={{fontSize:11,color:'var(--muted)'}}>{fmtDate(r.at)}</code>},{k:"u",l:"Usuario",render:r=><b style={{fontSize:12}}>{r.user}</b>},{k:"m",l:"Módulo",render:r=><span style={{fontSize:12,color:'var(--sub)'}}>{r.module}</span>},{k:"a",l:"Acción",render:r=><span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:4,background:`${aC[r.action]||'var(--muted)'}22`,color:aC[r.action]||'var(--muted)'}}>{r.action}</span>},{k:"e",l:"Registro",render:r=><code style={{fontSize:12,color:'var(--accent)',fontWeight:700}}>{r.entity}</code>},{k:"b",l:"Antes",render:r=>r.before?<code style={{fontSize:10,color:'var(--danger)'}}>{JSON.stringify(r.before)}</code>:<span style={{color:'var(--muted)'}}>—</span>},{k:"af",l:"Después",render:r=>r.after?<code style={{fontSize:10,color:'var(--success)'}}>{JSON.stringify(r.after)}</code>:<span style={{color:'var(--muted)'}}>—</span>},{k:"ip",l:"IP",render:r=><code style={{fontSize:11,color:'var(--muted)'}}>{r.ip}</code>}];
  return<div>
    <PH title="Auditoría del Sistema" sub={`${data.audit.length} eventos registrados`}/>
    <div className="card">
      <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:10,flexWrap:"wrap"}}>
        <SearchBar value={q} onChange={v=>{setQ(v);pgAu.goPage(1);}} placeholder="Usuario, módulo, entidad..."/>
      </div>
      <div style={{marginBottom:14}}><FilterChips opts={mods.map(m=>({v:m,l:m}))} val={fm} set={v=>{setFm(v);pgAu.goPage(1);}}/></div>
      <Table cols={cols} rows={pgAu.paged} empty="Sin eventos"/>
      <Pagination {...pgAu}/>
    </div>
  </div>;
}

/* ── SECURITY ───────────────────────────────────────────────────────── */
function SecurityModule({data,refresh,addToast}){
  // ── Usuarios ──────────────────────────────────────────────────────
  const[modal,setModal]=useState(null);const[form,setForm]=useState({});const[errs,setErrs]=useState({});const[saving,setSaving]=useState(false);
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));

  // ── Roles y permisos ──────────────────────────────────────────────
  const[roles,setRoles]=useState([]);
  const[permisosCatalog,setPermisosCatalog]=useState({});
  const[loadingRoles,setLoadingRoles]=useState(false);
  const[rolModal,setRolModal]=useState(null); // 'new' | 'edit' | null
  const[rolForm,setRolForm]=useState({});
  const[rolErrs,setRolErrs]=useState({});
  const[savingRol,setSavingRol]=useState(false);
  const[permRol,setPermRol]=useState(null);
  const[permSelected,setPermSelected]=useState(new Set());
  const[savingPerm,setSavingPerm]=useState(false);
  const[dupRol,setDupRol]=useState(null);
  const[dupNombre,setDupNombre]=useState("");
  const[savingDup,setSavingDup]=useState(false);
  const[delRol,setDelRol]=useState(null);
  const[deletingRol,setDeletingRol]=useState(false);

  const loadRoles=useCallback(async()=>{
    setLoadingRoles(true);
    try{
      const[r,cat]=await Promise.all([API.roles.getAll(),API.roles.getPermisosCatalog()]);
      setRoles(r);setPermisosCatalog(cat);
    }catch(e){addToast("Error cargando roles: "+e.message,"error");}
    finally{setLoadingRoles(false);}
  },[addToast]);
  useEffect(()=>{loadRoles();},[loadRoles]);

  const save=async()=>{
    const e={};
    if(!form.name?.trim())e.name="Requerido";
    if(!form.email?.trim())e.email="Requerido";
    if(modal==="new"&&(!form.pwd||form.pwd.length<8))e.pwd="Mín. 8 caracteres";
    if(!form.rolId)e.rolId="Requerido";
    setErrs(e);if(Object.keys(e).length)return;
    setSaving(true);
    try{
      const body={...form,rolId:parseInt(form.rolId)};
      if(modal==="new"){await API.usuarios.create(body);addToast("Usuario creado","success");}
      else{await API.usuarios.update(form.id,body);addToast("Usuario actualizado","success");}
      await refresh();setModal(null);
    }catch(e){addToast(e.message,"error");}
    finally{setSaving(false);}
  };

  const rf=(k,v)=>setRolForm(p=>({...p,[k]:v}));
  const openNewRol=()=>{setRolForm({nombre:"",descripcion:"",activo:true});setRolErrs({});setRolModal("new");};
  const openEditRol=(rol)=>{setRolForm({...rol});setRolErrs({});setRolModal("edit");};
  const saveRol=async()=>{
    const e={};
    if(!rolForm.nombre?.trim())e.nombre="Requerido";
    setRolErrs(e);if(Object.keys(e).length)return;
    setSavingRol(true);
    try{
      if(rolModal==="new"){
        await API.roles.create({nombre:rolForm.nombre.trim(),descripcion:rolForm.descripcion?.trim()||""});
        addToast("Rol creado","success");
      }else{
        await API.roles.update(rolForm.id,{nombre:rolForm.nombre.trim(),descripcion:rolForm.descripcion?.trim()||"",activo:rolForm.activo});
        addToast("Rol actualizado","success");
      }
      await loadRoles();setRolModal(null);
    }catch(e){addToast(e.message,"error");}
    finally{setSavingRol(false);}
  };

  const openPermisos=async(rol)=>{
    setPermRol(rol);
    try{
      const r=await API.roles.getPermisosByRol(rol.id);
      setPermSelected(new Set(r.permisoIds));
    }catch(e){addToast(e.message,"error");setPermRol(null);}
  };
  const togglePermiso=(id)=>setPermSelected(prev=>{
    const next=new Set(prev);
    if(next.has(id))next.delete(id);else next.add(id);
    return next;
  });
  const toggleAllModulo=(modulo)=>{
    const ids=permisosCatalog[modulo].map(p=>p.id);
    const allSel=ids.every(id=>permSelected.has(id));
    setPermSelected(prev=>{
      const next=new Set(prev);
      ids.forEach(id=>allSel?next.delete(id):next.add(id));
      return next;
    });
  };
  const savePermisos=async()=>{
    setSavingPerm(true);
    try{
      await API.roles.updatePermisos(permRol.id,[...permSelected]);
      addToast("Permisos actualizados","success");
      await loadRoles();setPermRol(null);
    }catch(e){addToast(e.message,"error");}
    finally{setSavingPerm(false);}
  };

  const openDuplicar=(rol)=>{setDupRol(rol);setDupNombre(`${rol.nombre} (Copia)`);};
  const doDuplicar=async()=>{
    if(!dupNombre.trim()){addToast("Nombre requerido","error");return;}
    setSavingDup(true);
    try{
      await API.roles.duplicar(dupRol.id,dupNombre.trim());
      addToast("Rol duplicado","success");
      await loadRoles();setDupRol(null);setDupNombre("");
    }catch(e){addToast(e.message,"error");}
    finally{setSavingDup(false);}
  };

  const doDeleteRol=async()=>{
    setDeletingRol(true);
    try{
      await API.roles.delete(delRol.id);
      addToast("Rol eliminado","info");
      await loadRoles();setDelRol(null);
    }catch(e){addToast(e.message,"error");}
    finally{setDeletingRol(false);}
  };

  const cols=[
    {k:"n",l:"Usuario",render:r=><div><div style={{fontWeight:600}}>{r.name}</div><div style={{fontSize:11,color:'var(--muted)'}}>{r.email}</div></div>},
    {k:"r",l:"Rol",render:r=><span style={{fontSize:12,fontWeight:700,color:r.role==="Administrador"?'var(--accent)':'var(--sub)'}}>{r.rolNombre||r.role}</span>},
    {k:"s",l:"Estado",render:r=><Badge v={r.status}/>},
    {k:"l",l:"Último acceso",render:r=><code style={{fontSize:11,color:'var(--muted)'}}>{r.lastLogin}</code>},
    {k:"a",l:"",render:r=><Btn variant="ghost" onClick={()=>{setForm({...r});setErrs({});setModal("edit");}}><Edit2 size={13}/></Btn>},
  ];

  const rolCols=[
    {k:"nombre",l:"Rol",render:r=><div><b>{r.nombre}</b>{!!r.es_sistema&&<span style={{marginLeft:6,fontSize:10,color:'var(--muted)'}}>· sistema</span>}</div>},
    {k:"descripcion",l:"Descripción",render:r=><span className="fs-12 c-muted">{r.descripcion||"—"}</span>},
    {k:"total_permisos",l:"Permisos",render:r=><b>{r.total_permisos}</b>},
    {k:"activo",l:"Estado",render:r=><Badge v={r.activo?"Activo":"Inactivo"}/>},
    {k:"a",l:"",render:r=><div style={{display:"flex",gap:4}}>
      <Btn variant="ghost" onClick={()=>openEditRol(r)} title="Editar rol"><Edit2 size={13}/></Btn>
      <Btn variant="ghost" onClick={()=>openPermisos(r)} title="Gestionar permisos"><Shield size={13}/></Btn>
      <Btn variant="ghost" onClick={()=>openDuplicar(r)} title="Duplicar rol"><Copy size={13}/></Btn>
      {!r.es_sistema&&<Btn variant="ghost" style={{color:'var(--danger)'}} onClick={()=>setDelRol(r)} title="Eliminar rol"><X size={13}/></Btn>}
    </div>},
  ];

  return<div>
    <PH title="Seguridad y Usuarios" sub={`${data.users.length} usuarios · ${roles.length} roles`} action={<Btn onClick={()=>{const op=roles.find(r=>r.nombre==="Operador");setForm({name:"",email:"",pwd:"",rolId:op?.id||"",status:"Activo"});setErrs({});setModal("new");}}><Plus size={14}/>Nuevo Usuario</Btn>}/>
    <div className="card" style={{marginBottom:14}}><Table cols={cols} rows={data.users} empty="Sin usuarios registrados"/></div>

    <div className="card">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <h3 style={{fontSize:13,fontWeight:700,color:'var(--text)'}}>Roles y permisos</h3>
        <Btn variant="secondary" onClick={openNewRol}><Plus size={13}/>Nuevo Rol</Btn>
      </div>
      {loadingRoles?<Spinner/>:<Table cols={rolCols} rows={roles} empty="Sin roles registrados"/>}
    </div>

    {modal&&<Modal title={modal==="new"?"Nuevo Usuario":"Editar Usuario"} onClose={()=>setModal(null)}>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <FInput label="Nombre completo" value={form.name||""} onChange={v=>f("name",v)} required error={errs.name}/>
        <FInput label="Correo electrónico" value={form.email||""} onChange={v=>f("email",v)} type="email" required error={errs.email}/>
        {modal==="new"&&<FInput label="Contraseña (mín. 8 caracteres)" value={form.pwd||""} onChange={v=>f("pwd",v)} type="password" required error={errs.pwd}/>}
        <FSelect label="Rol" value={form.rolId?String(form.rolId):""} onChange={v=>f("rolId",v)} options={roles.filter(r=>r.activo).map(r=>({v:r.id,l:r.nombre}))} required error={errs.rolId}/>
        <FSelect label="Estado" value={form.status||"Activo"} onChange={v=>f("status",v)} options={["Activo","Inactivo"]}/>
      </div>
      <div className="row row--end mt-20 gap-10"><Btn variant="secondary" onClick={()=>setModal(null)}>Cancelar</Btn><Btn onClick={save} disabled={saving}>{saving?<><RefreshCw size={13} className="spin"/>Guardando…</>:<><Check size={14}/>Guardar</>}</Btn></div>
    </Modal>}

    {rolModal&&<Modal title={rolModal==="new"?"Nuevo Rol":"Editar Rol"} onClose={()=>setRolModal(null)}>
      <div className="flex-col gap-14">
        <FInput label="Nombre del rol" value={rolForm.nombre||""} onChange={v=>rf("nombre",v)} required error={rolErrs.nombre} disabled={rolModal==="edit"&&!!rolForm.es_sistema} placeholder="Ej: Contabilidad, Supervisor…"/>
        <FInput label="Descripción (opcional)" value={rolForm.descripcion||""} onChange={v=>rf("descripcion",v)} placeholder="Para qué se usa este rol"/>
        {rolModal==="edit"&&<FSelect label="Estado" value={rolForm.activo?"Activo":"Inactivo"} onChange={v=>rf("activo",v==="Activo")} options={["Activo","Inactivo"]}/>}
      </div>
      <div className="row row--end mt-20 gap-10"><Btn variant="secondary" onClick={()=>setRolModal(null)}>Cancelar</Btn><Btn onClick={saveRol} disabled={savingRol}>{savingRol?<><RefreshCw size={13} className="spin"/>Guardando…</>:<><Check size={14}/>Guardar</>}</Btn></div>
    </Modal>}

    {permRol&&<Modal title={`Permisos — ${permRol.nombre}`} onClose={()=>setPermRol(null)} width={640}>
      <div style={{display:"flex",flexDirection:"column",gap:12,maxHeight:"55vh",overflowY:"auto",paddingRight:4}}>
        {Object.entries(permisosCatalog).map(([modulo,perms])=>{
          const ids=perms.map(p=>p.id);
          const allSel=ids.length>0&&ids.every(id=>permSelected.has(id));
          return<div key={modulo} style={{border:'1px solid var(--border)',borderRadius:8,padding:"10px 12px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <b style={{fontSize:12,textTransform:"uppercase",letterSpacing:"0.04em",color:'var(--text)'}}>{modulo}</b>
              <button onClick={()=>toggleAllModulo(modulo)} style={{fontSize:11,color:'var(--accent)',background:"none",border:"none",cursor:"pointer"}}>{allSel?"Ninguno":"Todos"}</button>
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:12}}>
              {perms.map(p=><label key={p.id} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:'var(--sub)',cursor:"pointer"}}>
                <input type="checkbox" checked={permSelected.has(p.id)} onChange={()=>togglePermiso(p.id)}/>
                {p.label}
              </label>)}
            </div>
          </div>;
        })}
      </div>
      <div className="row row--end mt-20 gap-10"><Btn variant="secondary" onClick={()=>setPermRol(null)}>Cancelar</Btn><Btn onClick={savePermisos} disabled={savingPerm}>{savingPerm?<><RefreshCw size={13} className="spin"/>Guardando…</>:<><Check size={14}/>Guardar permisos</>}</Btn></div>
    </Modal>}

    {dupRol&&<Modal title={`Duplicar rol: ${dupRol.nombre}`} onClose={()=>setDupRol(null)} width={460}>
      <FInput label="Nombre del nuevo rol" value={dupNombre} onChange={setDupNombre} required/>
      <div className="row row--end mt-20 gap-10"><Btn variant="secondary" onClick={()=>setDupRol(null)}>Cancelar</Btn><Btn onClick={doDuplicar} disabled={savingDup}>{savingDup?<><RefreshCw size={13} className="spin"/>Duplicando…</>:<><Copy size={13}/>Duplicar</>}</Btn></div>
    </Modal>}

    {delRol&&<Modal title={`Eliminar rol: ${delRol.nombre}`} onClose={()=>setDelRol(null)} width={460}>
      <div className="alert alert--danger"><AlertTriangle size={13}/>Esta acción no se puede deshacer. Los usuarios con este rol quedarán sin rol asignado.</div>
      <div className="row row--end mt-20 gap-10"><Btn variant="secondary" onClick={()=>setDelRol(null)}>Cancelar</Btn><Btn variant="danger" onClick={doDeleteRol} disabled={deletingRol}>{deletingRol?"Eliminando…":<><X size={13}/>Eliminar</>}</Btn></div>
    </Modal>}
  </div>;
}

/* ── SETTINGS ───────────────────────────────────────────────────────── */
function SettingsModule({data,refresh,addToast,effectiveTheme,onThemeChange}){
  const[form,setForm]=useState({...data.settings});
  // Si el tema cambia desde otro lugar (switch de la topbar, u otra pestaña/
  // sesión) mientras Configuración está abierta, el selector de aquí se
  // mantiene sincronizado en vez de mostrar un valor viejo.
  useEffect(()=>{
    if(effectiveTheme&&effectiveTheme!==form.theme)setForm(p=>({...p,theme:effectiveTheme}));
  },[effectiveTheme]);
  const[logoPreview,setLogoPreview]=useState(data.settings.logo||"");
  const[savingLogo,setSavingLogo]=useState(false);
  const[saving,setSaving]=useState(false);
  const[tab,setTab]=useState("general");
  const[bancos,setBancos]=useState(data.settings.bancos||[]);
  const[savingBancos,setSavingBancos]=useState(false);
  const[newBanco,setNewBanco]=useState({banco:"",cuenta:"",moneda:"USD"});
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));

  // Previsualiza el tema/colores en tiempo real sin guardar aún
  const previewTheme=(k,v)=>{
    const next={...form,[k]:v};
    f(k,v);
    applyTheme(next);
  };

  // Cambiar el modo claro/oscuro aquí usa el mismo mecanismo que el switch
  // de la topbar (onThemeChange) — así ambos quedan siempre sincronizados.
  const chooseTheme=t=>{
    previewTheme("theme",t);
    onThemeChange&&onThemeChange(t);
  };

  const save=async()=>{
    setSaving(true);
    try{
      await API.settings.update({
        ...form,
        exchangeRate:+form.exchangeRate,minLbs:+form.minLbs,minLbsPerPackage:+form.minLbsPerPackage,
        rateAir:+form.rateAir,rateSea:+form.rateSea,
        sessionTimeout:+form.sessionTimeout,maxAttempts:+form.maxAttempts,
      });
      addToast("Configuración guardada","success");
      await refresh();
    }catch(e){addToast(e.message,"error");}
    finally{setSaving(false);}
  };

  const handleLogoFile=e=>{
    const file=e.target.files[0];
    if(!file)return;
    if(!['image/png','image/jpeg','image/jpg'].includes(file.type)){addToast("Solo PNG o JPG","error");return;}
    if(file.size>512*1024){addToast(`Archivo demasiado grande (${Math.round(file.size/1024)}KB). Máximo 512KB.`,"error");return;}
    const reader=new FileReader();
    reader.onload=ev=>setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const uploadLogo=async()=>{
    if(!logoPreview||logoPreview===data.settings.logo){addToast("Selecciona una imagen primero","warn");return;}
    setSavingLogo(true);
    try{
      await API.settings.uploadLogo(logoPreview);
      addToast("Logo actualizado","success");
      await refresh();
    }catch(e){addToast(e.message,"error");}
    finally{setSavingLogo(false);}
  };

  const restoreLogo=async()=>{
    setSavingLogo(true);
    try{
      await API.settings.deleteLogo();
      setLogoPreview("");
      addToast("Logo restaurado al original","info");
      await refresh();
    }catch(e){addToast(e.message,"error");}
    finally{setSavingLogo(false);}
  };

  const resetColors=()=>{
    const defaults={colorAccent:"#3B9EE0",colorPrimary:"#020d1b",colorSecondary:"#0d1f35",theme:"light"};
    setForm(p=>({...p,...defaults}));
    applyTheme({...form,...defaults});
    onThemeChange&&onThemeChange(defaults.theme);
  };

  const saveBancos=async()=>{
    setSavingBancos(true);
    try{
      await API.settings.updateBancos(bancos);
      addToast("Bancos guardados","success");
      await refresh();
    }catch(e){addToast(e.message,"error");}
    finally{setSavingBancos(false);}
  };

  const addBanco=()=>{
    if(!newBanco.banco||!newBanco.cuenta){addToast("Completa banco y número de cuenta","warn");return;}
    setBancos(b=>[...b,{...newBanco,id:Date.now()}]);
    setNewBanco({banco:"",cuenta:"",moneda:"USD"});
  };

  const removeBanco=id=>setBancos(b=>b.filter(x=>x.id!==id));

  const TABS=[{id:"general",l:"General"},{id:"bancos",l:"Bancos"}];

  return<div style={{display:"flex",flexDirection:"column",gap:16}}>
    <PH title="Configuración General" sub="Parametrización del sistema"/>

    {/* ── TABS ── */}
    <div style={{display:"flex",gap:4,borderBottom:`1px solid ${'var(--border)'}`,paddingBottom:0}}>
      {TABS.map(t=><button key={t.id} onClick={()=>setTab(t.id)} style={{padding:"8px 18px",background:"none",border:"none",cursor:"pointer",fontSize:13,fontWeight:tab===t.id?700:400,color:tab===t.id?'var(--accent)':'var(--muted)',borderBottom:tab===t.id?`2px solid ${'var(--accent)'}`:
"2px solid transparent",marginBottom:-1,transition:"all 0.12s"}}>{t.l}</button>)}
    </div>

  {tab==="bancos"&&<div style={{display:"flex",flexDirection:"column",gap:16}}>
    <div className="card">
      <h3 style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:4,display:"flex",gap:8,alignItems:"center"}}><CreditCard size={15} color={'var(--accent)'}/>Información Bancaria</h3>
      <p style={{fontSize:12,color:'var(--muted)',marginBottom:16}}>Esta información aparece en las Órdenes de Pago para que el cliente sepa dónde depositar.</p>

      {/* Lista de bancos */}
      {bancos.length>0&&<div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
        {bancos.map((b,i)=><div key={b.id||i} style={{display:"flex",alignItems:"center",gap:10,background:'var(--card2)',borderRadius:8,padding:"10px 14px",border:`1px solid ${'var(--border)'}`}}>
          <CreditCard size={14} color={'var(--accent)'}/>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:'var(--text)'}}>{b.banco}</div>
            <div style={{fontSize:12,color:'var(--muted)'}}>Cta: <span style={{fontFamily:"monospace",color:'var(--text)'}}>{b.cuenta}</span> · <span style={{color:'var(--accent)',fontWeight:600}}>{b.moneda}</span></div>
          </div>
          <button onClick={()=>removeBanco(b.id||i)} style={{background:"none",border:"none",cursor:"pointer",color:'var(--danger)',padding:4,borderRadius:5,display:"flex",alignItems:"center"}}><X size={15}/></button>
        </div>)}
      </div>}

      {/* Formulario nuevo banco */}
      <div style={{display:"grid",gridTemplateColumns:"2fr 2fr 1fr auto",gap:10,alignItems:"flex-end",background:'var(--card2)',borderRadius:8,padding:14,border:`1px dashed ${'var(--border)'}`}}>
        <FInput label="Nombre del banco" value={newBanco.banco} onChange={v=>setNewBanco(p=>({...p,banco:v}))} placeholder="Ej: BDF, Banpro, BAC"/>
        <FInput label="Número de cuenta" value={newBanco.cuenta} onChange={v=>setNewBanco(p=>({...p,cuenta:v}))} placeholder="000-000000-0"/>
        <div className="field">
          <label className="ctrl-label">Moneda</label>
          <select value={newBanco.moneda} onChange={e=>setNewBanco(p=>({...p,moneda:e.target.value}))} className="ctrl-input">
            <option>USD</option>
            <option>NIO</option>
          </select>
        </div>
        <Btn onClick={addBanco} style={{marginBottom:0}}><Plus size={14}/>Agregar</Btn>
      </div>
    </div>
    <div className="row row--end">
      <Btn onClick={saveBancos} size="lg" disabled={savingBancos} style={{background:'var(--accent)'}}>
        {savingBancos?<><RefreshCw size={15} className="spin"/>Guardando…</>:<><Check size={15}/>Guardar bancos</>}
      </Btn>
    </div>
  </div>}

  {tab==="general"&&<>

    {/* ── LOGO CORPORATIVO ── */}
    <div className="card">
      <h3 style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:16,display:"flex",gap:8,alignItems:"center"}}><Building2 size={15} color={'var(--accent)'}/>Logo Corporativo</h3>
      <div style={{display:"flex",gap:24,alignItems:"flex-start",flexWrap:"wrap"}}>
        <div style={{display:"flex",flexDirection:"column",gap:8,alignItems:"center"}}>
          <div style={{width:140,height:80,borderRadius:10,border:`1px solid ${'var(--border)'}`,background:'var(--card2)',display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
            <img src={logoPreview||LOGO} alt="Logo preview" style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
          </div>
          <span style={{fontSize:11,color:'var(--muted)'}}>Vista previa</span>
        </div>
        <div style={{flex:1,display:"flex",flexDirection:"column",gap:10}}>
          <label style={{display:"flex",flexDirection:"column",gap:6}}>
            <span className="ctrl-label">Subir nuevo logo (PNG o JPG, máx. 512KB)</span>
            <input type="file" accept="image/png,image/jpeg" onChange={handleLogoFile} style={{fontSize:13,color:'var(--sub)',background:'var(--input)',border:`1px solid ${'var(--border)'}`,borderRadius:6,padding:"6px 10px",cursor:"pointer"}}/>
          </label>
          <div style={{display:"flex",gap:8}}>
            <Btn onClick={uploadLogo} disabled={savingLogo||!logoPreview||logoPreview===data.settings.logo}>
              {savingLogo?<><RefreshCw size={13} className="spin"/>Subiendo…</>:<><Check size={13}/>Guardar logo</>}
            </Btn>
            {(data.settings.logo||logoPreview)&&<Btn variant="danger" onClick={restoreLogo} disabled={savingLogo}><X size={13}/>Restaurar original</Btn>}
          </div>
          <p style={{fontSize:11,color:'var(--muted)'}}>El logo se aplica en login, dashboard, facturas, órdenes y reportes.</p>
        </div>
      </div>
    </div>

    {/* ── TEMA VISUAL ── */}
    <div className="card">
      <h3 style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:16,display:"flex",gap:8,alignItems:"center"}}><Settings size={15} color={'var(--accent)'}/>Tema y Paleta Corporativa</h3>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        <div>
          <p className="ctrl-label" style={{marginBottom:10}}>Modo visual</p>
          <div style={{display:"flex",gap:8}}>
            {["dark","light"].map(t=><button key={t} onClick={()=>chooseTheme(t)} style={{flex:1,padding:"10px 0",borderRadius:8,border:`2px solid ${form.theme===t?'var(--accent)':'var(--border)'}`,background:form.theme===t?'var(--accent-dim)':"transparent",color:form.theme===t?'var(--accent)':'var(--muted)',fontWeight:700,fontSize:13,cursor:"pointer",transition:"all 0.15s"}}>
              {t==="dark"?"🌙 Oscuro":"☀️ Claro"}
            </button>)}
          </div>
        </div>
        <div>
          <p className="ctrl-label" style={{marginBottom:10}}>Color de acento</p>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <input type="color" value={form.colorAccent||"#3B9EE0"} onChange={e=>previewTheme("colorAccent",e.target.value)}
              style={{width:44,height:36,border:"none",borderRadius:6,cursor:"pointer",background:"none",padding:0}}/>
            <input value={form.colorAccent||"#3B9EE0"} onChange={e=>{if(/^#[0-9a-fA-F]{6}$/.test(e.target.value))previewTheme("colorAccent",e.target.value);else f("colorAccent",e.target.value);}}
              className="ctrl-input" style={{flex:1,fontFamily:"monospace",fontSize:13}}/>
          </div>
        </div>
        <div>
          <p className="ctrl-label" style={{marginBottom:10}}>Color de fondo <span style={{fontSize:11,color:'var(--muted)'}}>(modo oscuro)</span></p>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <input type="color" value={form.colorPrimary||"#020d1b"} onChange={e=>previewTheme("colorPrimary",e.target.value)}
              style={{width:44,height:36,border:"none",borderRadius:6,cursor:"pointer",background:"none",padding:0}}/>
            <input value={form.colorPrimary||"#020d1b"} onChange={e=>{if(/^#[0-9a-fA-F]{6}$/.test(e.target.value))previewTheme("colorPrimary",e.target.value);else f("colorPrimary",e.target.value);}}
              className="ctrl-input" style={{flex:1,fontFamily:"monospace",fontSize:13}}/>
          </div>
        </div>
        <div>
          <p className="ctrl-label" style={{marginBottom:10}}>Color de tarjetas <span style={{fontSize:11,color:'var(--muted)'}}>(modo oscuro)</span></p>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <input type="color" value={form.colorSecondary||"#0d1f35"} onChange={e=>previewTheme("colorSecondary",e.target.value)}
              style={{width:44,height:36,border:"none",borderRadius:6,cursor:"pointer",background:"none",padding:0}}/>
            <input value={form.colorSecondary||"#0d1f35"} onChange={e=>{if(/^#[0-9a-fA-F]{6}$/.test(e.target.value))previewTheme("colorSecondary",e.target.value);else f("colorSecondary",e.target.value);}}
              className="ctrl-input" style={{flex:1,fontFamily:"monospace",fontSize:13}}/>
          </div>
        </div>
      </div>
      <div style={{marginTop:12,display:"flex",gap:8,alignItems:"center"}}>
        <button onClick={resetColors} style={{fontSize:12,color:'var(--muted)',background:"none",border:`1px solid ${'var(--border)'}`,borderRadius:6,padding:"5px 12px",cursor:"pointer"}}>↩ Restaurar colores por defecto</button>
        <span style={{fontSize:11,color:'var(--muted)'}}>Los cambios se aplican en tiempo real — guarda para persistirlos.</span>
      </div>
    </div>

    {/* ── DATOS EMPRESA + OPERATIVOS ── */}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
      <div className="card"><h3 style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:14,display:"flex",gap:8,alignItems:"center"}}><Building2 size={15} color={'var(--accent)'}/>Datos de la empresa</h3>
        <div style={{display:"flex",flexDirection:"column",gap:11}}>
          <FInput label="Nombre de la empresa" value={form.companyName||""} onChange={v=>f("companyName",v)}/>
          <FInput label="RUC / Número fiscal" value={form.ruc||""} onChange={v=>f("ruc",v)}/>
          <FInput label="Dirección física" value={form.address||""} onChange={v=>f("address",v)}/>
          <FInput label="WhatsApp empresarial" value={form.whatsapp||""} onChange={v=>f("whatsapp",v)} placeholder="+505 8888-7777"/>
          <FInput label="Correo electrónico" value={form.email||""} onChange={v=>f("email",v)} type="email"/>
        </div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div className="card"><h3 style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:12,display:"flex",gap:8,alignItems:"center"}}><DollarSign size={15} color={'var(--accent)'}/>Parámetros operativos</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:11}}>
            <FInput label="Tipo de cambio (C$/$)" value={String(form.exchangeRate||"")} onChange={v=>f("exchangeRate",v)} type="number"/>
            <FInput label="Libra mínima por factura" value={String(form.minLbs||"")} onChange={v=>f("minLbs",v)} type="number"/>
            <FInput label="Libra mínima por paquete" value={String(form.minLbsPerPackage||"")} onChange={v=>f("minLbsPerPackage",v)} type="number"/>
            <FInput label="Tarifa aérea base ($/lb)" value={String(form.rateAir||"")} onChange={v=>f("rateAir",v)} type="number"/>
            <FInput label="Tarifa marítima base ($/lb)" value={String(form.rateSea||"")} onChange={v=>f("rateSea",v)} type="number"/>
            <FInput label="Prefijo paquetes" value={form.pkgPfx||""} onChange={v=>f("pkgPfx",v)} placeholder="PK-"/>
            <FInput label="Prefijo recibos" value={form.invPfx||""} onChange={v=>f("invPfx",v)} placeholder="REC-"/>
          </div>
        </div>
        <div className="card"><h3 style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:12,display:"flex",gap:8,alignItems:"center"}}><Lock size={15} color={'var(--accent)'}/>Seguridad de sesión</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:11}}>
            <FInput label="Timeout inactividad (min)" value={String(form.sessionTimeout||"")} onChange={v=>f("sessionTimeout",v)} type="number"/>
            <FInput label="Intentos máximos de login" value={String(form.maxAttempts||"")} onChange={v=>f("maxAttempts",v)} type="number"/>
          </div>
        </div>
        <div style={{background:'var(--accent-dim)',border:`1px solid ${'var(--accent-border)'}`,borderRadius:8,padding:"10px 14px",fontSize:12,color:'var(--sub)'}}>💡 Cambiar tarifas base no afecta facturas históricas ni tarifas personalizadas de clientes existentes.</div>
      </div>
    </div>

    <div className="row row--end">
      <Btn onClick={save} size="lg" disabled={saving} style={{background:'var(--accent)'}}>
        {saving?<><RefreshCw size={15} className="spin"/>Guardando…</>:<><Check size={15}/>Guardar configuración</>}
      </Btn>
    </div>
  </>}
  </div>;
}

/* ── GLOBAL SEARCH ──────────────────────────────────────────────────── */
function GlobalSearch({data,onClose,onNav,onNavTrackPkg}){
  const[q,setQ]=useState("");
  const res=q.length<2?{c:[],p:[],i:[]}:{c:data.clients.filter(c=>`${c.name} ${c.lastName} ${c.whatsapp} ${c.code}`.toLowerCase().includes(q.toLowerCase())).slice(0,4),p:data.packages.filter(p=>`${p.code} ${p.tracking||""} ${p.desc}`.toLowerCase().includes(q.toLowerCase())).slice(0,4),i:data.invoices.filter(i=>i.number.toLowerCase().includes(q.toLowerCase())).slice(0,3)};
  const has=res.c.length||res.p.length||res.i.length;
  const RI=({icon:Icon,title,sub,badge,color,onClick})=><div onClick={onClick} style={{display:'flex',gap:12,alignItems:'center',padding:"9px 10px",borderRadius:8,cursor:"pointer",gap:10}} onMouseEnter={e=>e.currentTarget.style.background='var(--card2)'} onMouseLeave={e=>e.currentTarget.style.background="transparent"}><Icon size={15} color={color}/><div style={{flex:1}}><div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>{title}</div><div style={{fontSize:11,color:'var(--muted)'}}>{sub}</div></div>{badge&&<Badge v={badge}/>}</div>;
  const SH=({t})=><div style={{fontSize:10,color:'var(--muted)',fontWeight:700,padding:"8px 10px 3px",textTransform:"uppercase",letterSpacing:"0.08em"}}>{t}</div>;
  return<div style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.85)",display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:70,backdropFilter:"blur(5px)"}}>
    <div style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:'20px',width:"100%",maxWidth:560}}>
      <div style={{display:'flex',gap:12,alignItems:'center',marginBottom:14,gap:10}}><Search size={17} color={'var(--accent)'}/><input autoFocus value={q} onChange={e=>setQ(e.target.value)} placeholder="Buscar clientes, paquetes, facturas…" style={{background:'var(--input)',border:'1px solid var(--border)',borderRadius:8,padding:'8px 12px',color:'var(--text)',fontSize:14,width:'100%',outline:'none',flex:1,fontSize:15,background:"transparent",border:"none",outline:"none"}}/><button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:'var(--muted)',padding:3,borderRadius:5}}><X size={17}/></button></div>
      {q.length<2?<p style={{color:'var(--muted)',fontSize:13,textAlign:"center",padding:"16px 0"}}>Escribe para buscar en todo el sistema</p>:has?<div>
        {res.c.length>0&&<><SH t="Clientes"/>{res.c.map(c=><RI key={c.id} icon={User} title={`${c.name} ${c.lastName}`} sub={`${c.code} · 📱 ${c.whatsapp}`} badge={c.label} color={'var(--info)'} onClick={()=>{onNav("clients");onClose();}}/>)}</>}
        {res.p.length>0&&<><SH t="Paquetes"/>{res.p.map(p=>{const cli=data.clients.find(c=>c.id===p.clientId);return<RI key={p.id} icon={Package} title={p.code} sub={`${cli?cli.name+" "+cli.lastName:""} · ${fmtLbs(p.weight)}`} badge={p.status} color={'var(--accent)'} onClick={()=>{onNavTrackPkg(p.id);onClose();}}/>})}</>}
        {res.i.length>0&&<><SH t="Recibos"/>{res.i.map(i=><RI key={i.id} icon={FileText} title={i.number} sub={`${fmtUsd(i.totalUsd)} · ${fmtDate(i.fecha)}`} badge={i.status} color={'var(--success)'} onClick={()=>{onNav("billing");onClose();}}/>)}</>}
      </div>:<p style={{color:'var(--muted)',fontSize:13,textAlign:"center",padding:"16px 0"}}>Sin resultados para "{q}"</p>}
    </div>
  </div>;
}

/* ── SIDEBAR ────────────────────────────────────────────────────────── */
/* ── MÓDULO ÓRDENES DE PAGO ─────────────────────────────────────────── */
function OrdenesModule({data,refresh,addToast,currentUser}){
  const[ords,setOrds]=useState([]);
  const[loading,setLoading]=useState(false);
  const[genPDF,setGenPDF]=useState(false);
  const[view,setView]=useState("list"); // list | detail | new | edit
  const[selOrd,setSelOrd]=useState(null);
  const[modal,setModal]=useState(null); // cancel | convert
  const[form,setForm]=useState({});
  const[errs,setErrs]=useState({});
  const[q,setQ]=useState("");const[fSt,setFSt]=useState("");
  const f=useCallback((k,v)=>setForm(p=>({...p,[k]:v})),[]);

  const loadOrds=useCallback(async()=>{
    setLoading(true);
    try{const r=await API.ordenes.getAll();setOrds(r);}
    catch(e){addToast("Error cargando órdenes: "+e.message,"error");}
    finally{setLoading(false);}
  },[addToast]);

  useEffect(()=>{loadOrds();},[loadOrds]);

  const today=()=>new Date().toISOString().slice(0,10);
  const fmtUsd2=v=>"$"+parseFloat(v||0).toFixed(2);
  const fmtNio=v=>"C$"+parseFloat(v||0).toFixed(2);

  // Paquetes disponibles para OP (estado Registrado, sin OP pendiente activa)
  const pkgsDisp=useMemo(()=>{
    const inOrds=new Set(ords.filter(o=>o.status==="Pendiente").flatMap(o=>o.pkgIds||[]));
    return data.packages.filter(p=>p.status==="Registrado"&&!inOrds.has(p.id));
  },[data.packages,ords]);

  // Calcular totales del form — delega en calcInv (misma lógica que Recibos, incluye libra mínima opcional)
  const calcTotals=useCallback((sel,discType,discVal,exRate,weightMode)=>{
    const cliId=form.clientId;
    const cli=data.clients.find(c=>c.id===parseInt(cliId));
    const details=sel.map(p=>{
      const rate=p.type==="Aéreo"?parseFloat(cli?.rateAir||data.settings.rateAir):parseFloat(cli?.rateSea||data.settings.rateSea);
      return{pkgId:p.id,code:p.code,tracking:p.tracking,type:p.type,weight:+p.weight,rate,subtotal:+p.weight*rate};
    });
    const agg=calcInv(details,data.settings.minLbs,discType,discVal,parseFloat(exRate||data.settings.exchangeRate),weightMode==="factura",weightMode==="paquete"?data.settings.minLbsPerPackage:null);
    return{details,totalWeight:agg.totalW,billedWeight:agg.billedW,subtotalUsd:agg.sub,discAmt:agg.discAmt,totalUsd:agg.totalUsd,totalNio:agg.totalNio};
  },[form.clientId,data.clients,data.settings]);

  const openNew=()=>{
    setForm({clientId:"",sel:[],discType:"",discVal:"",exRate:String(data.settings.exchangeRate),notes:"",fecha:today(),weightMode:"factura"});
    setErrs({});setView("new");
  };

  const openEdit=(ord)=>{
    const cli=data.clients.find(c=>c.id===ord.clientId);
    const selPkgs=data.packages.filter(p=>ord.pkgIds.includes(p.id));
    setSelOrd(ord);
    setForm({clientId:String(ord.clientId),sel:selPkgs,
      discType:ord.discType||"",discVal:ord.discVal||"",
      exRate:String(ord.exRate),notes:ord.notes||"",
      fecha:ord.fecha||today(),weightMode:"factura"});
    setErrs({});setView("edit");
  };

  const clientPkgs=useMemo(()=>{
    if(!form.clientId)return[];
    const inOrds=new Set(ords.filter(o=>o.status==="Pendiente"&&(!selOrd||o.id!==selOrd.id)).flatMap(o=>o.pkgIds||[]));
    return data.packages.filter(p=>p.status==="Registrado"&&p.clientId===parseInt(form.clientId)&&!inOrds.has(p.id));
  },[form.clientId,data.packages,ords,selOrd]);

  const totals=useMemo(()=>{
    if(!(form.sel||[]).length)return{details:[],totalWeight:0,billedWeight:0,subtotalUsd:0,discAmt:0,totalUsd:0,totalNio:0};
    return calcTotals(form.sel,form.discType,form.discVal,form.exRate,form.weightMode);
  },[form.sel,form.discType,form.discVal,form.exRate,form.weightMode,calcTotals]);

  const togglePkg=(p)=>{
    const cur=form.sel||[];
    if(cur.find(s=>s.id===p.id))setForm(pr=>({...pr,sel:cur.filter(s=>s.id!==p.id)}));
    else setForm(pr=>({...pr,sel:[...cur,p]}));
  };

  const toggleSelectAll=()=>{
    setForm(pr=>({...pr,sel:(pr.sel||[]).length===clientPkgs.length?[]:clientPkgs}));
  };

  const validate=()=>{
    const e={};
    if(!form.clientId)e.clientId="Seleccione un cliente";
    if(!(form.sel||[]).length)e.sel="Seleccione al menos un paquete";
    if(!form.fecha||isNaN(Date.parse(form.fecha)))e.fecha="Fecha inválida";
    setErrs(e);return!Object.keys(e).length;
  };

  const handleSave=async()=>{
    if(!validate())return;
    setLoading(true);
    try{
      const body={clientId:parseInt(form.clientId),details:totals.details,
        totalWeight:totals.totalWeight,billedWeight:totals.billedWeight,subtotalUsd:totals.subtotalUsd,
        discType:form.discType||null,discVal:parseFloat(form.discVal)||0,
        discAmt:totals.discAmt,totalUsd:totals.totalUsd,
        exRate:parseFloat(form.exRate),totalNio:totals.totalNio,
        notes:form.notes||"",fecha:form.fecha,
        minLbsPerPackageUsed:form.weightMode==="paquete"?data.settings.minLbsPerPackage:null};
      let ord;
      if(view==="edit")ord=await API.ordenes.update(selOrd.id,body);
      else ord=await API.ordenes.create(body);
      addToast(`Orden ${ord.number} ${view==="edit"?"actualizada":"creada"} correctamente`,"success");
      await loadOrds();await refresh();
      setSelOrd(ord);setView("detail");
    }catch(e){addToast(e.message,"error");}
    finally{setLoading(false);}
  };

  const handleCancel=async()=>{
    if(!form.reason?.trim()){setErrs({reason:"Motivo requerido"});return;}
    setLoading(true);
    try{
      const ord=await API.ordenes.cancelar(selOrd.id,form.reason);
      addToast(`Orden ${ord.number} cancelada`,"warn");
      await loadOrds();setSelOrd(ord);setModal(null);setForm({});
    }catch(e){addToast(e.message,"error");}
    finally{setLoading(false);}
  };

  const handleConvert=async()=>{
    const e={};
    const ordFecha=(ords.find(o=>o.id===selOrd.id)||selOrd).fecha;
    if(!form.fecha)e.fecha="Seleccione la fecha del recibo";
    else if(form.fecha>today())e.fecha="La fecha no puede ser futura";
    else if(ordFecha&&form.fecha<ordFecha)e.fecha=`No puede ser anterior a la fecha de la Orden (${fmtDate(ordFecha)})`;
    if(!form.payMethod)e.payMethod="Seleccione método de pago";
    if(Object.keys(e).length){setErrs(e);return;}
    setLoading(true);
    try{
      const{orden,facturaNumero}=await API.ordenes.convertir(selOrd.id,{payMethod:form.payMethod,reference:form.reference||"",fecha:form.fecha});
      addToast(`✅ Orden convertida a Factura ${facturaNumero}`,"success");
      await loadOrds();await refresh();
      setSelOrd(orden);setModal(null);setForm({});
    }catch(e){addToast(e.message,"error");}
    finally{setLoading(false);}
  };

  const handleWA=async(ord)=>{
    const cli=data.clients.find(c=>c.id===ord.clientId);
    await generateOrdenPDFAndWhatsApp(ord,cli,data.settings,addToast,setGenPDF);
  };
  const handlePrint=(ord)=>{
    const cli=data.clients.find(c=>c.id===ord.clientId);
    const w=window.open("","_blank","width=900,height=1050");
    w.document.write(buildOrdenHTML(ord,cli,data.settings));
    w.document.close();w.focus();setTimeout(()=>w.print(),700);
  };

  const ST={Pendiente:{bg:"#fef3c7",c:"#92400e"},Facturada:{bg:"#d1fae5",c:"#065f46"},Cancelada:{bg:"#fee2e2",c:"#991b1b"}};

  const filtered=ords.filter(o=>{
    const cli=data.clients.find(c=>c.id===o.clientId);
    return`${o.number} ${cli?cli.name+" "+cli.lastName:""} ${o.status}`.toLowerCase().includes(q.toLowerCase())&&(!fSt||o.status===fSt);
  });
  const pgO=usePagination(filtered);

  // ── Vista lista ──
  if(view==="list")return<div>
    <PH title="Órdenes de Pago" sub={`${ords.length} órdenes · ${ords.filter(o=>o.status==="Pendiente").length} pendientes`}
      action={can(currentUser,"ordenes:crear")&&<Btn onClick={openNew}><Plus size={14}/>Nueva Orden</Btn>}/>
    <div style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:"10px 14px",marginBottom:16}}>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        <input value={q} onChange={e=>{setQ(e.target.value);pgO.goPage(1);}} placeholder="Buscar por número o cliente…" style={{background:'var(--input)',border:'1px solid var(--border)',borderRadius:8,padding:'8px 12px',color:'var(--text)',fontSize:13,width:'100%',outline:'none'}}/>
        <FilterChips opts={["Pendiente","Facturada","Cancelada"]} val={fSt} set={v=>{setFSt(v);pgO.goPage(1);}}/>
      </div>
    </div>
    {loading?<div style={{textAlign:"center",padding:40,color:'var(--muted)'}}>Cargando…</div>:filtered.length===0?<div style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:40,textAlign:"center",color:'var(--muted)'}}>No hay órdenes</div>:
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {pgO.paged.map(ord=>{
        const cli=data.clients.find(c=>c.id===ord.clientId);
        const st=ST[ord.status]||{bg:'var(--slate-dim)',c:'var(--sub)'};
        return<div key={ord.id} style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:'20px',padding:"14px 18px",cursor:"pointer",transition:"box-shadow 0.15s"}}
          onClick={()=>{setSelOrd(ord);setView("detail");}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
            <div style={{display:"flex",alignItems:"center",gap:14}}>
              <code style={{color:'var(--accent)',fontWeight:700,fontSize:14}}>{ord.number}</code>
              <span style={{background:st.bg,color:st.c,fontSize:11,fontWeight:700,padding:"2px 10px",borderRadius:20}}>{ord.status}</span>
              {ord.facturaNumber&&<span style={{fontSize:11,color:'var(--success)'}}>→ {ord.facturaNumber}</span>}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <b style={{color:'var(--accent)',fontSize:15}}>${parseFloat(ord.totalUsd).toFixed(2)}</b>
              <span style={{fontSize:11,color:'var(--muted)'}}>{fmtDate(ord.fecha)}</span>
            </div>
          </div>
          <div style={{marginTop:6,fontSize:13,color:'var(--sub)'}}>
            {cli?`${cli.name} ${cli.lastName}`:"-"} · {ord.pkgIds.length} paquete(s) · {parseFloat(ord.totalWeight).toFixed(2)} lb
          </div>
        </div>;
      })}
    </div>}
    {!loading&&filtered.length>0&&<div style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:"0 14px",marginTop:8}}><Pagination {...pgO}/></div>}
  </div>;

  // ── Vista detalle ──
  if(view==="detail"&&selOrd){
    const cli=data.clients.find(c=>c.id===selOrd.clientId);
    const ord=ords.find(o=>o.id===selOrd.id)||selOrd;
    const st=ST[ord.status]||{bg:'var(--slate-dim)',c:'var(--sub)'};
    return<div>
      <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:"wrap",marginBottom:20}}>
        <Btn variant="ghost" onClick={()=>{setSelOrd(null);setView("list");}} style={{color:'var(--muted)'}}><ChevronRight size={14} style={{transform:"rotate(180deg)"}}/>Órdenes</Btn>
        <code style={{color:'var(--accent)',fontWeight:700,fontSize:16,marginLeft:6}}>{ord.number}</code>
        <span style={{background:st.bg,color:st.c,fontSize:11,fontWeight:700,padding:"3px 12px",borderRadius:20}}>{ord.status}</span>
        <span style={{fontSize:12,color:'var(--muted)'}}>{fmtDate(ord.fecha)}</span>
        <div style={{flex:"1 0 auto",minWidth:0}}/>
        {ord.status==="Pendiente"&&<>
          {can(currentUser,"ordenes:editar")&&<Btn variant="secondary" onClick={()=>openEdit(ord)}><Edit2 size={13}/>Editar</Btn>}
          {can(currentUser,"ordenes:cancelar")&&<Btn variant="danger" onClick={()=>{setModal("cancel");setForm({reason:""});setErrs({});}}><X size={13}/>Cancelar</Btn>}
          {can(currentUser,"ordenes:convertir")&&<Btn onClick={()=>{setModal("convert");setForm({payMethod:"Efectivo",reference:"",fecha:today()});setErrs({});}} style={{background:"#059669",color:"#fff"}}><Check size={13}/>Convertir a Recibo</Btn>}
        </>}
        <Btn variant="secondary" onClick={()=>handlePrint(ord)}><Printer size={13}/>Imprimir</Btn>
        <Btn variant="secondary" disabled={genPDF} onClick={()=>handleWA(ord)} style={{color:"#25D366",borderColor:"#25D36633"}}>
          {genPDF?<><RefreshCw size={13} className="spin"/>Generando…</>:<><MessageCircle size={13}/>WhatsApp + PDF</>}
        </Btn>
      </div>
      {cli&&<div style={{background:'var(--accent-dim)',border:`1px solid ${'var(--accent-border)'||'var(--accent)'+"33"}`,borderRadius:8,padding:"10px 14px",marginBottom:16}}>
        <div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>CLIENTE</div>
        <div style={{fontSize:15,fontWeight:700}}>{cli.name} {cli.lastName}</div>
        <div style={{fontSize:12,color:'var(--sub)',marginTop:2}}>📱 {cli.whatsapp}</div>
      </div>}
      <div style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:'20px',marginBottom:12}}>
        <div className="table-wrap">
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:560}}>
          <thead><tr style={{borderBottom:`2px solid ${'var(--border)'}`}}>
            {["Código","Tracking","Tipo","Peso facturado (lb)","Tarifa","Subtotal"].map(h=><th key={h} style={{padding:"8px 12px",fontSize:11,color:'var(--muted)',fontWeight:700,textTransform:"uppercase",textAlign:h.includes("Peso")||h==="Tarifa"||h==="Subtotal"?"right":"left"}}>{h}</th>)}
          </tr></thead>
          <tbody>{billedLineItems(ord).lines.map((d,i)=><tr key={i} style={{borderBottom:`1px solid ${'var(--border)'}`}}>
            <td style={{padding:"9px 12px"}}><code style={{color:'var(--accent)',fontWeight:700}}>{d.code}</code></td>
            <td style={{padding:"9px 12px",fontSize:12,color:'var(--sub)'}}>{d.tracking||"—"}</td>
            <td style={{padding:"9px 12px",fontSize:12}}>{d.type}</td>
            <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700}}>{d.billedWeight.toFixed(2)}</td>
            <td style={{padding:"9px 12px",textAlign:"right",fontSize:12}}>${parseFloat(d.rate).toFixed(2)}</td>
            <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700,color:'var(--accent)'}}>${d.billedSubtotal.toFixed(2)}</td>
          </tr>)}
          {billedLineItems(ord).adjustment&&<tr style={{background:'var(--warn-dim)',borderBottom:`1px solid ${'var(--border)'}`}}><td colSpan={3} style={{padding:"9px 12px",fontSize:12,color:'var(--warn)',fontStyle:"italic"}}>{billedLineItems(ord).adjustment.label}</td><td style={{padding:"9px 12px",textAlign:"right",fontWeight:700,color:'var(--warn)'}}>+{billedLineItems(ord).adjustment.weight.toFixed(2)}</td><td/><td style={{padding:"9px 12px",textAlign:"right",fontWeight:700,color:'var(--warn)'}}>+${billedLineItems(ord).adjustment.amount.toFixed(2)}</td></tr>}
          </tbody>
        </table>
        </div>
        <div style={{borderTop:`1px solid ${'var(--border)'}`,padding:"12px 12px 4px",display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end"}}>
          {ord.discAmt>0&&<div style={{fontSize:13,color:'var(--success)'}}>Descuento: -${parseFloat(ord.discAmt).toFixed(2)}</div>}
          <div style={{fontSize:20,fontWeight:800,color:'var(--accent)'}}>Total: ${parseFloat(ord.totalUsd).toFixed(2)}</div>
          <div style={{fontSize:13,color:'var(--sub)'}}>C${parseFloat(ord.totalNio).toFixed(2)} (T.C. {parseFloat(ord.exRate).toFixed(2)})</div>
        </div>
      </div>
      {ord.notes&&<div style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:'20px',marginBottom:12,fontSize:13,color:'var(--sub)'}}><strong>Nota: </strong>{ord.notes}</div>}
      {ord.facturaNumber&&<div style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:'20px',background:"#d1fae5",border:"1px solid #6ee7b7",fontSize:13,color:"#065f46"}}><strong>✅ Convertida a Factura:</strong> {ord.facturaNumber}</div>}
      {ord.cancelReason&&<div style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:'20px',background:"#fee2e2",border:"1px solid #fca5a5",fontSize:13,color:"#991b1b"}}><strong>❌ Cancelada:</strong> {ord.cancelReason}{ord.cancelBy&&` — por ${ord.cancelBy}`}</div>}

      {/* Modal cancelar */}
      {modal==="cancel"&&<Modal title="Cancelar Orden de Pago" onClose={()=>{setModal(null);setErrs({});}}>
        <p style={{color:'var(--muted)',fontSize:13,marginBottom:16}}>Esta acción cancela la orden <strong>{ord.number}</strong>. Los paquetes quedarán disponibles para nuevo recibo.</p>
        <FInput label="Motivo de cancelación" value={form.reason||""} onChange={v=>f("reason",v)} required error={errs.reason} placeholder="Indique el motivo…"/>
        <div style={{display:'flex',gap:12,alignItems:'center',justifyContent:"flex-end",gap:8,marginTop:20}}>
          <Btn variant="secondary" onClick={()=>{setModal(null);setErrs({});}}>Cerrar</Btn>
          <Btn variant="danger" onClick={handleCancel} disabled={loading}>{loading?"Cancelando…":"Confirmar Cancelación"}</Btn>
        </div>
      </Modal>}

      {/* Modal convertir */}
      {modal==="convert"&&<Modal title="Convertir a Recibo" onClose={()=>{setModal(null);setErrs({});}}>
        <p style={{color:'var(--muted)',fontSize:13,marginBottom:16}}>Se creará el recibo para <strong>{cli?.name} {cli?.lastName}</strong> por <strong>${parseFloat(ord.totalUsd).toFixed(2)}</strong>.</p>
        <FInput label="Fecha del recibo" value={form.fecha||today()} onChange={v=>f("fecha",v)} type="date" min={ord.fecha} max={today()} required error={errs.fecha}/>
        <p className="c-muted fs-11" style={{marginTop:4}}>Debe estar entre la fecha de la Orden de Pago ({fmtDate(ord.fecha)}) y hoy.</p>
        <div style={{marginTop:12}}>
          <FSelect label="Método de pago" value={form.payMethod||"Efectivo"} onChange={v=>f("payMethod",v)} required error={errs.payMethod}
            options={["Efectivo","Transferencia","POS","Tarjeta","Mixto","Otro"]}/>
        </div>
        <div style={{marginTop:12}}>
          <FInput label="Referencia (opcional)" value={form.reference||""} onChange={v=>f("reference",v)} placeholder="No. transferencia, voucher…"/>
        </div>
        <div style={{display:'flex',gap:12,alignItems:'center',justifyContent:"flex-end",gap:8,marginTop:20}}>
          <Btn variant="secondary" onClick={()=>{setModal(null);setErrs({});}}>Cerrar</Btn>
          <Btn onClick={handleConvert} disabled={loading} style={{background:"#059669",color:"#fff"}}>{loading?"Convirtiendo…":"✅ Generar Recibo"}</Btn>
        </div>
      </Modal>}
    </div>;
  }

  // ── Vista nueva / editar ──
  if(view==="new"||view==="edit"){
    const cli=data.clients.find(c=>c.id===parseInt(form.clientId));
    return<div>
      <div style={{display:'flex',gap:12,alignItems:'center',marginBottom:20}}>
        <Btn variant="ghost" onClick={()=>{setView(selOrd?"detail":"list");setErrs({});}} style={{color:'var(--muted)'}}><ChevronRight size={14} style={{transform:"rotate(180deg)"}}/>{selOrd?"Volver":"Órdenes"}</Btn>
        <h2 style={{fontSize:17,fontWeight:700,color:'var(--text)',marginLeft:6}}>{view==="edit"?"Editar Orden":"Nueva Orden de Pago"}</h2>
      </div>
      <div className="grid-2m" style={{marginBottom:16}}>
        <div className="card">
          <h3 style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:14}}>1. Cliente</h3>
          <FSelect label="Seleccionar cliente" value={form.clientId||""} onChange={v=>{f("clientId",v);setForm(p=>({...p,clientId:v,sel:[]}));}} required error={errs.clientId}
            options={sortedActiveClientOptions(data.clients)}/>
          {cli&&<div style={{marginTop:12,padding:"10px 12px",background:'var(--accent-dim)',borderRadius:8,fontSize:12,color:'var(--sub)'}}>
            📱 {cli.whatsapp} · Aéreo: ${cli.rateAir}/lb · Marítimo: ${cli.rateSea}/lb
          </div>}
        </div>
        <div className="card">
          <h3 style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:14}}>3. Descuento y T.C.</h3>
          <div style={{marginBottom:14}}>
            <p className="ctrl-label" style={{marginBottom:8}}>Tipo de cálculo de peso</p>
            <div style={{display:"flex",gap:8}}>
              {[{v:"factura",l:"Peso mínimo por factura"},{v:"real",l:"Peso real"},{v:"paquete",l:"Peso mínimo por paquete"}].map(o=><button key={o.v} onClick={()=>f("weightMode",o.v)} style={{flex:1,padding:"9px 6px",borderRadius:8,border:`2px solid ${form.weightMode===o.v?'var(--accent)':'var(--border)'}`,background:form.weightMode===o.v?'var(--accent-dim)':"transparent",color:form.weightMode===o.v?'var(--accent)':'var(--muted)',fontWeight:700,fontSize:12,cursor:"pointer",transition:"all 0.15s"}}>{o.l}</button>)}
            </div>
          </div>
          <div style={{display:"flex",gap:10,marginBottom:10}}>
            <FSelect label="Tipo descuento" value={form.discType||""} onChange={v=>f("discType",v)} options={[{v:"",l:"Sin descuento"},{v:"porcentaje",l:"Porcentaje %"},{v:"monto",l:"Monto fijo $"}]}/>
            {form.discType&&<FInput label={form.discType==="porcentaje"?"% descuento":"$ descuento"} value={form.discVal||""} onChange={v=>f("discVal",v)} type="number"/>}
          </div>
          <div style={{display:"flex",gap:10,marginBottom:10}}>
            <FInput label="Fecha de la orden" value={form.fecha||""} onChange={v=>f("fecha",v)} type="date" required error={errs.fecha}/>
            <FInput label="Tipo de cambio (C$/$)" value={form.exRate||""} onChange={v=>f("exRate",v)} type="number"/>
          </div>
          <div style={{marginTop:10}}>
            <FInput label="Nota interna (opcional)" value={form.notes||""} onChange={v=>f("notes",v)} placeholder="Observaciones para la orden…"/>
          </div>
        </div>
      </div>
      {form.clientId&&<div style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:'20px',marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
          <h3 style={{fontSize:13,fontWeight:700,color:'var(--text)'}}>2. Seleccionar paquetes {errs.sel&&<span style={{color:'var(--danger)',fontSize:11,marginLeft:8}}>{errs.sel}</span>}</h3>
          {clientPkgs.length>0&&<Btn variant="secondary" size="sm" onClick={toggleSelectAll}>{(form.sel||[]).length===clientPkgs.length?"Deseleccionar todo":"Seleccionar todo"}</Btn>}
        </div>
        {clientPkgs.length===0?<p style={{color:'var(--muted)',fontSize:13}}>No hay paquetes disponibles en estado Registrado para este cliente.</p>:
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {clientPkgs.map(p=>{
            const rate=p.type==="Aéreo"?parseFloat(cli?.rateAir||data.settings.rateAir):parseFloat(cli?.rateSea||data.settings.rateSea);
            const sel=(form.sel||[]).find(s=>s.id===p.id);
            return<div key={p.id} onClick={()=>togglePkg(p)} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderRadius:8,border:`1px solid ${sel?'var(--accent)':'var(--border)'}`,background:sel?'var(--accent-dim)':'var(--card)',cursor:"pointer",transition:"all 0.12s"}}>
              <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${sel?'var(--accent)':'var(--border)'}`,background:sel?'var(--accent)':"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                {sel&&<Check size={11} color="#fff"/>}
              </div>
              <code style={{color:'var(--accent)',fontWeight:700,minWidth:90}}>{p.code}</code>
              <span style={{fontSize:12,color:'var(--sub)',flex:1}}>{p.desc} · {parseFloat(p.weight).toFixed(2)} lb · {p.type}</span>
              <span style={{fontSize:13,fontWeight:700,color:'var(--accent)'}}>${(+p.weight*rate).toFixed(2)}</span>
            </div>;
          })}
        </div>}
      </div>}
      {(form.sel||[]).length>0&&<div style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:'20px',background:'var(--accent-dim)',marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div><span style={{fontSize:13,color:'var(--muted)'}}>{(form.sel||[]).length} paquete(s) · {totals.totalWeight.toFixed(2)} lb</span>{totals.billedWeight>totals.totalWeight&&<span style={{fontSize:12,color:'var(--warn)',marginLeft:12}}>⚠ Mín. facturado: {totals.billedWeight.toFixed(2)} lb</span>}{totals.discAmt>0&&<span style={{fontSize:12,color:'var(--success)',marginLeft:12}}>Descuento: -${totals.discAmt.toFixed(2)}</span>}</div>
          <div style={{textAlign:"right"}}><div style={{fontSize:22,fontWeight:800,color:'var(--accent)'}}>${totals.totalUsd.toFixed(2)}</div><div style={{fontSize:12,color:'var(--sub)'}}>C${totals.totalNio.toFixed(2)}</div></div>
        </div>
      </div>}
      <div style={{display:'flex',gap:12,alignItems:'center',justifyContent:"flex-end",gap:8}}>
        <Btn variant="secondary" onClick={()=>{setView(selOrd?"detail":"list");setErrs({});}}>Cancelar</Btn>
        <Btn onClick={handleSave} disabled={loading}>{loading?"Guardando…":<>{view==="edit"?<><Check size={13}/>Guardar cambios</>:<><Receipt size={13}/>Crear Orden de Pago</>}</>}</Btn>
      </div>
    </div>;
  }

  return null;
}

/* ── GASTOS ─────────────────────────────────────────────────────────── */
const CATS_GASTO=["Impuestos","Alcaldía","Proveedores","Membresías","Suscripciones","Otros"];
const PAY_GASTO=["Efectivo","Transferencia","Tarjeta","POS","Otro"];

function GastosModule({data,refresh,addToast,currentUser}){
  const[modal,setModal]=useState(null); // new | anular
  const[selG,setSelG]=useState(null);
  const[form,setForm]=useState({});
  const[errs,setErrs]=useState({});
  const[saving,setSaving]=useState(false);
  const[q,setQ]=useState("");
  const[fCat,setFCat]=useState("");
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));

  const gastos=data.gastos||[];
  const activos=gastos.filter(g=>g.estado==="Activo");
  const hoy=today();
  const mesAct=hoy.slice(0,7);
  const gastosHoy=activos.filter(g=>g.fecha===hoy).reduce((s,g)=>s+g.montoUsd,0);
  const gastosMes=activos.filter(g=>g.fecha?.slice(0,7)===mesAct).reduce((s,g)=>s+g.montoUsd,0);
  const gastosTotal=activos.reduce((s,g)=>s+g.montoUsd,0);

  const list=gastos.filter(g=>{
    const txt=`${g.numero} ${g.categoria} ${g.proveedor} ${g.descripcion}`.toLowerCase();
    return txt.includes(q.toLowerCase())&&(!fCat||g.categoria===fCat);
  });
  const pgG=usePagination(list);

  const openNew=()=>{
    setForm({fecha:hoy,categoria:"Proveedores",proveedor:"",descripcion:"",montoUsd:"",tipoCambio:String(data.settings.exchangeRate),metodoPago:"Efectivo",referencia:"",notas:""});
    setErrs({});setModal("new");
  };

  const validate=()=>{
    const e={};
    if(!form.fecha)e.fecha="Requerida";
    if(!form.categoria)e.categoria="Requerida";
    if(!form.descripcion?.trim())e.descripcion="Requerida";
    if(!(parseFloat(form.montoUsd)>0))e.montoUsd="Debe ser > 0";
    setErrs(e);return!Object.keys(e).length;
  };

  const handleSave=async()=>{
    if(!validate())return;
    setSaving(true);
    try{
      const g=await API.gastos.create({
        fecha:form.fecha,categoria:form.categoria,proveedor:form.proveedor||"",
        descripcion:form.descripcion,montoUsd:parseFloat(form.montoUsd),
        tipoCambio:parseFloat(form.tipoCambio)||data.settings.exchangeRate,
        metodoPago:form.metodoPago,referencia:form.referencia||"",notas:form.notas||"",
      });
      addToast(`Gasto ${g.numero} registrado`,"success");
      await refresh();setModal(null);
    }catch(e){addToast(e.message,"error");}
    finally{setSaving(false);}
  };

  const handleAnular=async()=>{
    if(!form.reason?.trim()){setErrs({reason:"Motivo requerido"});return;}
    if(!form.adminPwd){setErrs({adminPwd:"Contraseña requerida"});return;}
    setSaving(true);
    try{
      await API.gastos.anular(selG.id,form.reason,form.adminPwd);
      addToast(`Gasto ${selG.numero} anulado`,"info");
      await refresh();setModal(null);setSelG(null);setForm({});
    }catch(e){setErrs({adminPwd:e.message});}
    finally{setSaving(false);}
  };

  const cols=[
    {k:"n",l:"Número",render:r=><code className="c-accent fw-700 fs-12">{r.numero}</code>},
    {k:"f",l:"Fecha",render:r=><span className="fs-12 c-muted">{fmtDate(r.fecha)}</span>},
    {k:"c",l:"Categoría",render:r=><Badge v={r.categoria}/>},
    {k:"p",l:"Proveedor",render:r=><span className="fs-12">{r.proveedor||"—"}</span>},
    {k:"d",l:"Descripción",render:r=><span className="fs-12">{r.descripcion}</span>},
    {k:"m",l:"Monto USD",render:r=><b style={{color:r.estado==="Anulado"?'var(--muted)':'var(--danger)',textDecoration:r.estado==="Anulado"?"line-through":"none"}}>{fmtUsd(r.montoUsd)}</b>},
    {k:"e",l:"Estado",render:r=><Badge v={r.estado}/>},
    {k:"a",l:"",render:r=>r.estado==="Activo"?<Btn variant="ghost" style={{color:'var(--danger)'}} onClick={e=>{e.stopPropagation();setSelG(r);setForm({reason:"",adminPwd:""});setErrs({});setModal("anular");}}><XCircle size={13}/></Btn>:<span className="c-muted fs-11">Anulado</span>},
  ];

  return<div>
    <PH title="Gastos" sub={`${activos.length} gastos activos`} action={can(currentUser,"gastos:crear")&&<Btn onClick={openNew}><Plus size={14}/>Registrar Gasto</Btn>}/>
    <div className="grid-4" style={{marginBottom:16}}>
      <StatCard icon={TrendingDown} title="Gastos hoy" value={fmtUsd(gastosHoy)} sub={`${activos.filter(g=>g.fecha===hoy).length} registros`} color={'var(--danger)'}/>
      <StatCard icon={Calendar} title="Gastos del mes" value={fmtUsd(gastosMes)} sub={mesAct} color={'var(--warn)'}/>
      <StatCard icon={CreditCard} title="Gastos acumulados" value={fmtUsd(gastosTotal)} sub={`${activos.length} registros activos`} color={'var(--violet)'}/>
      <StatCard icon={DollarSign} title="Utilidad bruta" value={fmtUsd(Math.max(0,data.invoices.filter(i=>i.status==="Pagada").reduce((s,i)=>s+i.totalUsd,0)-gastosMes))} sub="Ingresos − Gastos mes" color={'var(--success)'}/>
    </div>
    <div className="card">
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:10,flexWrap:"wrap"}}>
        <SearchBar value={q} onChange={v=>{setQ(v);pgG.goPage(1);}} placeholder="Número, categoría, descripción…"/>
      </div>
      <div style={{marginBottom:14}}><FilterChips opts={CATS_GASTO} val={fCat} set={v=>{setFCat(v);pgG.goPage(1);}}/></div>
      <Table cols={cols} rows={pgG.paged} empty="Sin gastos registrados"/>
      <Pagination {...pgG}/>
    </div>

    {modal==="new"&&<Modal title="Registrar Gasto" onClose={()=>setModal(null)} width={580}>
      <div className="grid-2">
        <FInput label="Fecha" value={form.fecha||""} onChange={v=>f("fecha",v)} type="date" required error={errs.fecha}/>
        <FSelect label="Categoría" value={form.categoria||""} onChange={v=>f("categoria",v)} options={CATS_GASTO} required error={errs.categoria}/>
        <FInput label="Proveedor (opcional)" value={form.proveedor||""} onChange={v=>f("proveedor",v)} placeholder="Nombre del proveedor"/>
        <FSelect label="Método de pago" value={form.metodoPago||"Efectivo"} onChange={v=>f("metodoPago",v)} options={PAY_GASTO}/>
        <FInput label="Monto (USD)" value={form.montoUsd||""} onChange={v=>f("montoUsd",v)} type="number" required error={errs.montoUsd} placeholder="0.00"/>
        <FInput label="Tipo de cambio (C$/$)" value={form.tipoCambio||""} onChange={v=>f("tipoCambio",v)} type="number"/>
        <div className="col-full"><FInput label="Descripción" value={form.descripcion||""} onChange={v=>f("descripcion",v)} required error={errs.descripcion} placeholder="Detalle del gasto…"/></div>
        <FInput label="Referencia (opcional)" value={form.referencia||""} onChange={v=>f("referencia",v)} placeholder="No. factura, voucher…"/>
        <FInput label="Notas (opcional)" value={form.notas||""} onChange={v=>f("notas",v)} placeholder="Observaciones"/>
      </div>
      {form.montoUsd>0&&<div style={{background:'var(--accent-dim)',border:`1px solid ${'var(--border)'}`,borderRadius:8,padding:"10px 14px",marginTop:12,fontSize:13,color:'var(--sub)'}}>
        Total en córdobas: <b style={{color:'var(--text)'}}>{fmtNio(parseFloat(form.montoUsd||0)*parseFloat(form.tipoCambio||data.settings.exchangeRate))}</b>
      </div>}
      <div className="row row--end mt-20 gap-10">
        <Btn variant="secondary" onClick={()=>setModal(null)}>Cancelar</Btn>
        <Btn onClick={handleSave} disabled={saving}>{saving?<><RefreshCw size={13} className="spin"/>Guardando…</>:<><Check size={14}/>Registrar Gasto</>}</Btn>
      </div>
    </Modal>}

    {modal==="anular"&&selG&&<Modal title={`Anular ${selG.numero}`} onClose={()=>{setModal(null);setSelG(null);}} width={460}>
      <div style={{background:'var(--dan-dim,#2d1a1a)',border:`1px solid ${'var(--danger)'}44`,borderRadius:8,padding:"10px 13px",marginBottom:14,fontSize:12,color:'var(--danger)',display:"flex",gap:7,alignItems:"center"}}><AlertTriangle size={15}/>Anular: <b>{selG.categoria}</b> — {fmtUsd(selG.montoUsd)} · {fmtDate(selG.fecha)}. Acción irreversible.</div>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <FInput label="Motivo de anulación" value={form.reason||""} onChange={v=>f("reason",v)} placeholder="Explique el motivo" required error={errs.reason}/>
        <FInput label="Contraseña del Administrador" value={form.adminPwd||""} onChange={v=>{f("adminPwd",v);setErrs({});}} type="password" placeholder="••••••••" required error={errs.adminPwd}/>
      </div>
      <div className="row row--end mt-20 gap-10">
        <Btn variant="secondary" onClick={()=>{setModal(null);setSelG(null);}}>Cancelar</Btn>
        <Btn variant="danger" onClick={handleAnular} disabled={saving}>{saving?<><RefreshCw size={13} className="spin"/>Anulando…</>:<><XCircle size={13}/>Confirmar Anulación</>}</Btn>
      </div>
    </Modal>}
  </div>;
}

/* ── PERÍODOS ───────────────────────────────────────────────────────── */
function PeriodosModule({data,refresh,addToast,currentUser}){
  const[modal,setModal]=useState(null);
  const[selP,setSelP]=useState(null);
  const[form,setForm]=useState({});
  const[errs,setErrs]=useState({});
  const[saving,setSaving]=useState(false);
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));

  const[qP,setQP]=useState("");
  const[fEstP,setFEstP]=useState("");
  const periodos=data.periodos||[];
  const activos=periodos.filter(p=>p.estado==="Abierto");
  const cerrados=periodos.filter(p=>p.estado==="Cerrado");
  const listP=periodos.filter(p=>{const txt=`${p.nombre} ${p.creadoPor||""} ${p.cerradoPor||""}`.toLowerCase();return txt.includes(qP.toLowerCase())&&(!fEstP||p.estado===fEstP);});
  const pgP=usePagination(listP);
  const hoy=today();
  const periodoActual=activos.find(p=>p.fechaInicio<=hoy&&p.fechaFin>=hoy);

  const openNew=()=>{setForm({nombre:"",fechaInicio:hoy,fechaFin:"",notas:""});setErrs({});setModal("new");};

  const validate=()=>{
    const e={};
    if(!form.nombre?.trim())e.nombre="Requerido";
    if(!form.fechaInicio)e.fechaInicio="Requerida";
    if(!form.fechaFin)e.fechaFin="Requerida";
    if(form.fechaInicio&&form.fechaFin&&form.fechaFin<form.fechaInicio)e.fechaFin="Debe ser posterior a la fecha inicio";
    setErrs(e);return!Object.keys(e).length;
  };

  const handleCreate=async()=>{
    if(!validate())return;
    setSaving(true);
    try{
      await API.periodos.create({nombre:form.nombre.trim(),fechaInicio:form.fechaInicio,fechaFin:form.fechaFin,notas:form.notas||""});
      addToast("Período creado","success");
      await refresh();setModal(null);
    }catch(e){addToast(e.message,"error");}
    finally{setSaving(false);}
  };

  const handleCerrar=async()=>{
    setSaving(true);
    try{
      await API.periodos.cerrar(selP.id);
      addToast(`Período "${selP.nombre}" cerrado`,"info");
      await refresh();setModal(null);setSelP(null);
    }catch(e){addToast(e.message,"error");}
    finally{setSaving(false);}
  };

  const handleAbrir=async()=>{
    setSaving(true);
    try{
      await API.periodos.abrir(selP.id);
      addToast(`Período "${selP.nombre}" reabierto`,"success");
      await refresh();setModal(null);setSelP(null);
    }catch(e){addToast(e.message,"error");}
    finally{setSaving(false);}
  };

  const handleDelete=async(p)=>{
    if(!window.confirm(`¿Eliminar el período "${p.nombre}"? Esta acción no se puede deshacer.`))return;
    try{
      await API.periodos.delete(p.id);
      addToast("Período eliminado","info");
      await refresh();
    }catch(e){addToast(e.message,"error");}
  };

  let diasPeriodo="—";
  if(periodoActual){
    const ini=new Date(periodoActual.fechaInicio);
    const fin=new Date(periodoActual.fechaFin);
    diasPeriodo=Math.round((fin-ini)/(1000*60*60*24))+1;
  }

  const cols=[
    {k:"nombre",l:"Período",render:r=><b className="fs-13">{r.nombre}</b>},
    {k:"ini",l:"Inicio",render:r=><span className="fs-12 c-muted">{fmtDate(r.fechaInicio)}</span>},
    {k:"fin",l:"Fin",render:r=><span className="fs-12 c-muted">{fmtDate(r.fechaFin)}</span>},
    {k:"estado",l:"Estado",render:r=><Badge v={r.estado}/>},
    {k:"cp",l:"Creado por",render:r=><span className="fs-12 c-muted">{r.creadoPor||"—"}</span>},
    {k:"cerr",l:"Cerrado por",render:r=><span className="fs-12 c-muted">{r.cerradoPor||"—"}</span>},
    {k:"acc",l:"",render:r=><div style={{display:"flex",gap:6}}>
      {r.estado==="Abierto"
        ?can(currentUser,"periodos:cerrar")&&<Btn variant="ghost" style={{color:"var(--warn)"}} onClick={e=>{e.stopPropagation();setSelP(r);setModal("cerrar");}}><Lock size={13}/>Cerrar</Btn>
        :can(currentUser,"periodos:abrir")&&<Btn variant="ghost" style={{color:"var(--success)"}} onClick={e=>{e.stopPropagation();setSelP(r);setModal("abrir");}}><CheckCircle size={13}/>Abrir</Btn>
      }
      {r.estado==="Cerrado"&&can(currentUser,"periodos:eliminar")&&<Btn variant="ghost" style={{color:"var(--danger)"}} onClick={e=>{e.stopPropagation();handleDelete(r);}}><X size={13}/></Btn>}
    </div>},
  ];

  return<div>
    <PH title="Períodos" sub={`${periodos.length} períodos registrados`} action={can(currentUser,"periodos:crear")&&<Btn onClick={openNew}><Plus size={14}/>Nuevo Período</Btn>}/>

    {periodoActual
      ?<div style={{background:"rgba(16,185,129,0.1)",border:"1px solid rgba(16,185,129,0.3)",borderRadius:10,padding:"12px 18px",marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
        <CheckCircle size={16} color="var(--success)"/>
        <span style={{fontSize:13}}><b>Período activo:</b> {periodoActual.nombre} &nbsp;·&nbsp; {fmtDate(periodoActual.fechaInicio)} al {fmtDate(periodoActual.fechaFin)}</span>
      </div>
      :<div style={{background:"rgba(251,191,36,0.1)",border:"1px solid rgba(251,191,36,0.3)",borderRadius:10,padding:"12px 18px",marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
        <AlertTriangle size={16} color="var(--warn)"/>
        <span style={{fontSize:13,color:"var(--text)"}}>No hay un período abierto que incluya la fecha de hoy.</span>
      </div>
    }

    <div className="grid-4" style={{marginBottom:16}}>
      <StatCard icon={Calendar} title="Total períodos" value={periodos.length} sub="registrados" color="var(--accent)"/>
      <StatCard icon={CheckCircle} title="Abiertos" value={activos.length} sub="en curso" color="var(--success)"/>
      <StatCard icon={Lock} title="Cerrados" value={cerrados.length} sub="finalizados" color="var(--muted)"/>
      <StatCard icon={Calendar} title="Días en período" value={diasPeriodo} sub={periodoActual?periodoActual.nombre:"sin período activo"} color="var(--violet)"/>
    </div>

    <div className="card">
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:10,flexWrap:"wrap"}}>
        <SearchBar value={qP} onChange={v=>{setQP(v);pgP.goPage(1);}} placeholder="Nombre, creado por…"/>
      </div>
      <div style={{marginBottom:14}}><FilterChips opts={["Abierto","Cerrado"]} val={fEstP} set={v=>{setFEstP(v);pgP.goPage(1);}}/></div>
      <Table cols={cols} rows={pgP.paged} empty="Sin períodos registrados. Cree el primero."/>
      <Pagination {...pgP}/>
    </div>

    {modal==="new"&&<Modal title="Nuevo Período" onClose={()=>setModal(null)} width={500}>
      <div className="flex-col gap-14">
        <FInput label="Nombre del período" value={form.nombre||""} onChange={v=>f("nombre",v)} placeholder="Ej: Julio 2026, Q3-2026…" required error={errs.nombre}/>
        <div className="grid-2">
          <FInput label="Fecha inicio" value={form.fechaInicio||""} onChange={v=>f("fechaInicio",v)} type="date" required error={errs.fechaInicio}/>
          <FInput label="Fecha fin" value={form.fechaFin||""} onChange={v=>f("fechaFin",v)} type="date" required error={errs.fechaFin}/>
        </div>
        <FInput label="Notas (opcional)" value={form.notas||""} onChange={v=>f("notas",v)} placeholder="Observaciones del período…"/>
      </div>
      <div className="row row--end mt-20 gap-10">
        <Btn variant="secondary" onClick={()=>setModal(null)}>Cancelar</Btn>
        <Btn onClick={handleCreate} disabled={saving}>{saving?<><RefreshCw size={13} className="spin"/>Guardando…</>:<><Check size={14}/>Crear Período</>}</Btn>
      </div>
    </Modal>}

    {modal==="cerrar"&&selP&&<Modal title={`Cerrar período: ${selP.nombre}`} onClose={()=>{setModal(null);setSelP(null);}} width={460}>
      <div style={{background:"rgba(251,191,36,0.1)",border:"1px solid rgba(251,191,36,0.35)",borderRadius:8,padding:"12px 14px",marginBottom:16,fontSize:13,color:"var(--warn)",display:"flex",gap:8,alignItems:"flex-start"}}>
        <AlertTriangle size={15} style={{flexShrink:0,marginTop:1}}/>
        <span>Al cerrar el período <b>{selP.nombre}</b> ({fmtDate(selP.fechaInicio)} — {fmtDate(selP.fechaFin)}) quedará bloqueado para nuevas operaciones en esas fechas.</span>
      </div>
      <div className="row row--end mt-20 gap-10">
        <Btn variant="secondary" onClick={()=>{setModal(null);setSelP(null);}}>Cancelar</Btn>
        <Btn variant="danger" onClick={handleCerrar} disabled={saving}>{saving?<><RefreshCw size={13} className="spin"/>Cerrando…</>:<><Lock size={13}/>Confirmar Cierre</>}</Btn>
      </div>
    </Modal>}

    {modal==="abrir"&&selP&&<Modal title={`Reabrir período: ${selP.nombre}`} onClose={()=>{setModal(null);setSelP(null);}} width={460}>
      <div style={{background:"var(--accent-dim)",border:"1px solid var(--accent-border)",borderRadius:8,padding:"12px 14px",marginBottom:16,fontSize:13,color:"var(--sub)",display:"flex",gap:8,alignItems:"flex-start"}}>
        <AlertCircle size={15} style={{flexShrink:0,marginTop:1}}/>
        <span>¿Reabrir el período <b>{selP.nombre}</b> ({fmtDate(selP.fechaInicio)} — {fmtDate(selP.fechaFin)})? Se permitirán nuevamente registros en esas fechas.</span>
      </div>
      <div className="row row--end mt-20 gap-10">
        <Btn variant="secondary" onClick={()=>{setModal(null);setSelP(null);}}>Cancelar</Btn>
        <Btn onClick={handleAbrir} disabled={saving}>{saving?<><RefreshCw size={13} className="spin"/>Reabriendo…</>:<><CheckCircle size={13}/>Confirmar Reapertura</>}</Btn>
      </div>
    </Modal>}
  </div>;
}

const NAV=[{id:"dashboard",l:"Dashboard",icon:Home},{id:"clients",l:"Clientes",icon:Users},{id:"packages",l:"Paquetería",icon:Package},{id:"ordenes",l:"Órdenes de Pago",icon:Receipt},{id:"billing",l:"Recibos",icon:FileText},{id:"delivery",l:"Entrega Física",icon:Truck},{id:"gastos",l:"Gastos",icon:TrendingDown},{id:"periodos",l:"Períodos",icon:Calendar},{id:"reports",l:"Reportes",icon:BarChart2},{id:"audit",l:"Auditoría",icon:ClipboardList},{id:"security",l:"Seguridad",icon:Shield},{id:"settings",l:"Configuración",icon:Settings}];

function Sidebar({active,onNav,onLogout,user,data,isOpen,onClose}){
  const pp=data.packages.filter(p=>p.status==="Registrado").length;
  const pd=data.invoices.filter(i=>i.status==="Pagada"&&i.pkgIds.some(id=>data.packages.find(p=>p.id===id)?.status==="Facturado")).length;
  const op=(data.ordenes||[]).filter(o=>o.status==="Pendiente").length;
  const bd={packages:pp>0?pp:null,delivery:pd>0?pd:null,ordenes:op>0?op:null};
  const handleNav=id=>{onNav(id);onClose();};
  const visibleNav=NAV.filter(n=>{
    if(n.id==="dashboard")return true;
    // /api/roles y /api/usuarios exigen el rol 'Administrador' en el backend
    // (fuera de alcance del sistema de permisos granular), asi que el menu
    // debe reflejar esa misma regla en vez de un permiso que no lo abre.
    if(n.id==="security")return user.role==="Administrador";
    return can(user,NAV_PERM[n.id]);
  });
  return<>
    {isOpen&&<div className="sidebar__overlay" onClick={onClose}/>}
    <aside className={`sidebar${isOpen?' is-open':''}`}>
      <div className="sidebar__logo">
        <img src={data.settings?.logo||LOGO} alt="Momotombo Box"/>
        <button className="sidebar__close" onClick={onClose}><X size={18}/></button>
      </div>
      <nav className="sidebar__nav">{visibleNav.map(n=>{const ac=active===n.id;return<button key={n.id} onClick={()=>handleNav(n.id)} className={`sidebar__btn${ac?' is-active':''}`}>
        <n.icon size={16} style={{flexShrink:0}}/><span style={{flex:1}}>{n.l}</span>
        {bd[n.id]&&<span className="sidebar__badge">{bd[n.id]}</span>}
      </button>;})}
      </nav>
      <div className="sidebar__footer">
        <div className="sidebar__user"><div className="sidebar__avatar"><User size={14} color="var(--accent)"/></div><div style={{overflow:"hidden",flex:1}}><div className="sidebar__uname">{user.name}</div><div className="sidebar__urole">{user.role}</div></div></div>
        <Btn variant="ghost" onClick={onLogout} style={{width:"100%",justifyContent:"flex-start",fontSize:12,padding:"7px 8px"}}><LogOut size={13}/>Cerrar sesión</Btn>
      </div>
    </aside>
  </>;
}

/* ── ROOT APP ───────────────────────────────────────────────────────── */

export default function App(){
  const[user,setUser]=useState(()=>{try{const t=localStorage.getItem("mb_token");if(!t)return null;const p=JSON.parse(atob(t.split(".")[1]));return p.exp*1000>Date.now()?p:null;}catch{return null;}});
  const[loading,setLoading]=useState(false);
  const[data,setData]=useState({settings:{companyName:"Momotombo Box",exchangeRate:36.80,minLbs:1.00,minLbsPerPackage:1.00,rateAir:5.00,rateSea:2.50,pkgPfx:"PK-",invPfx:"FAC-",sessionTimeout:30,maxAttempts:5,ruc:"",address:"",whatsapp:"",email:"",theme:"light",colorAccent:"#3B9EE0",colorPrimary:"#020d1b",colorSecondary:"#0d1f35",logo:"",bancos:[]},clients:[],packages:[],invoices:[],deliveries:[],users:[],audit:[],chartData:[],ordenes:[],gastos:[],periodos:[]});

  const[userTheme,setUserTheme]=useState(()=>localStorage.getItem("mb_user_theme")||null);

  useEffect(()=>{
    try{
      const cached=JSON.parse(localStorage.getItem("mb_theme")||"{}");
      const override=localStorage.getItem("mb_user_theme");
      if(Object.keys(cached).length||override)applyTheme({...cached,theme:override||cached.theme});
    }catch{}
  },[]);

  // El tema claro/oscuro efectivo: preferencia personal (este navegador) si existe, si no la de la empresa.
  useEffect(()=>{
    applyTheme({...data.settings,theme:userTheme||data.settings.theme});
  },[data.settings,userTheme]);

  // Único punto de cambio de tema, usado tanto por el switch de la topbar
  // como por el selector de Configuración (y por Login) — así los dos nunca
  // pueden quedar en estados distintos: cualquiera de ellos actualiza el
  // mismo valor y se refleja de inmediato en toda la app vía el efecto de
  // arriba. Si quien lo cambia es Administrador, además queda como el
  // default de la empresa (mismo permiso que ya exige PUT /api/settings),
  // para que su propio toggle y su panel de Configuración nunca diverjan.
  const setEffectiveTheme=next=>{
    localStorage.setItem("mb_user_theme",next);
    setUserTheme(next);
    if(user?.role==="Administrador"){
      API.settings.update({theme:next}).catch(()=>addToast("No se pudo guardar el tema como predeterminado de la empresa","warn"));
    }
  };
  const toggleUserTheme=()=>{
    const next=(userTheme||data.settings.theme||"light")==="dark"?"light":"dark";
    setEffectiveTheme(next);
  };

  const[mod,setMod]=useState("dashboard");
  const[showSearch,setShowSearch]=useState(false);
  const[toasts,setToasts]=useState([]);
  const[billingCid,setBillingCid]=useState(null);
  const[trackPkgId,setTrackPkgId]=useState(null);
  const[sidebarOpen,setSidebarOpen]=useState(false);

  const addToast=useCallback((message,type="info")=>{const id=Date.now();setToasts(t=>[...t,{id,message,type}]);setTimeout(()=>setToasts(t=>t.filter(x=>x.id!==id)),3500);},[]);

  const loadAll=useCallback(async()=>{
    if(!user)return;
    setLoading(true);
    try{
      const[settings,clients,packages,invoices,deliveries,users,audit,chartData,ordenes,gastos,periodos]=await Promise.all([
        API.settings.get(),
        safeFetch(API.clientes.getAll(),[]),
        safeFetch(API.paquetes.getAll(),[]),
        safeFetch(API.facturas.getAll(),[]),
        safeFetch(API.entregas.getAll(),[]),
        API.usuarios.getAll(),
        safeFetch(API.auditoria.getAll(),[]),
        safeFetch(API.reportes.ingresosMensuales(),[]),
        safeFetch(API.ordenes.getAll(),[]),
        safeFetch(API.gastos.getAll(),[]),
        safeFetch(API.periodos.getAll(),[]),
      ]);
      setData({settings,clients,packages,invoices,deliveries,users,audit,chartData,ordenes,gastos,periodos});
      localStorage.setItem("mb_theme",JSON.stringify({theme:settings.theme,colorAccent:settings.colorAccent,colorPrimary:settings.colorPrimary,colorSecondary:settings.colorSecondary}));
      if(settings.logo)localStorage.setItem("mb_logo",JSON.stringify({logo:settings.logo}));
      else localStorage.removeItem("mb_logo");
    }catch(e){
      if(e.message.includes("401")||e.message.includes("Token")){localStorage.removeItem("mb_token");setUser(null);}
      else addToast("Error cargando datos: "+e.message,"error");
    }finally{setLoading(false);}
  },[user]);

  useEffect(()=>{loadAll();},[loadAll]);

  const handleLogin=async u=>{setUser(u);};
  const handleLogout=()=>{localStorage.removeItem("mb_token");setUser(null);};
  const nav=m=>{setMod(m);if(m!=="billing")setBillingCid(null);if(m!=="packages")setTrackPkgId(null);setSidebarOpen(false);};
  const navTrackPkg=pkgId=>{setTrackPkgId(pkgId);setMod("packages");setSidebarOpen(false);};
  const refresh=useCallback(()=>loadAll(),[loadAll]);

  if(!user)return<LoginScreen onLogin={handleLogin} theme={userTheme||"light"} onToggleTheme={toggleUserTheme}/>;

  const mp={data,refresh,addToast,currentUser:user};

  return<div className="app-shell">
    <Sidebar active={mod} onNav={nav} onLogout={handleLogout} user={user} data={data} isOpen={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
    <div className="content-area">
      <header className="topbar">
        <button className="topbar__hamburger" onClick={()=>setSidebarOpen(o=>!o)} aria-label="Menú"><span/><span/><span/></button>
        <div className="topbar__title">{NAV.find(n=>n.id===mod)?.l||"Sistema"}</div>
        <button onClick={()=>loadAll()} className="topbar__btn" title="Actualizar datos"><RefreshCw size={15} className={loading?"spin":""}/></button>
        <button onClick={()=>setShowSearch(true)} className="topbar__search"><Search size={13}/><span>Búsqueda global</span><span className="topbar__kbd">⌘K</span></button>
        <button onClick={toggleUserTheme} className="topbar__btn" title="Cambiar a modo claro/oscuro (solo este navegador)">
          {(userTheme||data.settings.theme)==="light"?<Moon size={15}/>:<Sun size={15}/>}
        </button>
        <div className="topbar__user"><div className="topbar__avatar"><User size={13} color="var(--accent)"/></div><span className="fw-600">{user.name}</span><span className="c-muted fs-11">· {user.role}</span></div>
      </header>
      <main className="main-content">
        {loading&&!data.clients.length?<Spinner/>:<>
          {mod==="dashboard"&&<DashboardModule {...mp}/>}
          {mod==="clients"&&<ClientsModule {...mp}/>}
          {mod==="packages"&&<PackagesModule {...mp} openBilling={cid=>{setBillingCid(cid);setMod("billing");}} initialSelectedId={trackPkgId} onConsumeInitialSelectedId={()=>setTrackPkgId(null)}/>}
          {mod==="billing"&&<BillingModule {...mp} initClientId={billingCid}/>}
          {mod==="ordenes"&&<OrdenesModule {...mp}/>}
          {mod==="gastos"&&<GastosModule {...mp}/>}
          {mod==="periodos"&&<PeriodosModule {...mp}/>}
          {mod==="delivery"&&<DeliveryModule {...mp}/>}
          {mod==="reports"&&<ReportsModule {...mp}/>}
          {mod==="audit"&&<AuditModule {...mp}/>}
          {mod==="security"&&<SecurityModule {...mp}/>}
          {/* CMS en construccion - ver import comentado arriba */}
          {mod==="settings"&&<SettingsModule {...mp} effectiveTheme={userTheme||data.settings.theme} onThemeChange={setEffectiveTheme}/>}
        </>}
      </main>
    </div>
    {showSearch&&<GlobalSearch data={data} onClose={()=>setShowSearch(false)} onNav={nav} onNavTrackPkg={navTrackPkg}/>}
    <Toast toasts={toasts}/>
  </div>;
}
